import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import { 
  FaUsers, 
  FaDrumstickBite, 
  FaChartBar, 
  FaCog, 
  FaEdit, 
  FaTrash, 
  FaPlus,
  FaDownload,
  FaEye,
  FaSignOutAlt,
  FaUserPlus,
  FaTimes,
  FaUpload,
  FaImage,
  FaUserCircle,
  FaEyeSlash,
  FaChartPie,
  FaShoppingCart,
  FaUserCog,
  FaFileAlt,
  FaChevronDown,
  FaCoins,
  FaPaw,
  FaMoneyBillWave,
  FaReceipt,
  FaFileInvoice,
  FaClipboardList,
  FaUserFriends,
  FaCalendarAlt,
  FaDatabase,
  FaBars
} from 'react-icons/fa';
import LoginFormComponent from '../components/LoginForm';
import ContentPlannerSection from '../components/ContentPlannerSection';
// API Servisleri - Merkezi API yönetimi için
import { animalService, userService, tahsilatService, reportService, expenseService, kesimService, whatsappService } from '../utils/apiService';
import API_BASE_URL, { API_ENDPOINTS } from '../config/api';

const AdminContainer = styled.div`
  min-height: 100vh;
  background: var(--light-gray);
  padding-top: 120px;
`;

const AdminHeader = styled.div`
  background: var(--primary-color);
  color: var(--white-color);
  padding: 40px 0 20px 0;
  text-align: center;
  position: relative;
  margin-bottom: 30px;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
`;

const AdminTitle = styled.h1`
  font-size: 2.5rem;
  color: var(--primary-color);
  margin-bottom: 10px;
  text-align: center;
`;

const AdminSubtitle = styled.p`
  color: var(--secondary-color);
  text-align: center;
  font-size: 1.1rem;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
`;

const StatCard = styled(motion.div)`
  background: var(--white-color);
  padding: 30px;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  text-align: center;
  border-left: 4px solid var(--primary-color);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
`;

const StatIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 24px;
  color: var(--white-color);
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 10px;
`;

const StatLabel = styled.div`
  color: var(--secondary-color);
  font-size: 1.1rem;
`;

const ContentSection = styled.div`
  background: var(--white-color);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  margin-bottom: 30px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  background: var(--primary-color);
  color: var(--white-color);
  padding: 20px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
`;

const ActionButton = styled.button`
  background: var(--white-color);
  color: var(--primary-color);
  border: none;
  padding: 10px 20px;
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: var(--light-gray);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const AdminTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  background: var(--light-gray);
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: var(--secondary-color);
  border-bottom: 2px solid var(--primary-color);
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--light-gray);
  
  &:hover {
    background: var(--light-gray);
  }
`;

const TableCell = styled.td`
  padding: 15px;
  color: var(--secondary-color);
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: var(--transition);
  margin: 0 5px;
  
  &.edit {
    color: var(--primary-color);
    &:hover { background: rgba(255, 140, 0, 0.1); }
  }
  
  &.delete {
    color: #e74c3c;
    &:hover { background: rgba(231, 76, 60, 0.1); }
  }
  
  &.view {
    color: var(--secondary-color);
    &:hover { background: rgba(85, 93, 97, 0.1); }
  }
`;

const SearchBar = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 15px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const FilterSelect = styled.select`
  padding: 12px 15px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 1rem;
  background: var(--white-color);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const LogoutButton = styled.button`
  position: absolute;
  top: 50px;
  right: 20px;
  background: var(--white-color);
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  padding: 12px 20px;
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  white-space: nowrap;
  z-index: 1000;
  
  &:hover {
    background: var(--primary-color);
    color: var(--white-color);
  }
`;

// Dropdown Stilleri
const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled(motion.button)`
  background: var(--white-color);
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  padding: 12px 20px;
  border-radius: var(--border-radius);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: var(--transition);
  cursor: pointer;
  
  &:hover {
    background: var(--primary-color);
    color: var(--white-color);
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--white-color);
  border: 2px solid var(--primary-color);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  z-index: 100;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 20px;
  background: none;
  border: none;
  text-align: left;
  font-size: 14px;
  color: var(--secondary-color);
  transition: var(--transition);
  cursor: pointer;
  
  &:hover {
    background: var(--light-gray);
    color: var(--primary-color);
  }
`;

const SettingsButton = styled.button`
  background: var(--white-color);
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  padding: 12px 20px;
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  white-space: nowrap;
  
  &:hover {
    background: var(--primary-color);
    color: var(--white-color);
  }
`;

const SettingsDropdown = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 0;
  background: var(--white-color);
  border: 3px solid red;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  min-width: 200px;
  z-index: 9999;
  overflow: visible;
  margin-top: 5px;
  pointer-events: auto;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: var(--white-color);
  border-radius: var(--border-radius);
  padding: 30px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--light-gray);
`;

const ModalTitle = styled.h2`
  color: var(--primary-color);
  margin: 0;
  font-size: 1.5rem;
`;

const CloseModalButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: var(--secondary-color);
  cursor: pointer;
  padding: 5px;
  
  &:hover {
    color: var(--primary-color);
  }
`;

const Form = styled.form`
  display: grid;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--secondary-color);
  font-size: 0.9rem;
  
  .required {
    color: #e74c3c;
    margin-left: 2px;
  }
`;

const FormInput = styled.input`
  padding: 12px 15px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 140, 0, 0.1);
  }
  
  &.error {
    border-color: #e74c3c;
  }
`;

const FormSelect = styled.select`
  padding: 12px 15px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: var(--transition);
  background: var(--white-color);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 140, 0, 0.1);
  }
`;

const FormButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 2px solid var(--light-gray);
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  border: none;
  font-size: 1rem;
  
  &.primary {
    background: var(--primary-color);
    color: var(--white-color);
    
    &:hover {
      background: #e67e00;
      transform: translateY(-2px);
    }
  }
  
  &.secondary {
    background: var(--white-color);
    color: var(--secondary-color);
    border: 2px solid var(--light-gray);
    
    &:hover {
      background: var(--light-gray);
    }
  }
  
  &.danger {
    background: #e74c3c;
    color: var(--white-color);
    
    &:hover {
      background: #c0392b;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 0.8rem;
  margin-top: 5px;
`;

const SuccessBanner = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 10px 12px;
  border-radius: var(--border-radius);
  border: 1px solid #c3e6cb;
  font-size: 0.9rem;
  margin-bottom: 10px;
`;

const ImageUploadContainer = styled.div`
  border: 2px dashed var(--light-gray);
  border-radius: var(--border-radius);
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: var(--transition);
  margin-bottom: 20px;
  
  &:hover {
    border-color: var(--primary-color);
    background: rgba(255, 140, 0, 0.05);
  }
  
  &.has-image {
    border-color: var(--primary-color);
    background: rgba(255, 140, 0, 0.05);
  }
`;

const ImagePreview = styled.div`
  width: 100%;
  max-width: 300px;
  height: 200px;
  margin: 0 auto 15px;
  border-radius: var(--border-radius);
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UploadIcon = styled.div`
  font-size: 48px;
  color: var(--primary-color);
  margin-bottom: 15px;
`;

const UploadText = styled.div`
  color: var(--secondary-color);
  font-size: 1rem;
  margin-bottom: 10px;
`;

const UploadSubtext = styled.div`
  color: var(--secondary-color);
  font-size: 0.8rem;
  opacity: 0.7;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FormTextarea = styled.textarea`
  padding: 12px 15px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: var(--transition);
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 140, 0, 0.1);
  }
  
  &.error {
    border-color: #e74c3c;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: var(--secondary-color);
  
  input[type="radio"] {
    width: 16px;
    height: 16px;
    accent-color: var(--primary-color);
  }
`;

// Kullanıcı Listesi için şık bir ListView styled componentleri ekle
const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const UserListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  padding: 14px 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: box-shadow 0.2s, background 0.2s;
  font-size: 1rem;
  &:hover {
    box-shadow: 0 4px 16px rgba(255,140,0,0.10);
    background: #fff7ed;
  }
`;
const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  font-weight: 500;
`;
const UserMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #888;
  font-size: 0.95em;
`;

const OrangeButton = styled.button`
  background: #ff8c00;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 18px;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #e67600;
  }
`;
const RedButton = styled(OrangeButton)`
  background: #e74c3c;
  &:hover {
    background: #c0392b;
  }
`;

const TopButtonGroup = styled.div`
  position: absolute;
  top: 50px;
  right: 20px;
  display: flex;
  gap: 12px;
  z-index: 1000;
  align-items: center;
  justify-content: flex-end;

  .mobile-menu-toggle {
    display: none;
  }

  @media (max-width: 768px) {
    top: 14px;
    right: 12px;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    background: ${props => (props.$open ? 'rgba(255,255,255,0.98)' : 'transparent')};
    padding: ${props => (props.$open ? '10px' : '0')};
    border-radius: 10px;
    box-shadow: ${props => (props.$open ? '0 8px 20px rgba(0,0,0,0.15)' : 'none')};
    min-width: ${props => (props.$open ? '190px' : 'auto')};

    .mobile-menu-toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background: var(--white-color);
      color: var(--primary-color);
      border: 2px solid var(--primary-color);
      padding: 10px 14px;
      border-radius: var(--border-radius);
      font-weight: 600;
      cursor: pointer;
    }

    > *:not(.mobile-menu-toggle) {
      display: ${props => (props.$open ? 'flex' : 'none')};
      width: 100%;
      justify-content: flex-start;
      position: static !important;
      margin: 0;
    }
  }
`;

// ModalContent stilini güncelle:
const LargeModalContent = styled(ModalContent)`
  max-width: 1200px;
  width: 90vw;
  max-height: 90vh;
`;

const SecretAdminPanel = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showReportsDropdown, setShowReportsDropdown] = useState(false);
  const [showShareSaleDropdown, setShowShareSaleDropdown] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [showSystemSettingsModal, setShowSystemSettingsModal] = useState(false);
  const [systemAdminPassword, setSystemAdminPassword] = useState('');
  const [systemSettingsLoading, setSystemSettingsLoading] = useState(false);
  const [systemSettingsMessage, setSystemSettingsMessage] = useState('');
  const systemDbRestoreInputRef = useRef(null);
  const topButtonGroupRef = useRef(null);
  const [showSacrificeModal, setShowSacrificeModal] = useState(false);
  const [showSalesReportModal, setShowSalesReportModal] = useState(false);
  const [showTahsilatlarModal, setShowTahsilatlarModal] = useState(false);
  const [showContentPlannerModal, setShowContentPlannerModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const closeMobileToolbarMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    setShowSettingsDropdown(false);
    setShowReportsDropdown(false);
  }, []);
  const [tahsilatlarData, setTahsilatlarData] = useState([]);
  const [tahsilatlarLoading, setTahsilatlarLoading] = useState(false);
  const [tahsilatlarSearchTerm, setTahsilatlarSearchTerm] = useState('');
  const [selectedTahsilatId, setSelectedTahsilatId] = useState(null);
  const [showTahsilatEkleModal, setShowTahsilatEkleModal] = useState(false);
  const [selectedTahsilatData, setSelectedTahsilatData] = useState(null);
  const [tahsilatEkleFormData, setTahsilatEkleFormData] = useState({
    tahsilatTarihi: new Date().toISOString().split('T')[0],
    tahsilatMiktari: '',
    tahsilatYolu: 'Nakit'
  });
  
  // Personel Raporları state'leri
  const [showPersonelRaporlariModal, setShowPersonelRaporlariModal] = useState(false);
  const [personelRaporlariData, setPersonelRaporlariData] = useState([]);
  const [personelRaporlariLoading, setPersonelRaporlariLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [personelRaporlariFilters, setPersonelRaporlariFilters] = useState({
    userId: '',
    branch: '',
    startDate: '',
    endDate: ''
  });
  
  // Kurum Raporları state'leri
  const [showKurumRaporlariModal, setShowKurumRaporlariModal] = useState(false);
  const [kurumRaporlariData, setKurumRaporlariData] = useState([]);
  const [kurumRaporlariLoading, setKurumRaporlariLoading] = useState(false);
  const [availableBranchesForKurum, setAvailableBranchesForKurum] = useState([]);
  const [kurumRaporlariFilters, setKurumRaporlariFilters] = useState({
    branch: '',
    startDate: '',
    endDate: ''
  });
  
  // Gider Ekle state'leri
  const [showGiderEkleModal, setShowGiderEkleModal] = useState(false);
  const [giderFormData, setGiderFormData] = useState({
    title: '',
    category: 'Yem',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_method: 'Nakit',
    branch: '',
    description: '',
    document: null
  });
  const [availableBranchesForGider, setAvailableBranchesForGider] = useState([]);
  const [giderFormErrors, setGiderFormErrors] = useState({});
  
  // Kasa Raporları state'leri
  const [showKasaRaporlariModal, setShowKasaRaporlariModal] = useState(false);
  const [kasaRaporlariData, setKasaRaporlariData] = useState(null);
  const [kasaRaporlariLoading, setKasaRaporlariLoading] = useState(false);
  const [kasaRaporlariFilters, setKasaRaporlariFilters] = useState({
    userId: '',
    branch: ''
  });
  const [availableUsersForKasa, setAvailableUsersForKasa] = useState([]);
  const [availableBranchesForKasa, setAvailableBranchesForKasa] = useState([]);
  
  // Hayvanlar Modal state'leri
  const [showHayvanlarModal, setShowHayvanlarModal] = useState(false);
  const [hayvanlarList, setHayvanlarList] = useState([]);
  const [hayvanlarLoading, setHayvanlarLoading] = useState(false);
  const [hayvanlarSearchTerm, setHayvanlarSearchTerm] = useState('');
  const [hayvanlarFilterType, setHayvanlarFilterType] = useState('all');
  const [hayvanlarFilterStatus, setHayvanlarFilterStatus] = useState('all');
  const [expandedHayvanId, setExpandedHayvanId] = useState(null);
  const [hayvanHissedarlar, setHayvanHissedarlar] = useState({});
  const [showKesimModal, setShowKesimModal] = useState(false);
  const [selectedKesimHayvan, setSelectedKesimHayvan] = useState(null);
  const [kesimFormData, setKesimFormData] = useState({
    kesim_no: '',
    kesim_tarihi: '',
    tahmini_kesim_saati: '',
    tahmini_teslim_saati: ''
  });
  
  // Kullanıcı bilgileri (gerçek uygulamada localStorage'dan veya context'ten gelecek)
  const [currentUser, setCurrentUser] = useState({
    id: 1,
    name: 'Osman AYDIN',
    role: 'Yönetici', // 'Personel', 'Kurum Mesulü', 'Yönetici'
    institution: 'SULTANLAR BESİCİLİK'
  });
  
  // Satış verileri (API: /api/animals/share-sales/all)
  const [salesData, setSalesData] = useState([]);
  const [salesReportLoading, setSalesReportLoading] = useState(false);

  // Satış verilerini API'den çek
  const fetchSalesData = async () => {
    setSalesReportLoading(true);
    try {
      const raw = await animalService.getAllShareSales();
      const data = Array.isArray(raw) ? raw : [];
      // API'den gelen veriyi modal için uygun formata çevir
      const formattedData = data.map(sale => ({
        id: sale.id,
        hisseSahibi: sale.customer_name,
        hisseAdedi: sale.share_count,
        aracı: sale.reference_user_name || 'Sistem',
        kurum: sale.reference_user_branch || 'Merkez',
        fiyat: sale.total_amount || 0,
        tarih: sale.created_at ? new Date(sale.created_at).toISOString().split('T')[0] : '2024-01-15',
        personelId: sale.reference_user_id,
        personelAdi: sale.reference_user_name || 'Sistem Yöneticisi',
        pricePerShare: sale.price_per_share || 0,
        paymentMethod: sale.payment_method || 'Nakit',
        paidAmount: sale.paid_amount || 0,
        remainingDebt: sale.remaining_debt || 0
      }));
      setSalesData(formattedData);
    } catch (error) {
      console.error('Satış verileri çekme hatası:', error);
      setSalesData([]);
    } finally {
      setSalesReportLoading(false);
    }
  };

  // Satış Raporu modalı açılınca listeyi sunucudan yükle
  useEffect(() => {
    if (showSalesReportModal) {
      fetchSalesData();
    }
  }, [showSalesReportModal]);

  // Tahsilatlar verilerini çek
  const fetchTahsilatlarData = async () => {
    setTahsilatlarLoading(true);
    try {
      const data = await tahsilatService.getTahsilatlar(currentUser.id, currentUser.role, currentUser.institution);
      setTahsilatlarData(data);
      console.log('📊 Tahsilatlar verisi çekildi:', data);
    } catch (error) {
      console.error('Tahsilatlar verisi çekme hatası:', error);
      setTahsilatlarData([]);
    } finally {
      setTahsilatlarLoading(false);
    }
  };

  // Tahsilatlar verilerini filtrele
  const getFilteredTahsilatlarData = () => {
    if (!tahsilatlarSearchTerm.trim()) {
      return tahsilatlarData;
    }
    
    return tahsilatlarData.filter(tahsilat => 
      tahsilat.hisse_sahibi.toLowerCase().includes(tahsilatlarSearchTerm.toLowerCase())
    );
  };

  // Hisse sahibi seçme fonksiyonu
  const handleTahsilatSelect = (tahsilatId) => {
    if (selectedTahsilatId === tahsilatId) {
      setSelectedTahsilatId(null);
      setSelectedTahsilatData(null);
    } else {
      setSelectedTahsilatId(tahsilatId);
      const selectedData = tahsilatlarData.find(t => t.id === tahsilatId);
      setSelectedTahsilatData(selectedData);
    }
  };

  // Tahsilat kaydetme fonksiyonu
  const handleTahsilatKaydet = async () => {
    if (!selectedTahsilatData || !tahsilatEkleFormData.tahsilatMiktari) {
      alert('Lütfen tüm alanları doldurun!');
      return;
    }

    const tahsilatMiktari = parseFloat(tahsilatEkleFormData.tahsilatMiktari);
    if (isNaN(tahsilatMiktari) || tahsilatMiktari <= 0) {
      alert('Tahsilat miktarı geçerli bir pozitif sayı olmalıdır!');
      return;
    }

    if (tahsilatMiktari > selectedTahsilatData.kalan_bakiye) {
      alert(`Tahsilat miktarı kalan bakiyeyi (${selectedTahsilatData.kalan_bakiye.toLocaleString('tr-TR')} TL) aşamaz!`);
      return;
    }

    try {
      const result = await tahsilatService.saveTahsilat({
        hisse_sahibi_id: selectedTahsilatData.id,
        tahsilat_miktari: tahsilatMiktari,
        tahsilat_tarihi: tahsilatEkleFormData.tahsilatTarihi,
        tahsilat_yolu: tahsilatEkleFormData.tahsilatYolu,
        notes: `${tahsilatEkleFormData.tahsilatYolu} ile tahsilat`
      });

      if (result.success) {
        if (result.whatsappSent) {
          alert('✅ Tahsilat başarıyla kaydedildi ve WhatsApp mesajı gönderildi!');
        } else if (result.whatsappLinkAlternative || result.whatsappLink) {
          const waUrl = result.whatsappLinkAlternative || result.whatsappLink;
          window.open(waUrl, '_blank', 'noopener,noreferrer');
          alert('✅ Tahsilat kaydedildi. WhatsApp sekmesi açıldı; mesajı göndermeyi onaylayın.');
        } else {
          alert('✅ Tahsilat başarıyla kaydedildi!');
        }

        // Modalı kapat
        setShowTahsilatEkleModal(false);
        setSelectedTahsilatId(null);
        setSelectedTahsilatData(null);
        
        // Tahsilatlar listesini yenile
        fetchTahsilatlarData();
        
        console.log('Tahsilat kaydedildi:', result.data);
      } else {
        alert(`❌ Hata: ${result.message}`);
        console.error('Tahsilat kaydetme hatası:', result);
      }
    } catch (error) {
      console.error('Tahsilat kaydetme hatası:', error);
      alert('❌ Tahsilat kaydedilirken bir hata oluştu!');
    }
  };

  // Tahsilat ekle modalını açma
  const handleTahsilatEkle = () => {
    if (selectedTahsilatData) {
      setTahsilatEkleFormData({
        tahsilatTarihi: new Date().toISOString().split('T')[0],
        tahsilatMiktari: '',
        tahsilatYolu: 'Nakit'
      });
      setShowTahsilatEkleModal(true);
    }
  };
  
  // Personel Raporları - Kullanıcıları getir
  const fetchAvailableUsers = async () => {
    try {
      if (!currentUser || !currentUser.id) {
        console.warn('currentUser veya id eksik:', currentUser);
        setAvailableUsers([]);
        return;
      }
      const data = await reportService.getUsers(currentUser.role, currentUser.institution, currentUser.id);
      // API'den dönen veri formatı kontrolü
      const users = Array.isArray(data) ? data : (data.users || data.data || []);
      setAvailableUsers(users);
    } catch (error) {
      console.error('Kullanıcılar getirme hatası:', error);
      setAvailableUsers([]);
    }
  };
  
  // Personel Raporları - Kurumları getir (sadece admin için)
  const fetchAvailableBranches = async () => {
    if (currentUser.role === 'Yönetici' || currentUser.role === 'admin') {
      try {
        const data = await reportService.getBranches();
        setAvailableBranches(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Kurumlar getirme hatası:', error);
        setAvailableBranches([]);
      }
    }
  };
  
  // Personel Raporları - Rapor verilerini getir
  const fetchPersonelRaporlari = async () => {
    setPersonelRaporlariLoading(true);
    try {
      if (!currentUser || !currentUser.id) {
        console.warn('currentUser veya id eksik, raporlar getirilemiyor:', currentUser);
        setPersonelRaporlariData([]);
        return;
      }
      
      const filters = {
        userId: currentUser.id.toString(),
        userRole: currentUser.role || '',
        userInstitution: currentUser.institution || '',
        ...(personelRaporlariFilters.userId && { filterUserId: personelRaporlariFilters.userId }),
        ...(personelRaporlariFilters.branch && { filterBranch: personelRaporlariFilters.branch }),
        ...(personelRaporlariFilters.startDate && { startDate: personelRaporlariFilters.startDate }),
        ...(personelRaporlariFilters.endDate && { endDate: personelRaporlariFilters.endDate })
      };
      
      const data = await reportService.getPersonelReport(filters);
      
      if (data.success) {
        setPersonelRaporlariData(data.reports || []);
      } else {
        console.error('Rapor getirme hatası:', data.error);
        setPersonelRaporlariData([]);
      }
    } catch (error) {
      console.error('Personel raporları getirme hatası:', error);
      setPersonelRaporlariData([]);
    } finally {
      setPersonelRaporlariLoading(false);
    }
  };
  
  // Personel Raporları modalını aç
  const handlePersonelRaporlariOpen = async () => {
    try {
      console.log('Personel Raporları butonu tıklandı', currentUser);
      console.log('Modal açılıyor, showPersonelRaporlariModal:', showPersonelRaporlariModal);
      setShowPersonelRaporlariModal(true);
      console.log('Modal state güncellendi');
      
      // Verileri yükle
      await Promise.all([
        fetchAvailableUsers().catch(err => console.error('Kullanıcılar getirme hatası:', err)),
        fetchAvailableBranches().catch(err => console.error('Kurumlar getirme hatası:', err)),
        fetchPersonelRaporlari().catch(err => console.error('Raporlar getirme hatası:', err))
      ]);
      
      console.log('Tüm veriler yüklendi');
    } catch (error) {
      console.error('Personel Raporları açılırken hata:', error);
      alert('Personel Raporları açılırken bir hata oluştu. Lütfen konsolu kontrol edin.');
    }
  };
  
  // Personel Raporları filtre değiştiğinde
  useEffect(() => {
    if (showPersonelRaporlariModal) {
      const timeoutId = setTimeout(() => {
        fetchPersonelRaporlari();
      }, 300); // 300ms debounce
      return () => clearTimeout(timeoutId);
    }
  }, [personelRaporlariFilters, showPersonelRaporlariModal]);
  
  // Kurum Raporları - Kurumları getir
  const fetchAvailableBranchesForKurum = async () => {
    if (currentUser.role === 'Yönetici' || currentUser.role === 'admin') {
      try {
        const data = await reportService.getBranches();
        setAvailableBranchesForKurum(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Kurumlar getirme hatası:', error);
        setAvailableBranchesForKurum([]);
      }
    }
  };
  
  // Kurum Raporları - Rapor verilerini getir
  const fetchKurumRaporlari = async () => {
    setKurumRaporlariLoading(true);
    try {
      if (!currentUser || !currentUser.id) {
        console.warn('currentUser veya id eksik, raporlar getirilemiyor:', currentUser);
        setKurumRaporlariData([]);
        return;
      }
      
      const filters = {
        userId: currentUser.id.toString(),
        userRole: currentUser.role || '',
        userInstitution: currentUser.institution || '',
        ...(kurumRaporlariFilters.branch && { filterBranch: kurumRaporlariFilters.branch }),
        ...(kurumRaporlariFilters.startDate && { startDate: kurumRaporlariFilters.startDate }),
        ...(kurumRaporlariFilters.endDate && { endDate: kurumRaporlariFilters.endDate })
      };
      
      console.log('📊 Kurum Raporları - İstek parametreleri:', {
        userId: currentUser.id,
        userRole: currentUser.role,
        userInstitution: currentUser.institution,
        filters: kurumRaporlariFilters
      });
      
      const data = await reportService.getKurumReport(filters);
      
      console.log('📊 Kurum Raporları - Backend yanıtı:', {
        success: data.success,
        total: data.total,
        sales_count: data.sales_count,
        tahsilat_count: data.tahsilat_count,
        reports_count: data.reports?.length || 0
      });
      
      if (data.success) {
        setKurumRaporlariData(data.reports || []);
      } else if (Array.isArray(data)) {
        setKurumRaporlariData(data);
      } else {
        console.error('Rapor getirme hatası:', data.error);
        setKurumRaporlariData([]);
      }
    } catch (error) {
      console.error('Kurum raporları getirme hatası:', error);
      setKurumRaporlariData([]);
    } finally {
      setKurumRaporlariLoading(false);
    }
  };
  
  // Kurum Raporları modalını aç
  const handleKurumRaporlariOpen = async () => {
    // Yetki kontrolü
    if (currentUser.role !== 'Kurum Mesulü' && currentUser.role !== 'Yönetici' && currentUser.role !== 'admin') {
      alert('⚠️ Bu raporu görüntülemek için yetkiniz yok.\n\nSadece Kurum Mesulü ve Sistem Yöneticisi erişebilir.');
      return;
    }
    
    try {
      console.log('Kurum Raporları butonu tıklandı', currentUser);
      setShowKurumRaporlariModal(true);
      await fetchAvailableBranchesForKurum();
      await fetchKurumRaporlari();
    } catch (error) {
      console.error('Kurum Raporları açılırken hata:', error);
      alert('Kurum Raporları açılırken bir hata oluştu. Lütfen konsolu kontrol edin.');
    }
  };
  
  // Kurum Raporları filtre değiştiğinde
  useEffect(() => {
    if (showKurumRaporlariModal) {
      const timeoutId = setTimeout(() => {
        fetchKurumRaporlari();
      }, 300); // 300ms debounce
      return () => clearTimeout(timeoutId);
    }
  }, [kurumRaporlariFilters, showKurumRaporlariModal]);
  
  // Gider Ekle - Kurumları getir
  const fetchBranchesForGider = async () => {
    try {
      const data = await expenseService.getBranches();
      const branches = Array.isArray(data) ? data : [];
      setAvailableBranchesForGider(branches);
      
      // Eğer kullanıcının kurumu varsa ve liste boş değilse, otomatik seç
      if (currentUser.institution && branches && branches.length > 0) {
        if (branches.includes(currentUser.institution)) {
          setGiderFormData(prev => ({ ...prev, branch: currentUser.institution }));
        }
      }
    } catch (error) {
      console.error('Kurumlar getirme hatası:', error);
      setAvailableBranchesForGider([]);
    }
  };
  
  // Gider Ekle modalını aç
  const handleGiderEkleOpen = async () => {
    setShowGiderEkleModal(true);
    setGiderFormErrors({});
    await fetchBranchesForGider();
    
    // Form verilerini sıfırla
    setGiderFormData({
      title: '',
      category: 'Yem',
      amount: '',
      expense_date: new Date().toISOString().split('T')[0],
      payment_method: 'Nakit',
      branch: currentUser.institution || '',
      description: '',
      document: null
    });
  };
  
  // Gider form validasyonu
  const validateGiderForm = () => {
    const errors = {};
    
    if (!giderFormData.title || giderFormData.title.trim() === '') {
      errors.title = 'Gider başlığı zorunludur';
    }
    
    if (!giderFormData.category) {
      errors.category = 'Kategori seçimi zorunludur';
    }
    
    if (!giderFormData.amount || parseFloat(giderFormData.amount) <= 0) {
      errors.amount = 'Geçerli bir tutar giriniz';
    }
    
    if (!giderFormData.expense_date) {
      errors.expense_date = 'Tarih seçimi zorunludur';
    }
    
    if (!giderFormData.payment_method) {
      errors.payment_method = 'Ödeme türü seçimi zorunludur';
    }
    
    if (!giderFormData.branch || giderFormData.branch.trim() === '') {
      errors.branch = 'Kurum seçimi zorunludur';
    }
    
    setGiderFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Gider kaydet
  const handleAddExpense = async () => {
    if (!validateGiderForm()) {
      alert('Lütfen tüm zorunlu alanları doldurun!');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('title', giderFormData.title);
      formData.append('category', giderFormData.category);
      formData.append('amount', giderFormData.amount);
      formData.append('expense_date', giderFormData.expense_date);
      formData.append('payment_method', giderFormData.payment_method);
      formData.append('branch', giderFormData.branch);
      formData.append('description', giderFormData.description || '');
      formData.append('personel_id', currentUser.id.toString());
      
      if (giderFormData.document) {
        formData.append('document', giderFormData.document);
      }
      
      const result = await expenseService.addExpense(formData);
      
      if (result.success) {
        alert('✅ Gider başarıyla kaydedildi!');
        setShowGiderEkleModal(false);
        
        // Formu sıfırla
        setGiderFormData({
          title: '',
          category: 'Yem',
          amount: '',
          expense_date: new Date().toISOString().split('T')[0],
          payment_method: 'Nakit',
          branch: currentUser.institution || '',
          description: '',
          document: null
        });
        setGiderFormErrors({});
      } else {
        alert(`❌ Hata: ${result.error}`);
        console.error('Gider kaydetme hatası:', result);
      }
    } catch (error) {
      console.error('Gider kaydetme hatası:', error);
      alert('❌ Gider kaydedilirken bir hata oluştu!');
    }
  };
  
  // Kasa Raporları - Personel ve kurum listelerini getir
  const fetchAvailableDataForKasa = async () => {
    try {
      console.log('🔍 Kasa Raporları - Personel ve kurum listeleri getiriliyor...', {
        role: currentUser.role,
        institution: currentUser.institution,
        id: currentUser.id
      });
      
      // Kullanıcıları getir
      const usersData = await reportService.getUsers(currentUser.role, currentUser.institution, currentUser.id);
      console.log('📡 Users API Response Data:', usersData);
      
      const users = Array.isArray(usersData) ? usersData : (usersData.users || usersData.data || []);
      console.log('✅ Personel listesi alındı:', users.length, 'kişi');
      setAvailableUsersForKasa(users);
      
      // Kurumları getir
      const branchesData = await reportService.getBranches();
      console.log('📡 Branches API Response Data:', branchesData);
      
      const branches = Array.isArray(branchesData) ? branchesData : (branchesData.branches || branchesData.data || []);
      console.log('✅ Kurum listesi alındı:', branches.length, 'kurum');
      setAvailableBranchesForKasa(branches);
    } catch (error) {
      console.error('❌ Kasa raporları filtre verileri getirme hatası:', error);
    }
  };
  
  // Kasa Raporları - Rapor verilerini getir
  const fetchKasaRaporlari = async () => {
    setKasaRaporlariLoading(true);
    try {
      const filters = {
        userRole: currentUser.role || ''
      };
      
      const data = await reportService.getKasaReport(filters);
      
      if (data.success) {
        setKasaRaporlariData(data);
      } else {
        console.error('Kasa raporu getirme hatası:', data.error);
        setKasaRaporlariData(null);
      }
    } catch (error) {
      console.error('Kasa raporları getirme hatası:', error);
      setKasaRaporlariData(null);
    } finally {
      setKasaRaporlariLoading(false);
    }
  };
  
  // Hayvanlar Listesi - Hayvanları getir
  const fetchHayvanlarList = async () => {
    setHayvanlarLoading(true);
    try {
      const data = await animalService.getAnimals();
      
      // Her hayvan için kesim bilgisini getir
      const hayvanlarWithKesim = await Promise.all(
        (data || []).map(async (animal) => {
          try {
            const kesimData = await kesimService.getAnimalKesim(animal.id);
            const kesimNo = kesimData.success && kesimData.kesim ? kesimData.kesim.kesim_no : null;
            
            if (kesimNo) {
              console.log(`✅ Hayvan #${animal.id} (${animal.ear_tag}) - Kesim No: ${kesimNo}`);
            }
            
            return {
              ...animal,
              kesim_no: kesimNo
            };
          } catch (err) {
            console.error(`❌ Hayvan #${animal.id} - Kesim bilgisi getirme hatası:`, err);
            return { ...animal, kesim_no: null };
          }
        })
      );
      
      setHayvanlarList(hayvanlarWithKesim || []);
    } catch (error) {
      console.error('Hayvanlar getirme hatası:', error);
      setHayvanlarList([]);
    } finally {
      setHayvanlarLoading(false);
    }
  };
  
  // Hayvanın hissedarlarını getir
  const fetchHayvanHissedarlar = async (animalId) => {
    try {
      const data = await animalService.getShareSales(animalId);
      
      // Hissedarları grupla (aynı müşteri için birden fazla satış olabilir)
      const hissedarMap = {};
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        // Tek bir obje döndüyse (eski format)
        if (data.customer_name) {
          const key = data.customer_phone || data.customer_name;
          hissedarMap[key] = {
            name: data.customer_name,
            phone: data.customer_phone,
            total_shares: data.share_count || 0,
            total_amount: data.total_amount || 0,
            paid_amount: data.paid_amount || 0
          };
        }
      } else if (Array.isArray(data)) {
        // Array döndüyse
        data.forEach(sale => {
          const key = sale.customer_phone || sale.customer_name;
          if (!hissedarMap[key]) {
            hissedarMap[key] = {
              name: sale.customer_name,
              phone: sale.customer_phone,
              total_shares: 0,
              total_amount: 0,
              paid_amount: 0
            };
          }
          hissedarMap[key].total_shares += sale.share_count || 0;
          hissedarMap[key].total_amount += sale.total_amount || 0;
          hissedarMap[key].paid_amount += sale.paid_amount || 0;
        });
      }
      
      setHayvanHissedarlar(prev => ({
        ...prev,
        [animalId]: Object.values(hissedarMap)
      }));
    } catch (error) {
      console.error('Hissedarlar getirme hatası:', error);
      setHayvanHissedarlar(prev => ({
        ...prev,
        [animalId]: []
      }));
    }
  };
  
  // Kesim bilgisi kaydet
  const handleKesimKaydet = async () => {
    if (!selectedKesimHayvan) return;
    
    if (!kesimFormData.kesim_no || parseInt(kesimFormData.kesim_no) <= 0) {
      alert('⚠️ Lütfen geçerli bir kesim numarası girin!');
      return;
    }
    
    try {
      const result = await kesimService.saveKesim({
        animal_id: selectedKesimHayvan.id,
        kesim_no: parseInt(kesimFormData.kesim_no),
        kesim_tarihi: kesimFormData.kesim_tarihi || null,
        tahmini_kesim_saati: kesimFormData.tahmini_kesim_saati || null,
        tahmini_teslim_saati: kesimFormData.tahmini_teslim_saati || null
      });
      
      if (result.success) {
        // WhatsApp mesajlarını oluştur ve gönder
        console.log('📊 Backend\'den gelen hissedar sayısı:', result.shareholders?.length || 0);
        if (result.shareholders && result.shareholders.length > 0) {
          const animal = result.animal;
          const kesimBilgisi = result.kesim_bilgisi;
          
          let whatsappSentCount = 0;
          let whatsappSkippedCount = 0;
          let whatsappErrorCount = 0;
          
          console.log('📨 Tüm hissedarlara WhatsApp mesajı otomatik olarak gönderiliyor...');
          
          // Mesajları sırayla otomatik gönder
          for (let i = 0; i < result.shareholders.length; i++) {
            const shareholder = result.shareholders[i];
            console.log(`  → [${i + 1}/${result.shareholders.length}] ${shareholder.customer_name || shareholder.name} (${shareholder.customer_phone || shareholder.phone || 'telefon yok'})`);
            
            const hayvanCinsi = animal.type === 'Büyükbaş' ? 'Büyükbaş' : 'Küçükbaş';
            const kupeNo = animal.ear_tag || 'Belirtilmemiş';
            const kesimNo = kesimBilgisi.kesim_no || 'Belirtilmemiş';
            const kesimSaati = kesimBilgisi.tahmini_kesim_saati || 'Belirtilmemiş';
            const teslimSaati = kesimBilgisi.tahmini_teslim_saati || 'Belirtilmemiş';
            const hisseSahibiAdi = shareholder.customer_name || shareholder.name || 'Değerli Müşterimiz';
            
            const message = `📢 Sultanlar Besicilik - Kesim Bilgilendirmesi



Sayın ${hisseSahibiAdi},

${hayvanCinsi} (${kupeNo}) numaralı hayvanınızın kesim sırası: ${kesimNo} olarak belirlenmiştir.

🔪 Tahmini Kesim Saati: ${kesimSaati}

📦 Tahmini Teslim Saati: ${teslimSaati}

📌 Tesisimizde oluşabilecek kalabalığı önlemek ve sizleri bekletmemek adına, tahmini kesim saatinizden 15 dakika önce gelmeniz yeterli olacaktır.

Ekiplerimiz sizi belirtilen sıraya göre yönlendirecektir.

Teşekkür ederiz.  

Sultanlar Besicilik  

📞 0 507 244 52 04`;
            
            let phoneNumber = (shareholder.customer_phone || shareholder.phone || '').replace(/\D/g, '');
            
            // Türk telefon numarası formatını düzelt (ülke kodu ekle)
            if (phoneNumber && phoneNumber.length >= 10) {
              // Eğer 0 ile başlıyorsa 0'ı kaldır
              if (phoneNumber.startsWith('0')) {
                phoneNumber = phoneNumber.substring(1);
              }
              // Eğer 90 ile başlamıyorsa ekle
              if (!phoneNumber.startsWith('90')) {
                phoneNumber = '90' + phoneNumber;
              }
              
              try {
                // Backend'e otomatik gönderme isteği gönder
                const whatsappResult = await whatsappService.sendMessage({
                  phone: phoneNumber,
                  message: message
                });
                
                if (whatsappResult.success && whatsappResult.sent) {
                  console.log(`  ✓ WhatsApp mesajı otomatik olarak gönderildi: ${phoneNumber}`);
                  whatsappSentCount++;
                } else if (
                  whatsappResult.success &&
                  (whatsappResult.whatsappLink || whatsappResult.whatsappLinkAlternative)
                ) {
                  const linkToOpen =
                    whatsappResult.whatsappLinkAlternative || whatsappResult.whatsappLink;
                  window.open(linkToOpen, '_blank', 'noopener,noreferrer');
                  console.log(`  ✓ WhatsApp için wa.me bağlantısı açıldı: ${phoneNumber}`);
                  whatsappSentCount++;
                } else {
                  console.log(`  ⚠️ WhatsApp gönderilemedi veya link üretilemedi: ${phoneNumber}`);
                  whatsappErrorCount++;
                }
                
                // Her mesaj arasında kısa bir bekleme (rate limiting için)
                if (i < result.shareholders.length - 1) {
                  await new Promise(resolve => setTimeout(resolve, 1000));
                }
              } catch (error) {
                console.error(`  ✗ WhatsApp mesaj gönderme hatası: ${error.message}`);
                whatsappErrorCount++;
              }
            } else {
              console.log(`  ✗ Telefon numarası geçersiz veya eksik: ${phoneNumber}`);
              whatsappSkippedCount++;
            }
          }
          
          console.log(`✅ Toplam ${whatsappSentCount} mesaj otomatik gönderildi, ${whatsappSkippedCount} mesaj atlandı, ${whatsappErrorCount} hata oluştu`);
          
          let alertMessage = `✅ Kesim bilgisi kaydedildi!\n\n`;
          if (whatsappSentCount > 0) {
            alertMessage += `✅ ${whatsappSentCount} hissedara WhatsApp ile ulaşıldı (otomatik gönderim veya tarayıcıda WhatsApp penceresi açıldı).\n\n`;
          }
          if (whatsappSkippedCount > 0) {
            alertMessage += `⚠️ ${whatsappSkippedCount} hissedarın telefon numarası bulunamadı veya geçersiz.\n\n`;
          }
          if (whatsappErrorCount > 0) {
            alertMessage += `❌ ${whatsappErrorCount} mesaj gönderilemedi (WhatsApp servisi kullanılamıyor olabilir).`;
          }
          
          alert(alertMessage.trim());
        } else {
          alert('✅ Kesim bilgisi kaydedildi!\n\nAncak bu hayvana ait hissedar bulunamadı.');
        }
        
        // Modal'ları kapat ve formu temizle
        setShowKesimModal(false);
        setSelectedKesimHayvan(null);
        setKesimFormData({
          kesim_no: '',
          kesim_tarihi: '',
          tahmini_kesim_saati: '',
          tahmini_teslim_saati: ''
        });
        
        // Hayvan listesini yenile
        await fetchHayvanlarList();
      } else {
        alert(`❌ Hata: ${result.error || 'Kesim bilgisi kaydedilemedi'}`);
      }
    } catch (error) {
      console.error('Kesim bilgisi kaydetme hatası:', error);
      alert('❌ Kesim bilgisi kaydedilirken bir hata oluştu!');
    }
  };
  
  // Kasa Raporları modalını aç
  const handleKasaRaporlariOpen = async () => {
    // Yetki kontrolü
    if (currentUser.role !== 'Yönetici' && currentUser.role !== 'admin') {
      alert('⚠️ Bu raporu görüntülemek için yetkiniz yok.\n\nSadece Sistem Yöneticisi erişebilir.');
      return;
    }
    
    try {
      console.log('Kasa Raporları butonu tıklandı', currentUser);
      setShowKasaRaporlariModal(true);
      
      // Rapor verilerini getir
      await fetchKasaRaporlari();
    } catch (error) {
      console.error('Kasa Raporları açılırken hata:', error);
      alert('Kasa Raporları açılırken bir hata oluştu. Lütfen konsolu kontrol edin.');
    }
  };
  
  
  // userData değiştiğinde currentUser'ı güncelle
  useEffect(() => {
    if (userData) {
      setCurrentUser({
        id: userData.id,
        name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.username,
        role: userData.role === 'admin' ? 'Yönetici' : 
              userData.role === 'manager' ? 'Kurum Mesulü' : 
              userData.role === 'employee' ? 'Personel' : 'Personel',
        institution: userData.branch || 'SULTANLAR BESİCİLİK'
      });
    }
  }, [userData]);

  // Yetki kontrolü: Sadece Yönetici veya Kurumu Çiftlik olan kullanıcılar için
  const canManageAnimals = () => {
    const isAdmin = currentUser.role === 'Yönetici' || currentUser.role === 'admin';
    const isCiftlik = currentUser.institution === 'Çiftlik' || currentUser.institution?.toLowerCase() === 'çiftlik';
    return isAdmin || isCiftlik;
  };

  const [sacrificeData, setSacrificeData] = useState({
    kesimSira: '',
    teslimSira1: '',
    teslimSira2: '',
    teslimSira3: '',
    teslimSira4: ''
  });

  // Kullanıcı rolüne göre satış verilerini filtrele
  const getFilteredSalesData = () => {
    switch (currentUser.role) {
      case 'Personel':
        // Sadece kendi satışları
        return salesData.filter(sale => sale.personelId === currentUser.id);
      case 'Kurum Mesulü':
        // Sadece kendi kurumuna ait satışlar
        return salesData.filter(sale => sale.kurum === currentUser.institution);
      case 'Yönetici':
        // Tüm satışlar
        return salesData;
      default:
        return [];
    }
  };

  // Excel dosyası indirme fonksiyonu
  const exportToExcel = () => {
    const filteredData = getFilteredSalesData();
    
    // Excel için veri hazırla
    const excelData = [
      ['ID', 'Hisse Sahibi', 'Hisse Adedi', 'Aracı', 'Kurum', 'Fiyat (TL)', 'Tarih', 'Personel', 'Hisse Başı Fiyat', 'Ödeme Yöntemi', 'Ödenen Tutar', 'Kalan Borç'],
      ...filteredData.map(sale => [
        sale.id,
        sale.hisseSahibi,
        sale.hisseAdedi,
        sale.aracı,
        sale.kurum,
        sale.fiyat,
        sale.tarih,
        sale.personelAdi,
        sale.pricePerShare || 0,
        sale.paymentMethod || 'Nakit',
        sale.paidAmount || 0,
        sale.remainingDebt || 0
      ])
    ];

    // Workbook oluştur
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    // Sütun genişliklerini ayarla
    const colWidths = [
      { wch: 5 },   // ID
      { wch: 20 },  // Hisse Sahibi
      { wch: 12 },  // Hisse Adedi
      { wch: 15 },  // Aracı
      { wch: 20 },  // Kurum
      { wch: 15 },  // Fiyat
      { wch: 12 },  // Tarih
      { wch: 20 },  // Personel
      { wch: 15 },  // Hisse Başı Fiyat
      { wch: 15 },  // Ödeme Yöntemi
      { wch: 15 },  // Ödenen Tutar
      { wch: 15 }   // Kalan Borç
    ];
    ws['!cols'] = colWidths;
    
    // Sheet'i workbook'a ekle
    XLSX.utils.book_append_sheet(wb, ws, 'Satış Raporu');
    
    // Dosyayı indir
    const fileName = `satis_raporu_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    alert('Excel dosyası başarıyla indirildi!');
  };
  const [isSacrificeModeActive, setIsSacrificeModeActive] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    username: '',
    role: '',
    branch: ''
  });
  const [profileFormErrors, setProfileFormErrors] = useState({});
  const [profileSubmitLoading, setProfileSubmitLoading] = useState(false);
  const [profileSubmitError, setProfileSubmitError] = useState('');
  const [profileSubmitSuccess, setProfileSubmitSuccess] = useState('');
  const [showAnimalForm, setShowAnimalForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    username: '',
    password: '',
    confirmPassword: '',
    role: '',
    branch: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  
  const [animalFormData, setAnimalFormData] = useState({
    type: '',
    breed: '',
    gender: '',
    earTag: '',
    birthDate: '',
    entryDate: '',
    startWeight: '',
    notes: '',
    supplier: '',
    purchasePrice: '', // Alış fiyatı
    purchasePricePerKg: '' // ₺/kg
  });
  const [animalFormErrors, setAnimalFormErrors] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [animalSubmitLoading, setAnimalSubmitLoading] = useState(false);
  const [animalSubmitError, setAnimalSubmitError] = useState('');
  const [animalSubmitSuccess, setAnimalSubmitSuccess] = useState('');
  
  const [stats, setStats] = useState({
    smallAnimals: 0,
    largeAnimals: 0,
    topBranch: 'Veri Yok',
    topSalesperson: 'Veri Yok'
  });

  const [customers] = useState([
    { id: 1, name: 'Ahmet Yılmaz', email: 'ahmet@email.com', phone: '+90 555 123 4567', status: 'Aktif' },
    { id: 2, name: 'Fatma Demir', email: 'fatma@email.com', phone: '+90 555 234 5678', status: 'Aktif' },
    { id: 3, name: 'Mehmet Kaya', email: 'mehmet@email.com', phone: '+90 555 345 6789', status: 'Pasif' }
  ]);

  const [animals, setAnimals] = useState([]);
  const [animalsLoading, setAnimalsLoading] = useState(true);
  const [animalsError, setAnimalsError] = useState('');

  // Hayvan işlemleri için state'ler
  const [showAnimalViewModal, setShowAnimalViewModal] = useState(false);
  const [showAnimalEditModal, setShowAnimalEditModal] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [editAnimalData, setEditAnimalData] = useState({});
  const [editAnimalErrors, setEditAnimalErrors] = useState({});
  const [editSubmitLoading, setEditSubmitLoading] = useState(false);
  const [editSubmitError, setEditSubmitError] = useState('');
  const [editSubmitSuccess, setEditSubmitSuccess] = useState('');
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [animalToDelete, setAnimalToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // 1. State'ler
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [selectedWeightAnimal, setSelectedWeightAnimal] = useState(null);
  const [selectedSaleAnimal, setSelectedSaleAnimal] = useState(null);
  // 1. State ekle
  const [selectedRowId, setSelectedRowId] = useState(null);
  // 1. State'ler
  const [weightDate, setWeightDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [weightValue, setWeightValue] = useState('');
  const [weightError, setWeightError] = useState('');
  const [weightSuccess, setWeightSuccess] = useState('');
  // State ekle
  const [animalWeights, setAnimalWeights] = useState([]);
  const [animalWeightsLoading, setAnimalWeightsLoading] = useState(false);
  const [animalWeightsError, setAnimalWeightsError] = useState('');

  const settingsRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active'); // Sadece aktif hayvanları göster
  const [filterBreed, setFilterBreed] = useState('all'); // Irk filtresi
  const [filterGender, setFilterGender] = useState('all'); // Cins filtresi

  // Satışa Ekle modalı için state'ler
  const [saleShareCount, setSaleShareCount] = useState(1);
  const [saleTotalPrice, setSaleTotalPrice] = useState('');
  const [salePricePerKg, setSalePricePerKg] = useState('');
  const [salePricePerShare, setSalePricePerShare] = useState('');
  const [saleError, setSaleError] = useState('');
  const [saleSuccess, setSaleSuccess] = useState('');

  const getSaleWeightKg = (animal) => {
    if (!animal) return NaN;
    const raw = animal.last_weight != null && animal.last_weight !== '' ? animal.last_weight : animal.start_weight;
    const n = parseFloat(String(raw).replace(/[^0-9.,]/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  };

  const parseSaleMoney = (s) => {
    const n = parseFloat(String(s ?? '').replace(/[^0-9.,]/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  };

  const formatSaleMoneyForInput = (n) => {
    if (!Number.isFinite(n)) return '';
    return n.toFixed(2).replace('.', ',');
  };

  const handleSaleTotalChange = (e) => {
    const val = e.target.value.replace(/[^0-9.,]/g, '');
    setSaleTotalPrice(val);
    const w = getSaleWeightKg(selectedSaleAnimal);
    const total = parseSaleMoney(val);
    if (Number.isFinite(w) && w > 0 && Number.isFinite(total) && total >= 0) {
      setSalePricePerKg(formatSaleMoneyForInput(total / w));
    }
  };

  const handleSalePricePerKgChange = (e) => {
    const val = e.target.value.replace(/[^0-9.,]/g, '');
    setSalePricePerKg(val);
    const w = getSaleWeightKg(selectedSaleAnimal);
    const pkg = parseSaleMoney(val);
    if (Number.isFinite(w) && w > 0 && Number.isFinite(pkg) && pkg >= 0) {
      setSaleTotalPrice(formatSaleMoneyForInput(pkg * w));
    }
  };

  // Hisse Satış formu için state'ler
  const [showShareSaleForm, setShowShareSaleForm] = useState(false);
  const [selectedShareSaleAnimal, setSelectedShareSaleAnimal] = useState(null);
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [shareSaleFormData, setShareSaleFormData] = useState({
    customerName: '',
    customerPhone: '',
    shareCount: 1,
    pricePerShare: '',
    totalAmount: '',
    paymentMethod: '',
    paidAmount: '',
    remainingDebt: '',
    paymentDate: new Date().toISOString().slice(0, 10)
  });
  const [shareSaleFormErrors, setShareSaleFormErrors] = useState({});
  const [shareSaleSubmitLoading, setShareSaleSubmitLoading] = useState(false);
  const [shareSaleSubmitError, setShareSaleSubmitError] = useState('');
  const [shareSaleSubmitSuccess, setShareSaleSubmitSuccess] = useState('');


  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changePasswordStep, setChangePasswordStep] = useState(1);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordRepeat, setNewPasswordRepeat] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState('');

  // Kullanıcı listesi için yeni state'ler
  const [showUserListModal, setShowUserListModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUserData, setEditUserData] = useState(null);
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [editUserError, setEditUserError] = useState('');
  const [editUserSuccess, setEditUserSuccess] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordRepeat, setShowNewPasswordRepeat] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  const [showShareSaleListModal, setShowShareSaleListModal] = useState(false);

  // Diğer useState'lerin yanına ekle:
  const [shareSaleSearch, setShareSaleSearch] = useState('');
  const [shareSaleType, setShareSaleType] = useState('all');
  const [shareSaleBreed, setShareSaleBreed] = useState('all');
  const [shareSaleGender, setShareSaleGender] = useState('all');
  
  // Hisse satışları için state'ler
  const [shareSales, setShareSales] = useState([]);
  const [shareSalesLoading, setShareSalesLoading] = useState(false);
  const [shareSalesError, setShareSalesError] = useState('');

  // Kullanıcıları çekme fonksiyonu
  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const data = await userService.getUsers();
      const users = Array.isArray(data) ? data : (data.users || []);
      setUsers(users);
    } catch (err) {
      setUsersError('Kullanıcılar yüklenemedi');
    }
    setUsersLoading(false);
  };
  
  // Hisse satışlarını çekme fonksiyonu
  const fetchShareSales = async () => {
    setShareSalesLoading(true);
    setShareSalesError('');
    try {
      const data = await animalService.getAllShareSales();
      setShareSales(Array.isArray(data) ? data : []);
    } catch (err) {
      setShareSalesError(err.message || 'Hisse satışları yüklenemedi');
    }
    setShareSalesLoading(false);
  };

  // Kullanıcı Ayarları butonuna tıklanınca kullanıcı listesi modalını aç
  const handleShowUserList = () => {
    setShowUserListModal(true);
    fetchUsers();
  };

  // Kullanıcı ekleme formu açıldığında, kullanıcı listesi modalı kapalı olmalı
  const handleShowUserForm = () => {
    setShowUserForm(true);
    setShowUserListModal(false);
  };

  // Kullanıcı ekleme başarılı olursa kullanıcı listesini güncelle
  useEffect(() => {
    if (!showUserForm && showUserListModal) {
      fetchUsers();
    }
  }, [showUserForm, showUserListModal]);

  // Dashboard verilerini çek
  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchDashboardData = async () => {
      try {
        // Küçükbaş hayvan sayısını çek
        const smallAnimalsData = await animalService.getCounts.small();
        setStats(prevStats => ({
          ...prevStats,
          smallAnimals: smallAnimalsData.total || 0
        }));

        // Büyükbaş hayvan sayısını çek
        const largeAnimalsData = await animalService.getCounts.large();
        setStats(prevStats => ({
          ...prevStats,
          largeAnimals: largeAnimalsData.total || 0
        }));

        // En çok satış yapan kurum
        const branchData = await animalService.getTopBranch();
        setStats(prevStats => ({
          ...prevStats,
          topBranch: branchData.branch || '-'
        }));

        // En çok satış yapan personel
        const salespersonData = await animalService.getTopSalesperson();
        setStats(prevStats => ({
          ...prevStats,
          topSalesperson: salespersonData.name || '-'
        }));

      } catch (error) {
        console.error('Dashboard verileri çekme hatası:', error);
      }
    };

    fetchDashboardData();
  }, [isLoggedIn]);

  // Hisse satışları Excel raporu indirme
  const handleDownloadShareSaleReport = () => {
    if (shareSales.length === 0) {
      alert('İndirilecek hisse satışı bulunamadı!');
      return;
    }
    
    const worksheet = XLSX.utils.json_to_sheet(shareSales.map(sale => ({
      'ID': sale.id,
      'Hisse Türü': sale.animal_type || '',
      'Hisse Cinsi': sale.animal_gender || '',
      'Hisse Sahibi': sale.customer_name || '',
      'Hisse Adedi': sale.share_count || 0,
      'Aracı Olan': sale.reference_user_name || '',
      'Kurum': sale.reference_user_branch || ''
    })));
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Hisse Satışları');
    
    XLSX.writeFile(workbook, `Hisse_Satis_Raporu_${new Date().toLocaleDateString('tr-TR')}.xlsx`);
  };
  


  // Satışa Ekle modalı açıldığında state'i güncelle
  useEffect(() => {
    if (showSaleModal && selectedSaleAnimal) {
      if (selectedSaleAnimal.type === 'Büyükbaş') {
        setSaleShareCount(1);
      } else {
        setSaleShareCount(1);
      }
      setSaleTotalPrice('');
      setSalePricePerKg('');
      setSalePricePerShare('');
      setSaleError('');
      setSaleSuccess('');
    }
  }, [showSaleModal, selectedSaleAnimal]);

  // Toplam fiyat değişince hisse başı fiyatı otomatik hesapla
  useEffect(() => {
    if (!saleTotalPrice || !saleShareCount) {
      setSalePricePerShare('');
      return;
    }
    const total = parseFloat(saleTotalPrice.replace(/[^0-9.,]/g, '').replace(',', '.'));
    if (isNaN(total) || total <= 0) {
      setSalePricePerShare('');
      return;
    }
    const perShare = total / saleShareCount;
    setSalePricePerShare(perShare.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺');
  }, [saleTotalPrice, saleShareCount]);

  // Satışa Ekle form submit
  const handleSaleSubmit = async (e) => {
    e.preventDefault();
    setSaleError('');
    setSaleSuccess('');
    if (!saleTotalPrice || !saleShareCount) {
      setSaleError('Tüm alanlar zorunludur');
      return;
    }
    const wKg = getSaleWeightKg(selectedSaleAnimal);
    if (!Number.isFinite(wKg) || wKg <= 0) {
      setSaleError('Mevcut kilo bulunamadı. Son tartım veya başlangıç kilosu gerekli.');
      return;
    }
    const total = parseSaleMoney(saleTotalPrice);
    if (isNaN(total) || total <= 0) {
      setSaleError('Toplam fiyat geçerli bir sayı olmalı');
      return;
    }
    try {
      const payload = {
        shareCount: saleShareCount,
        totalPrice: total
      };
      const pk = parseSaleMoney(salePricePerKg);
      if (Number.isFinite(pk) && pk >= 0) {
        payload.pricePerKg = pk;
      }
      await animalService.sellAnimal(selectedSaleAnimal.id, payload);
      setSaleSuccess('Satış başarıyla eklendi');
      // Hayvanlar listesini yenile
      await refreshAnimals();
      setTimeout(() => {
        setShowSaleModal(false);
        setSaleSuccess('');
      }, 1200);
    } catch (err) {
      setSaleError(err.message || 'Bir hata oluştu');
    }
  };

  // Hayvan ekleme başarılı olduğunda listeyi yenile
  const refreshAnimals = async () => {
    try {
      setAnimalsLoading(true);
      setAnimalsError('');
      const data = await animalService.getAnimals();
      const processedData = data.map((animal, index) => {
        if (!animal.ear_tag) {
          const typePrefix = animal.type === 'Büyükbaş' ? 'BB' : 'KB';
          const autoNumber = String(index + 1).padStart(3, '0');
          animal.ear_tag = `${typePrefix}${autoNumber}`;
        }
        return animal;
      });
      setAnimals(processedData);
    } catch (err) {
      console.error('Hayvanlar yenileme hatası:', err);
      setAnimalsError('Hayvanlar yüklenirken hata oluştu');
    } finally {
      setAnimalsLoading(false);
    }
  };

  // Oturum açıkken listeleri API'den yükle; çıkışta temizle (DB sıfırlama sonrası eski state kalmasın)
  useEffect(() => {
    if (!isLoggedIn) {
      setShareSales([]);
      setAnimals([]);
      return;
    }
    fetchShareSales();
    refreshAnimals();
  }, [isLoggedIn]);

  // Hayvan görüntüleme
  const handleViewAnimal = async (animal) => {
    console.log('🔍 Hayvan görüntüleme - Animal objesi:', animal);
    console.log('🔍 image_url değeri:', animal?.image_url || animal?.imageUrl);
    
    // Modal açılırken güncel hayvan verisini backend'den çek
    try {
      const updatedAnimal = await animalService.getAnimalById(animal.id);
      console.log('✅ Güncel hayvan verisi alındı:', updatedAnimal);
      console.log('✅ Güncel image_url:', updatedAnimal?.image_url);
      if (updatedAnimal) {
        setSelectedAnimal(updatedAnimal);
      } else {
        setSelectedAnimal(animal);
      }
    } catch (error) {
      console.error('❌ Hayvan verisi çekme hatası:', error);
      // Hata durumunda mevcut veriyi kullan
      setSelectedAnimal(animal);
    }
    
    setImageLoadError(false); // Modal açıldığında hata durumunu sıfırla
    setShowAnimalViewModal(true);
  };

  // Hayvan düzenleme
  const handleEditAnimal = (animal) => {
    setSelectedAnimal(animal);
    setEditAnimalData({
      type: animal.type,
      breed: animal.breed,
      gender: animal.gender,
      earTag: animal.ear_tag || '',
      birthDate: animal.birth_date || '',
      entryDate: animal.entry_date || '',
      startWeight: animal.start_weight || '',
      notes: animal.notes || '',
      supplier: animal.supplier || ''
    });
    setEditAnimalErrors({});
    setEditSubmitError('');
    setEditSubmitSuccess('');
    setShowAnimalEditModal(true);
  };

  // Hayvan düzenleme form submit
  const handleEditAnimalSubmit = async (e) => {
    e.preventDefault();
    
    setEditSubmitError('');
    setEditSubmitSuccess('');
    
    // Form validasyonu
    const errors = {};
    
    if (!editAnimalData.type) errors.type = 'Hayvan türü seçimi zorunludur';
    if (!editAnimalData.breed.trim()) errors.breed = 'Irk alanı zorunludur';
    if (!editAnimalData.gender) errors.gender = 'Cins seçimi zorunludur';
    if (!editAnimalData.earTag.trim()) errors.earTag = 'Küpe/Hayvan No alanı zorunludur';
    if (!editAnimalData.entryDate) errors.entryDate = 'Giriş tarihi zorunludur';
    if (!editAnimalData.startWeight) errors.startWeight = 'Başlangıç kilosu zorunludur';
    if (!editAnimalData.supplier.trim()) errors.supplier = 'Alındığı kişi/firma alanı zorunludur';
    
    // Kilo kontrolü
    const weight = parseFloat(editAnimalData.startWeight);
    if (isNaN(weight) || weight < 1 || weight > 2000) {
      errors.startWeight = 'Başlangıç kilosu 1-2000 kg arası olmalıdır';
    }
    
    if (Object.keys(errors).length > 0) {
      setEditAnimalErrors(errors);
      return;
    }
    
    try {
      setEditSubmitLoading(true);
      
      const formData = new FormData();
      formData.append('type', editAnimalData.type);
      formData.append('breed', editAnimalData.breed.trim());
      formData.append('gender', editAnimalData.gender);
      formData.append('earTag', editAnimalData.earTag.trim());
      formData.append('entryDate', editAnimalData.entryDate);
      formData.append('startWeight', editAnimalData.startWeight);
      formData.append('supplier', editAnimalData.supplier.trim());
      
      if (editAnimalData.birthDate) {
        formData.append('birthDate', editAnimalData.birthDate);
      }
      if (editAnimalData.notes.trim()) {
        formData.append('notes', editAnimalData.notes.trim());
      }
      
      await animalService.updateAnimal(selectedAnimal.id, formData);

      setEditSubmitSuccess('Hayvan başarıyla güncellendi');
      
      // Listeyi yenile
      await refreshAnimals();
      
      // Modalı kısa bir süre sonra kapat
      setTimeout(() => {
        setShowAnimalEditModal(false);
        setEditSubmitSuccess('');
      }, 1500);
    } catch (err) {
      setEditSubmitError(err.message || 'Güncelleme sırasında bir hata oluştu');
    } finally {
      setEditSubmitLoading(false);
    }
  };

  // Kullanıcı bilgilerini veritabanından çek
  const fetchCurrentUserInfo = async () => {
    try {
      if (userData?.id) {
        const userInfo = await userService.getUserById(userData.id);
        return userInfo;
      }
    } catch (error) {
      console.error('Kullanıcı bilgileri çekilemedi:', error);
    }
    
    // API'den veri gelmezse, mevcut userData'dan bilgileri al
    if (userData) {
      return {
        id: userData.id,
        first_name: userData.first_name || 'Bilinmiyor',
        last_name: userData.last_name || 'Bilinmiyor',
        branch: userData.branch || 'Sultanlar Besicilik'
      };
    }
    
    return null;
  };

  // Hayvanın satış bilgilerini veritabanından çek
  const fetchAnimalSalesInfo = async (animalId) => {
    try {
      console.log('🔍 Hayvan satış bilgileri çekiliyor, Animal ID:', animalId);
      const salesInfo = await animalService.getAnimalSales(animalId);
      
      if (salesInfo) {
        console.log('📡 API Response:', salesInfo);
        console.log('✅ Satış bilgileri alındı:', salesInfo);
        return salesInfo;
      } else {
        console.log('ℹ️ Satış bilgisi bulunamadı');
        return null;
      }
    } catch (error) {
      console.error('❌ Hayvan satış bilgileri çekilemedi:', error);
      return null;
    }
    return null;
  };

  // Hisse satış formunu aç
  const handleOpenShareSaleForm = async (animal) => {
    console.log('🚀 Hisse satış formu açılıyor, Hayvan:', animal);
    setSelectedShareSaleAnimal(animal);
    
    // Güncel kullanıcı bilgilerini veritabanından çek
    const currentUserInfo = await fetchCurrentUserInfo();
    setCurrentUserInfo(currentUserInfo);
    
    // Hayvanın satış bilgilerini veritabanından çek
    const animalSalesInfo = await fetchAnimalSalesInfo(animal.id);
    console.log('💰 Hayvan satış bilgileri:', animalSalesInfo);
    
    // Hisse başı fiyatı belirle (veritabanından gelen bilgi varsa onu kullan, yoksa 0)
    const currentPricePerShare = animalSalesInfo?.price_per_share || 0;
    console.log('💵 Hisse başı fiyat:', currentPricePerShare);
    
    const formData = {
      customerName: '',
      customerPhone: '',
      shareCount: 1,
      pricePerShare: currentPricePerShare.toString(),
      totalAmount: (currentPricePerShare * 1).toString(),
      paymentMethod: '',
      paidAmount: '',
      remainingDebt: '',
      paymentDate: new Date().toISOString().slice(0, 10)
    };
    
    console.log('📝 Form verisi set ediliyor:', formData);
    setShareSaleFormData(formData);
    
    setShareSaleFormErrors({});
    setShareSaleSubmitError('');
    setShareSaleSubmitSuccess('');
    setShowShareSaleForm(true);
  };

  // Hisse satış form input değişiklikleri
  const handleShareSaleInputChange = (e) => {
    const { name, value } = e.target;
    
    setShareSaleFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Otomatik hesaplamalar
    if (name === 'shareCount' || name === 'pricePerShare') {
      const shareCount = name === 'shareCount' ? parseInt(value) : parseInt(shareSaleFormData.shareCount);
      const pricePerShare = name === 'pricePerShare' ? parseFloat(value) : parseFloat(shareSaleFormData.pricePerShare);
      
      if (!isNaN(shareCount) && !isNaN(pricePerShare)) {
        const totalAmount = shareCount * pricePerShare;
        const paidAmount = parseFloat(shareSaleFormData.paidAmount) || 0;
        const remainingDebt = totalAmount - paidAmount;
        
        setShareSaleFormData(prev => ({
          ...prev,
          totalAmount: totalAmount.toString(),
          remainingDebt: remainingDebt.toString()
        }));
      }
    }

    if (name === 'paidAmount') {
      const totalAmount = parseFloat(shareSaleFormData.totalAmount) || 0;
      const paidAmount = parseFloat(value) || 0;
      const remainingDebt = totalAmount - paidAmount;
      
      setShareSaleFormData(prev => ({
        ...prev,
        remainingDebt: remainingDebt.toString()
      }));
    }
  };

  // Hisse satış form submit
  const handleShareSaleSubmit = async (e) => {
    e.preventDefault();
    
    setShareSaleSubmitError('');
    setShareSaleSubmitSuccess('');
    
    // Hayvan seçili mi kontrol et
    if (!selectedShareSaleAnimal) {
      setShareSaleSubmitError('Lütfen önce bir hayvan seçin');
      return;
    }
    
    // Form validasyonu
    const errors = {};
    
    if (!shareSaleFormData.customerName.trim()) errors.customerName = 'Müşteri adı zorunludur';
    if (!shareSaleFormData.customerPhone.trim()) errors.customerPhone = 'Telefon numarası zorunludur';
    if (!shareSaleFormData.shareCount || shareSaleFormData.shareCount < 1) errors.shareCount = 'Hisse sayısı zorunludur';
    if (!shareSaleFormData.pricePerShare || parseFloat(shareSaleFormData.pricePerShare) <= 0) errors.pricePerShare = 'Hisse başı fiyat zorunludur';
    if (!shareSaleFormData.paymentMethod) errors.paymentMethod = 'Ödeme yöntemi seçimi zorunludur';
    if (!shareSaleFormData.paymentDate) errors.paymentDate = 'Ödeme tarihi zorunludur';
    
    // Maksimum hisse kontrolü
    const maxShares = selectedShareSaleAnimal?.type === 'Büyükbaş' ? 7 : 1;
    const currentSoldShares = selectedShareSaleAnimal?.sold_shares || 0;
    const requestedShares = parseInt(shareSaleFormData.shareCount);
    
    if (currentSoldShares + requestedShares > maxShares) {
      errors.shareCount = `Bu hayvandan maksimum ${maxShares} hisse satılabilir. Zaten ${currentSoldShares} hisse satılmış.`;
    }
    
    // Telefon format kontrolü
    const phoneRegex = /^05\d{9}$/;
    if (!phoneRegex.test(shareSaleFormData.customerPhone.replace(/\s/g, ''))) {
      errors.customerPhone = 'Telefon formatı: 05xx xxx xx xx olmalıdır';
    }
    
    if (Object.keys(errors).length > 0) {
      setShareSaleFormErrors(errors);
      return;
    }
    
    try {
      setShareSaleSubmitLoading(true);
      
      // Backend'e hisse satış kaydı gönder
      const result = await animalService.createShareSale(selectedShareSaleAnimal.id, {
        customerName: shareSaleFormData.customerName.trim(),
        customerPhone: shareSaleFormData.customerPhone.trim(),
        shareCount: parseInt(shareSaleFormData.shareCount),
        pricePerShare: parseFloat(shareSaleFormData.pricePerShare),
        totalAmount: parseFloat(shareSaleFormData.totalAmount),
        paymentMethod: shareSaleFormData.paymentMethod,
        paidAmount: parseFloat(shareSaleFormData.paidAmount || 0),
        remainingDebt: parseFloat(shareSaleFormData.remainingDebt || 0),
        paymentDate: shareSaleFormData.paymentDate,
        referenceUserId: currentUserInfo.id
      });
      
      setShareSaleSubmitSuccess('Hisse satışı ve tahsilat kaydı başarıyla kaydedildi!');
      
      // WhatsApp mesajı gönder
      try {
        const paymentStatus = result.paidAmount >= result.totalAmount 
          ? 'Ödendi' 
          : result.paidAmount > 0 
            ? 'Kısmi' 
            : 'Beklemede';
        
        const animalInfo = result.animal 
          ? `${result.animal.breed} (Küpe No: ${result.animal.ear_tag || 'Belirtilmemiş'})`
          : selectedShareSaleAnimal?.breed || 'Hayvan';
        
        const whatsappData = await whatsappService.sendMessage({
          phone: result.customerPhone || shareSaleFormData.customerPhone.trim(),
          customerName: result.customerName || shareSaleFormData.customerName.trim(),
          date: result.paymentDate || shareSaleFormData.paymentDate,
          animalInfo: animalInfo,
          shareCount: result.shareCount || parseInt(shareSaleFormData.shareCount),
          totalAmount: result.totalAmount || parseFloat(shareSaleFormData.totalAmount),
          paymentStatus: paymentStatus,
          animalType: result.animalType || selectedShareSaleAnimal?.type || 'Belirtilmemiş',
          farmName: 'SULTANLAR BESİCİLİK',
          contactPhone: '0 507 244 52 04',
          imageUrl: result.animal?.image_url || selectedShareSaleAnimal?.image_url || null
        });
        
        if (whatsappData) {
          if (whatsappData.sent && whatsappData.method === 'whatsapp-web.js') {
            // Otomatik gönderim başarılı
            console.log('✅ WhatsApp mesajı otomatik olarak gönderildi!');
            setShareSaleSubmitSuccess('Hisse satışı kaydedildi ve WhatsApp mesajı otomatik gönderildi!');
          } else {
            // Link yöntemi - WhatsApp açılacak
            const linkToOpen = whatsappData.whatsappLink || whatsappData.whatsappLinkAlternative;
            
            if (linkToOpen) {
              window.open(linkToOpen, '_blank');
              console.log('✅ WhatsApp linki açıldı - Mesajı manuel olarak gönderebilirsiniz');
              
              if (whatsappData.needsSetup) {
                console.warn('⚠️ WhatsApp otomatik gönderim aktif değil. QR kod ile bağlantı kurmak için backend konsolunu kontrol edin.');
              }
            }
            
            console.log('📱 Mesaj içeriği:', whatsappData.message);
          }
        } else {
          console.warn('⚠️ WhatsApp mesajı oluşturulamadı');
        }
      } catch (whatsappError) {
        console.error('❌ WhatsApp mesajı gönderme hatası:', whatsappError);
        // WhatsApp hatası olsa bile işlem devam etsin
      }
      
      // 3 saniye sonra formu kapat
      setTimeout(() => {
        setShowShareSaleForm(false);
        setSelectedShareSaleAnimal(null);
        setShareSaleSubmitSuccess('');
        // Listeyi yenile
        refreshAnimals();
      }, 3000);
    } catch (err) {
      console.error('Hisse satışı kaydetme hatası:', err);
      setShareSaleSubmitError('Sunucu hatası oluştu');
    } finally {
      setShareSaleSubmitLoading(false);
    }
  };

  // Hisse satış formunu temizle
  const handleShareSaleReset = async () => {
    if (selectedShareSaleAnimal) {
      // Hayvanın güncel satış bilgilerini tekrar çek
      const animalSalesInfo = await fetchAnimalSalesInfo(selectedShareSaleAnimal.id);
      const currentPricePerShare = animalSalesInfo?.price_per_share || 0;
      
      setShareSaleFormData({
        customerName: '',
        customerPhone: '',
        shareCount: 1,
        pricePerShare: currentPricePerShare.toString(),
        totalAmount: (currentPricePerShare * 1).toString(),
        paymentMethod: '',
        paidAmount: '',
        remainingDebt: '',
        paymentDate: new Date().toISOString().slice(0, 10)
      });
    } else {
      setShareSaleFormData({
        customerName: '',
        customerPhone: '',
        shareCount: 1,
        pricePerShare: '',
        totalAmount: '',
        paymentMethod: '',
        paidAmount: '',
        remainingDebt: '',
        paymentDate: new Date().toISOString().slice(0, 10)
      });
    }
    setShareSaleFormErrors({});
  };

  // Hayvan silme onayı
  const handleDeleteAnimal = (animal) => {
    setAnimalToDelete(animal);
    setDeleteConfirmModal(true);
  };

  // Hayvan silme işlemi
  const confirmDeleteAnimal = async () => {
    if (!animalToDelete) return;
    
    try {
      setDeleteLoading(true);
      
      await animalService.deleteAnimal(animalToDelete.id);

      // Hemen local state'den kaldır (anında UI güncellemesi için)
      setAnimals(prevAnimals => prevAnimals.filter(animal => animal.id !== animalToDelete.id));
      
      // Modalı kapat
      setDeleteConfirmModal(false);
      setAnimalToDelete(null);
      
      // Başarı mesajı göster
      alert('Hayvan başarıyla silindi');
      
      // Arka planda listeyi de yenile (veritabanı senkronizasyonu için)
      setTimeout(() => {
        refreshAnimals();
      }, 100);
    } catch (err) {
      alert('Hata: ' + err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Edit form input değişiklikleri
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditAnimalData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Hata mesajını temizle
    if (editAnimalErrors[name]) {
      setEditAnimalErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleEditInputBlur = (e) => {
    const { name, value } = e.target;
    
    // Alındığı Kişi/Firma alanı için otomatik format düzeltme
    if (name === 'supplier' && value.trim()) {
      const words = value.trim().split(' ');
      
      if (words.length >= 2) {
        // İlk kelime (ad): İlk harf büyük, diğerleri küçük
        const firstName = words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();
        // Son kelime (soyad): Tümü büyük harf
        const lastName = words[words.length - 1].toUpperCase();
        // Ortadaki kelimeler (varsa): İlk harf büyük, diğerleri küçük
        const middleNames = words.slice(1, -1).map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        );
        
        const formattedValue = [firstName, ...middleNames, lastName].join(' ');
        setEditAnimalData(prev => ({
          ...prev,
          [name]: formattedValue
        }));
      } else if (words.length === 1) {
        // Tek kelime: İlk harf büyük, diğerleri küçük
        const formattedValue = words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();
        setEditAnimalData(prev => ({
          ...prev,
          [name]: formattedValue
        }));
      }
    }
  };

  // Dinamik filtre seçenekleri oluştur
  const uniqueBreeds = [...new Set(animals.map(animal => animal.breed).filter(Boolean))].sort();
  const uniqueGenders = [...new Set(animals.map(animal => animal.gender).filter(Boolean))].sort();

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAnimals = animals.filter(animal =>
    // Durum filtresi
    (filterStatus === 'all' || animal.status === filterStatus) &&
    // Arama filtresi - Çoklu alan arama
    (searchTerm === '' || 
      // Irk arama
      (animal.breed && animal.breed.toLowerCase().includes(searchTerm.toLowerCase())) ||
      // Küpe/Hayvan No arama
      (animal.ear_tag && animal.ear_tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
      // Tür arama
      (animal.type && animal.type.toLowerCase().includes(searchTerm.toLowerCase())) ||
      // Cins arama
      (animal.gender && animal.gender.toLowerCase().includes(searchTerm.toLowerCase())) ||
      // ID arama (sayısal)
      (animal.id && animal.id.toString().includes(searchTerm))
    ) &&
    // Tür filtresi
    (filterType === 'all' || animal.type === filterType) &&
    // Irk filtresi
    (filterBreed === 'all' || animal.breed === filterBreed) &&
    // Cins filtresi
    (filterGender === 'all' || animal.gender === filterGender)
  );

  const handleLogin = (loginData) => {
    setIsLoggedIn(true);
    setUserData(loginData);
    
    // Local storage'a kaydet (eğer "Beni hatırla" seçiliyse)
    if (loginData.rememberMe) {
      localStorage.setItem('sultanlarAdmin', JSON.stringify(loginData));
    }
  };

  // Tüm filtreleri sıfırla
  const resetAllFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterStatus('active');
    setFilterBreed('all');
    setFilterGender('all');
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUserData(null);
    localStorage.removeItem('sultanlarAdmin');
  };

  const handleSystemSettingsBackup = async () => {
    if (!userData?.username) {
      alert('Oturum bilgisi bulunamadı.');
      return;
    }
    if (!systemAdminPassword.trim()) {
      alert('Yönetici şifrenizi girin.');
      return;
    }
    setSystemSettingsLoading(true);
    setSystemSettingsMessage('');
    try {
      const res = await fetch(API_ENDPOINTS.SYSTEM_BACKUP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: userData.username, password: systemAdminPassword })
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Yedekleme başarısız');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sultanlar_yedek_${new Date().toISOString().slice(0, 10)}.db`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSystemSettingsMessage('Yedek dosyası indirildi.');
    } catch (err) {
      alert(err.message || 'Yedekleme hatası');
    } finally {
      setSystemSettingsLoading(false);
    }
  };

  const handleSystemSettingsReset = async () => {
    if (!userData?.username) {
      alert('Oturum bilgisi bulunamadı.');
      return;
    }
    if (!systemAdminPassword.trim()) {
      alert('Yönetici şifrenizi girin.');
      return;
    }
    if (!window.confirm('TÜM VERİLERİ SİLMEK ÜZERESİNİZ. Bu işlem geri alınamaz. Devam edilsin mi?')) return;
    if (!window.confirm('Son onay: Tüm kayıtlar silinecek; yalnızca varsayılan admin hesabı oluşturulacak. Emin misiniz?')) return;
    setSystemSettingsLoading(true);
    setSystemSettingsMessage('');
    try {
      const res = await fetch(API_ENDPOINTS.SYSTEM_RESET, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: userData.username, password: systemAdminPassword })
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || 'Sıfırlama başarısız');
      setSystemSettingsMessage(j.message || 'Sıfırlama tamamlandı.');
      alert(j.message || 'Sıfırlama tamamlandı. Sayfayı yenileyip admin ile tekrar giriş yapın.');
      setSystemAdminPassword('');
      setShowSystemSettingsModal(false);
      handleLogout();
    } catch (err) {
      alert(err.message || 'Hata');
    } finally {
      setSystemSettingsLoading(false);
    }
  };

  const handleSystemSettingsRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!userData?.username) {
      alert('Oturum bilgisi bulunamadı.');
      return;
    }
    if (!systemAdminPassword.trim()) {
      alert('Önce yönetici şifrenizi girin.');
      return;
    }
    if (!window.confirm('Mevcut veritabanı bu dosya ile değiştirilecek. Backend birkaç saniye içinde yeniden başlayacak. Devam?')) return;
    setSystemSettingsLoading(true);
    setSystemSettingsMessage('');
    try {
      const formData = new FormData();
      formData.append('database', file);
      formData.append('username', userData.username);
      formData.append('password', systemAdminPassword);
      const res = await fetch(API_ENDPOINTS.SYSTEM_RESTORE, {
        method: 'POST',
        body: formData
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || 'Yükleme başarısız');
      setSystemSettingsMessage(j.message || '');
      alert(j.message || 'İşlem tamamlandı.');
      setShowSystemSettingsModal(false);
      setSystemAdminPassword('');
    } catch (err) {
      if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
        alert('Sunucu yeniden başlatılıyor olabilir. Birkaç saniye sonra sayfayı yenileyin.');
      } else {
        alert(err.message || 'Hata');
      }
    } finally {
      setSystemSettingsLoading(false);
    }
  };

  // Sayfa yüklendiğinde local storage'dan giriş bilgilerini kontrol et
  useEffect(() => {
    try {
      const savedLogin = localStorage.getItem('sultanlarAdmin');
      if (savedLogin) {
        const loginData = JSON.parse(savedLogin);
        if (loginData && typeof loginData === 'object' && loginData.username) {
          setIsLoggedIn(true);
          setUserData(loginData);
        } else {
          localStorage.removeItem('sultanlarAdmin');
        }
      }
    } catch (err) {
      console.error('Kayıtlı oturum verisi bozuk. Temizleniyor...', err);
      localStorage.removeItem('sultanlarAdmin');
    }
  }, []);

  // Kurban modu bilgilerini localStorage'dan yükle
  useEffect(() => {
    try {
      const savedSacrificeMode = localStorage.getItem('sacrificeMode');
      if (savedSacrificeMode) {
        const parsed = JSON.parse(savedSacrificeMode);
        setIsSacrificeModeActive(parsed.isActive || false);
        if (parsed.data) {
          setSacrificeData(parsed.data);
        }
      }
    } catch (err) {
      console.error('Kurban modu verisi yüklenirken hata:', err);
    }
  }, []);

  // Modal açıkken body scroll'unu kilitle
  useEffect(() => {
    if (showUserForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showUserForm]);

  // Profil ayarları modal'ı açıldığında profil verilerini yükle
  useEffect(() => {
    if (showProfileForm && userData) {
      console.log('Profil modal açıldı, veriler yükleniyor...');
      setProfileFormData({
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        username: userData.username || '',
        role: userData.role || '',
        branch: userData.branch || ''
      });
      setProfileFormErrors({});
      setProfileSubmitError('');
      setProfileSubmitSuccess('');
    }
  }, [showProfileForm, userData]);



  // Ayarlar menüsü açıkken dışarı tıklanınca kapat
  useEffect(() => {
    if (!showSettingsDropdown && !showReportsDropdown && !showShareSaleDropdown) return;
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettingsDropdown(false);
        setShowReportsDropdown(false);
      }
      if (showShareSaleDropdown && !e.target.closest('.share-sale-dropdown')) {
        setShowShareSaleDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettingsDropdown, showReportsDropdown, showShareSaleDropdown]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setIsMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* Mobil hamburger: panel dışına dokununca menüyü kapat */
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handlePointerDown = (e) => {
      if (typeof window !== 'undefined' && window.innerWidth > 768) return;
      if (topButtonGroupRef.current?.contains(e.target)) return;
      closeMobileToolbarMenu();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isMobileMenuOpen, closeMobileToolbarMenu]);

  // Hisse satış formu açıldığında kullanıcı bilgilerini yükle
  useEffect(() => {
    if (showShareSaleForm && userData?.id) {
      const loadUserInfo = async () => {
        const userInfo = await fetchCurrentUserInfo();
        setCurrentUserInfo(userInfo);
      };
      loadUserInfo();
    }
  }, [showShareSaleForm, userData?.id]);

  // Hayvan seçildiğinde hisse başı fiyatı otomatik doldur
  useEffect(() => {
    if (selectedShareSaleAnimal && showShareSaleForm) {
      console.log('🔄 useEffect: Hayvan fiyatı çekiliyor...', selectedShareSaleAnimal.id);
      
      // Hayvanın satış bilgilerini çek ve fiyatı doldur
      const fetchAnimalPrice = async () => {
        try {
          console.log('🌐 API çağrısı yapılıyor:', API_ENDPOINTS.ANIMAL_SALES(selectedShareSaleAnimal.id));
          const response = await fetch(API_ENDPOINTS.ANIMAL_SALES(selectedShareSaleAnimal.id));
          console.log('📡 useEffect API Response Status:', response.status);
          
          if (response.ok) {
            // Response'un boş olup olmadığını kontrol et
            const responseText = await response.text();
            console.log('📄 useEffect Response text:', responseText);
            
            if (responseText && responseText.trim() !== '') {
              try {
                const salesData = JSON.parse(responseText);
                console.log('✅ useEffect Satış verisi:', salesData);
                
                if (salesData && salesData.price_per_share) {
                  console.log('💰 useEffect Fiyat bulundu:', salesData.price_per_share);
                  console.log('📊 Mevcut form fiyatı:', shareSaleFormData.pricePerShare);
                  
                  // Eğer fiyat henüz girilmemişse veritabanından gelen fiyatı set et
                  if (!shareSaleFormData.pricePerShare || shareSaleFormData.pricePerShare === '' || shareSaleFormData.pricePerShare === '0') {
                    const newFormData = {
                      ...shareSaleFormData,
                      pricePerShare: salesData.price_per_share.toString(),
                      totalAmount: (salesData.price_per_share * (shareSaleFormData.shareCount || 1)).toString()
                    };
                    console.log('🔄 useEffect Form güncelleniyor:', newFormData);
                    setShareSaleFormData(newFormData);
                  } else {
                    console.log('ℹ️ useEffect Fiyat zaten dolu, güncellenmiyor');
                  }
                } else {
                  console.log('❌ useEffect Fiyat bilgisi bulunamadı veya null');
                }
              } catch (parseError) {
                console.error('❌ useEffect JSON parse hatası:', parseError);
                console.log('ℹ️ useEffect Boş response, fiyat 0 olarak set ediliyor');
              }
            } else {
              console.log('ℹ️ useEffect Boş response, fiyat 0 olarak set ediliyor');
            }
          } else {
            console.log('❌ useEffect API Hatası:', response.status, response.statusText);
          }
        } catch (error) {
          console.error('❌ useEffect Hayvan fiyat bilgisi çekilemedi:', error);
        }
      };
      
      fetchAnimalPrice();
    }
  }, [selectedShareSaleAnimal, showShareSaleForm]);

  // Hisse sayısı veya fiyat değiştiğinde toplam tutarı güncelle
  useEffect(() => {
    if (shareSaleFormData.shareCount && shareSaleFormData.pricePerShare) {
      const total = parseFloat(shareSaleFormData.shareCount) * parseFloat(shareSaleFormData.pricePerShare);
      setShareSaleFormData(prev => ({
        ...prev,
        totalAmount: total.toString()
      }));
    }
  }, [shareSaleFormData.shareCount, shareSaleFormData.pricePerShare]);

  // useEffect'leri component fonksiyonunun en üstüne taşı
  // (örnek olarak animalWeights ile ilgili olanı):
  useEffect(() => {
    if (showAnimalViewModal && selectedAnimal) {
      setAnimalWeights([]);
      setAnimalWeightsLoading(true);
      setAnimalWeightsError('');
      animalService.getAnimalWeights(selectedAnimal.id)
        .then(data => setAnimalWeights(Array.isArray(data) ? data : []))
        .catch(err => setAnimalWeightsError(err.message || 'Tartım geçmişi yüklenemedi'))
        .finally(() => setAnimalWeightsLoading(false));
    }
  }, [showAnimalViewModal, selectedAnimal]);

  // Sayfa yüklendiğinde localStorage'dan userData'yı her zaman yükle
  useEffect(() => {
    if (!userData || !userData.id) {
      const savedLogin = localStorage.getItem('sultanlarAdmin');
      if (savedLogin) {
        const loginData = JSON.parse(savedLogin);
        if (loginData && typeof loginData === 'object' && loginData.id) {
          setUserData(loginData);
        }
      }
    }
  }, []);

  // Giriş yapılmamışsa giriş formunu göster
  if (!isLoggedIn) {
    return <LoginFormComponent onLogin={handleLogin} />;
  }

  const handleToggleSettings = () => {
    console.log('Ayarlar butonuna tıklandı!');
    setShowSettingsDropdown(prev => {
      console.log('Önceki değer:', prev);
      const newValue = !prev;
      console.log('Yeni değer:', newValue);
      return newValue;
    });
  };

  const handleProfileSettings = () => {
    if (userData) {
      setProfileFormData({
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        username: userData.username || '',
        role: userData.role || '',
        branch: userData.branch || ''
      });
      setProfileFormErrors({});
      setProfileSubmitError('');
      setProfileSubmitSuccess('');
      setShowProfileForm(true);
    }
  };

  const handleUserFormSubmit = async (e) => {
    e.preventDefault();
    
    setSubmitError('');
    setSubmitSuccess('');
    
    // Form validasyonu
    const errors = {};
    
    if (!formData.firstName.trim()) errors.firstName = 'Ad alanı zorunludur';
    if (!formData.lastName.trim()) errors.lastName = 'Soyad alanı zorunludur';
    if (!formData.email.trim()) errors.email = 'E-posta alanı zorunludur';
    if (!formData.username.trim()) errors.username = 'Kullanıcı adı zorunludur';
    if (!formData.password) errors.password = 'Şifre alanı zorunludur';
    if (!formData.confirmPassword) errors.confirmPassword = 'Şifre tekrar alanı zorunludur';
    if (!formData.role) errors.role = 'Rol seçimi zorunludur';
    if (!formData.branch) errors.branch = 'Şube seçimi zorunludur';
    
    // E-posta format kontrolü
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = 'Geçerli bir e-posta adresi girin';
    }
    
    // Şifre uzunluk kontrolü
    if (formData.password && formData.password.length < 8) {
      errors.password = 'Şifre en az 8 karakter olmalıdır';
    }
    
    // Şifre karmaşıklık kontrolü
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)/;
    if (formData.password && !passwordRegex.test(formData.password)) {
      errors.password = 'Şifre en az 1 harf ve 1 rakam içermelidir';
    }
    
    // Şifre eşleşme kontrolü
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Şifreler eşleşmiyor';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    try {
      setSubmitLoading(true);
      const saved = await userService.createUser({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        username: formData.username.trim(),
        password: formData.password,
        role: formData.role,
        branch: formData.branch
      });
      
      setSubmitSuccess('Kullanıcı başarıyla kaydedildi (ID: ' + (saved.id || 'N/A') + ')');
      // Formu temizle
      setFormData({
        firstName: '', lastName: '', email: '', phone: '', username: '', password: '', confirmPassword: '', role: '', branch: ''
      });
      setFormErrors({});
      // Modalı kısa bir süre sonra kapat
      setTimeout(() => {
        setShowUserForm(false);
        setSubmitSuccess('');
      }, 1200);
    } catch (err) {
      setSubmitError(err?.message || (typeof err === 'string' ? err : JSON.stringify(err)) || 'Kayıt sırasında bir hata oluştu');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleFormReset = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      username: '',
      password: '',
      confirmPassword: '',
      role: '',
      branch: ''
    });
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Hata mesajını temizle
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAnimalFormSubmit = async (e) => {
    e.preventDefault();
    
    setAnimalSubmitError('');
    setAnimalSubmitSuccess('');
    
    // Form validasyonu
    const errors = {};
    
    if (!animalFormData.type) errors.type = 'Hayvan türü seçimi zorunludur';
    if (!animalFormData.breed.trim()) errors.breed = 'Irk alanı zorunludur';
    if (!animalFormData.gender) errors.gender = 'Cinsiyet seçimi zorunludur';
    if (!animalFormData.earTag.trim()) errors.earTag = 'Küpe/Hayvan No alanı zorunludur';
    if (!animalFormData.entryDate) errors.entryDate = 'Giriş tarihi zorunludur';
    if (!animalFormData.startWeight) errors.startWeight = 'Başlangıç kilosu zorunludur';
    if (!animalFormData.supplier.trim()) errors.supplier = 'Alındığı kişi/firma alanı zorunludur';
    
    // İsim formatını otomatik düzelt
    const formatSupplierName = (name) => {
      return name.trim()
        .split(' ')
        .map((word, index) => {
          if (index === 0) {
            // İlk kelime: Sadece ilk harf büyük
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          } else {
            // Diğer kelimeler: Tamamen büyük harf
            return word.toUpperCase();
          }
        })
        .join(' ');
    };
    
    // Supplier adını formatla
    const formattedSupplier = formatSupplierName(animalFormData.supplier);
    const parseMoneyStr = (s) => parseFloat(String(s || '').replace(/[^0-9.,]/g, '').replace(',', '.'));
    if (animalFormData.purchasePrice && isNaN(parseMoneyStr(animalFormData.purchasePrice))) {
      errors.purchasePrice = 'Alış fiyatı geçerli bir sayı olmalı';
    }
    if (animalFormData.purchasePricePerKg && isNaN(parseMoneyStr(animalFormData.purchasePricePerKg))) {
      errors.purchasePricePerKg = 'Kilo fiyatı geçerli bir sayı olmalı';
    }
    
    // Kilo kontrolü
    const weight = parseFloat(animalFormData.startWeight);
    if (isNaN(weight) || weight < 1 || weight > 2000) {
      errors.startWeight = 'Başlangıç kilosu 1-2000 kg arası olmalıdır';
    }
    
    // Doğum tarihi kontrolü (opsiyonel)
    if (animalFormData.birthDate) {
      const birthDate = new Date(animalFormData.birthDate);
      const entryDate = new Date(animalFormData.entryDate);
      if (birthDate > entryDate) {
        errors.birthDate = 'Doğum tarihi giriş tarihinden sonra olamaz';
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setAnimalFormErrors(errors);
      return;
    }
    
    try {
      setAnimalSubmitLoading(true);
      
      const formData = new FormData();
      formData.append('type', animalFormData.type);
      formData.append('breed', animalFormData.breed.trim());
      formData.append('gender', animalFormData.gender);
      formData.append('earTag', animalFormData.earTag.trim());
      formData.append('entryDate', animalFormData.entryDate);
      formData.append('startWeight', animalFormData.startWeight);
      formData.append('supplier', formattedSupplier);
      
      if (animalFormData.birthDate) {
        formData.append('birthDate', animalFormData.birthDate);
      }
      if (animalFormData.notes.trim()) {
        formData.append('notes', animalFormData.notes.trim());
      }
      if (selectedImage) {
        formData.append('image', selectedImage);
      }
      if (animalFormData.purchasePrice) {
        formData.append('purchasePrice', animalFormData.purchasePrice.replace(/[^0-9.,]/g, '').replace(',', '.'));
      }
      if (animalFormData.purchasePricePerKg) {
        formData.append('purchasePricePerKg', animalFormData.purchasePricePerKg.replace(/[^0-9.,]/g, '').replace(',', '.'));
      }
      if (currentUser?.institution) {
        formData.append('branch', currentUser.institution);
      }
      if (currentUser?.id) {
        formData.append('personel_id', String(currentUser.id));
      }
      
      // Debug için form verilerini logla
      console.log('Form data being sent:');
      for (let [key, value] of formData.entries()) {
        console.log(key, ':', value);
      }
      
      const response = await fetch(API_ENDPOINTS.ANIMALS, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        console.log('Backend error details:', errData);
        throw new Error(errData.error || 'Hayvan eklenemedi');
      }

      const saved = await response.json();
      setAnimalSubmitSuccess('Hayvan başarıyla eklendi (ID: ' + saved.id + ')');
      
      // Hayvanlar listesini yenile
      await refreshAnimals();
      
      // Formu temizle
      setAnimalFormData({
        type: '', breed: '', gender: '', earTag: '', birthDate: '', entryDate: '', startWeight: '', notes: '', supplier: '', purchasePrice: '', purchasePricePerKg: ''
      });
      setAnimalFormErrors({});
      setSelectedImage(null);
      setImagePreview(null);
      
      // Modalı kısa bir süre sonra kapat
      setTimeout(() => {
        setShowAnimalForm(false);
        setAnimalSubmitSuccess('');
        refreshAnimals(); // Listeyi yenile
      }, 1500);
    } catch (err) {
      setAnimalSubmitError(err?.message || (typeof err === 'string' ? err : JSON.stringify(err)) || 'Kayıt sırasında bir hata oluştu');
    } finally {
      setAnimalSubmitLoading(false);
    }
  };

  const handleAnimalFormReset = () => {
    setAnimalFormData({
      type: '', breed: '', gender: '', earTag: '', birthDate: '', entryDate: '', startWeight: '', notes: '', supplier: '', purchasePrice: '', purchasePricePerKg: ''
    });
    setAnimalFormErrors({});
    setSelectedImage(null);
    setImagePreview(null);
  };

  const parseAnimalMoney = (s) => {
    const n = parseFloat(String(s ?? '').replace(/[^0-9.,]/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  };

  const formatAnimalMoneyForInput = (n) => {
    if (!Number.isFinite(n)) return '';
    return n.toFixed(2).replace('.', ',');
  };

  const handleAnimalPriceSync = (name, rawValue) => {
    const sanitizeMoney = (v) => String(v).replace(/[^0-9.,]/g, '');
    const value = name === 'startWeight' ? rawValue : sanitizeMoney(rawValue);

    setAnimalFormData((prev) => {
      const next = { ...prev, [name]: value };
      const w = parseFloat(String(next.startWeight).replace(',', '.'));
      const total = parseAnimalMoney(next.purchasePrice);
      const pkg = parseAnimalMoney(next.purchasePricePerKg);

      if (name === 'purchasePrice') {
        if (Number.isFinite(w) && w > 0 && Number.isFinite(total) && total >= 0) {
          next.purchasePricePerKg = formatAnimalMoneyForInput(total / w);
        }
      } else if (name === 'purchasePricePerKg') {
        if (Number.isFinite(w) && w > 0 && Number.isFinite(pkg) && pkg >= 0) {
          next.purchasePrice = formatAnimalMoneyForInput(pkg * w);
        }
      } else if (name === 'startWeight') {
        if (Number.isFinite(w) && w > 0) {
          if (Number.isFinite(pkg) && pkg >= 0) {
            next.purchasePrice = formatAnimalMoneyForInput(pkg * w);
          } else if (Number.isFinite(total) && total >= 0) {
            next.purchasePricePerKg = formatAnimalMoneyForInput(total / w);
          }
        }
      }
      return next;
    });

    if (animalFormErrors[name]) {
      setAnimalFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleAnimalInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startWeight' || name === 'purchasePrice' || name === 'purchasePricePerKg') {
      handleAnimalPriceSync(name, value);
      return;
    }
    setAnimalFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Hata mesajını temizle
    if (animalFormErrors[name]) {
      setAnimalFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAnimalInputBlur = (e) => {
    const { name, value } = e.target;
    
    // Alındığı Kişi/Firma alanı için otomatik format düzeltme
    if (name === 'supplier' && value.trim()) {
      const words = value.trim().split(' ');
      
      if (words.length >= 2) {
        // İlk kelime (ad): İlk harf büyük, diğerleri küçük
        const firstName = words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();
        // Son kelime (soyad): Tümü büyük harf
        const lastName = words[words.length - 1].toUpperCase();
        // Ortadaki kelimeler (varsa): İlk harf büyük, diğerleri küçük
        const middleNames = words.slice(1, -1).map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        );
        
        const formattedValue = [firstName, ...middleNames, lastName].join(' ');
        setAnimalFormData(prev => ({
          ...prev,
          [name]: formattedValue
        }));
      } else if (words.length === 1) {
        // Tek kelime: İlk harf büyük, diğerleri küçük
        const formattedValue = words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();
        setAnimalFormData(prev => ({
          ...prev,
          [name]: formattedValue
        }));
      }
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Dosya boyutu kontrolü (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setAnimalSubmitError('Dosya boyutu 5MB\'dan büyük olamaz');
        return;
      }
      
      // Dosya tipi kontrolü
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setAnimalSubmitError('Sadece JPG, PNG ve WEBP formatları desteklenir');
        return;
      }
      
      setSelectedImage(file);
      setAnimalSubmitError('');
      
      // Önizleme oluştur
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Yeni Kilo Ekle modalı form submit fonksiyonu
  const handleWeightSubmit = async (e) => {
    e.preventDefault();
    setWeightError('');
    setWeightSuccess('');
    if (!weightDate || !weightValue) {
      setWeightError('Tarih ve kilo zorunludur');
      return;
    }
    const weightNum = parseFloat(weightValue);
    if (isNaN(weightNum) || weightNum < 1 || weightNum > 2000) {
      setWeightError('Kilo 1-2000 kg arası olmalı');
      return;
    }
    try {
      const response = await fetch(API_ENDPOINTS.ANIMAL_WEIGHTS(selectedWeightAnimal.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: weightDate, weight: weightNum })
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Tartım eklenemedi');
      }
      setWeightSuccess('Tartım başarıyla eklendi');
      setWeightValue('');
      setWeightDate(new Date().toISOString().slice(0, 10));
      
      // Hayvanlar listesini yenile
      await refreshAnimals();
      
      setTimeout(() => {
        setShowWeightModal(false);
        setWeightSuccess('');
      }, 1200);
    } catch (err) {
      setWeightError(err.message || 'Bir hata oluştu');
    }
  };

  const handleDownloadReport = async () => {
    // Tüm hayvanları al
    const response = await fetch(API_ENDPOINTS.ANIMALS);
    const animalsData = await response.json();

    // Her hayvan için tartım geçmişini çekip, son tartım kilosu ve kilo farkını hesapla
    const data = await Promise.all(animalsData.map(async (animal) => {
      let lastWeight = animal.start_weight;
      let kiloFarki = 0;
      try {
        const res = await fetch(API_ENDPOINTS.ANIMAL_WEIGHTS(animal.id));
        if (res.ok) {
          const weights = await res.json();
          if (weights.length > 0) {
            lastWeight = weights[weights.length - 1].weight;
            kiloFarki = lastWeight - animal.start_weight;
          }
        }
      } catch {}
      return {
        'ID': `#${animal.id}`,
        'Tür': animal.type,
        'Irk': animal.breed,
        'Cins': animal.gender,
        'Küpe/Hayvan No': animal.ear_tag,
        'Doğum Tarihi': animal.birth_date ? new Date(animal.birth_date).toLocaleDateString('tr-TR') : '',
        'Giriş Tarihi': animal.entry_date ? new Date(animal.entry_date).toLocaleDateString('tr-TR') : '',
        'Durumu': animal.status === 'active' ? 'Aktif' : (animal.status === 'Satışta' ? 'Satışta' : 'Pasif'),
        'Giriş Kilosu': animal.start_weight,
        'Son Tartım Kilosu': lastWeight,
        'Kilo Farkı': kiloFarki > 0 ? `+${kiloFarki}` : `${kiloFarki}`,
        'Alındığı Kişi/Firma': animal.supplier,
        'Notlar': animal.notes || ''
      };
    }));

    // Excel dosyası oluştur
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hayvanlar');
    XLSX.writeFile(wb, 'hayvan_raporu.xlsx');
  };



  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileFormReset = () => {
    if (userData) {
      setProfileFormData({
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        username: userData.username || '',
        role: userData.role || '',
        branch: userData.branch || ''
      });
      setProfileFormErrors({});
      setProfileSubmitError('');
      setProfileSubmitSuccess('');
    }
  };

  const handleProfileFormSubmit = async (e) => {
    e.preventDefault();
    setProfileSubmitLoading(true);
    setProfileSubmitError('');
    setProfileSubmitSuccess('');
    // Validasyon örneği
    if (!profileFormData.firstName || !profileFormData.lastName || !profileFormData.email || !profileFormData.username) {
      setProfileFormErrors({ firstName: !profileFormData.firstName, lastName: !profileFormData.lastName, email: !profileFormData.email, username: !profileFormData.username });
      setProfileSubmitLoading(false);
      return;
    }
    // Backend'e güncelleme isteği gönderilebilir (örnek: /api/users/me PUT)
    setTimeout(() => {
      setProfileSubmitSuccess('Profil başarıyla güncellendi!');
      setProfileSubmitLoading(false);
    }, 1000);
  };

  const handleOpenChangePassword = () => {
    setShowChangePasswordModal(true);
    setChangePasswordStep(1);
    setCurrentPassword('');
    setNewPassword('');
    setNewPasswordRepeat('');
    setChangePasswordError('');
    setChangePasswordSuccess('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePasswordError('');
    setChangePasswordSuccess('');
    if (changePasswordStep === 1) {
      // Mevcut şifreyi backend'de kontrol et
      try {
        const res = await fetch(API_ENDPOINTS.CHECK_PASSWORD, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: userData.username, password: currentPassword })
        });
        if (!res.ok) {
          setChangePasswordError('Mevcut şifre yanlış!');
          return;
        }
        setChangePasswordStep(2);
        return;
      } catch (err) {
        setChangePasswordError('Sunucu hatası!');
        return;
      }
    }
    // Yeni şifre validasyonları
    if (!newPassword || !newPasswordRepeat) {
      setChangePasswordError('Yeni şifre alanları boş olamaz!');
      return;
    }
    if (newPassword !== newPasswordRepeat) {
      setChangePasswordError('Yeni şifreler eşleşmiyor!');
      return;
    }
    if (newPassword === currentPassword) {
      setChangePasswordError('Yeni şifre, mevcut şifreyle aynı olamaz!');
      return;
    }
    // Gerçek backend isteği
    try {
      const res = await fetch(API_ENDPOINTS.CHANGE_PASSWORD(userData.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldPassword: currentPassword, newPassword })
      });
      if (!res.ok) {
        const err = await res.json();
        setChangePasswordError(err.message || 'Şifre değiştirilemedi!');
        return;
      }
      setChangePasswordSuccess('Şifre başarıyla değiştirildi!');
      setTimeout(() => {
        setShowChangePasswordModal(false);
        setChangePasswordSuccess('');
      }, 1200);
    } catch (err) {
      setChangePasswordError('Sunucu hatası!');
    }
  };

  // Kullanıcı satırına tıklama
  const handleUserRowClick = (id) => {
    setSelectedUserId(id === selectedUserId ? null : id);
  };
  // Düzenle butonu
  const handleEditUser = (user) => {
    setEditUserData(user);
    setShowEditUserModal(true);
  };
  // Sil butonu
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`${user.first_name} ${user.last_name} kullanıcısını silmek istediğinize emin misiniz?`)) return;
    try {
      await userService.deleteUser(user.id);
      fetchUsers();
      setSelectedUserId(null);
    } catch (err) {
      alert('Kullanıcı silinemedi!');
    }
  };
  // Kullanıcı güncelleme submit
  const handleEditUserSubmit = async (e) => {
    e.preventDefault();
    setEditUserLoading(true);
    setEditUserError('');
    setEditUserSuccess('');
    try {
      const res = await fetch(API_ENDPOINTS.USER_BY_ID(editUserData.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: editUserData.first_name,
          lastName: editUserData.last_name,
          email: editUserData.email,
          phone: editUserData.phone,
          username: editUserData.username,
          role: editUserData.role,
          branch: editUserData.branch
        })
      });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      setEditUserSuccess('Kullanıcı başarıyla güncellendi');
      setShowEditUserModal(false);
      fetchUsers();
    } catch (err) {
      setEditUserError('Kullanıcı güncellenemedi');
    }
    setEditUserLoading(false);
  };

  // Ad alanı için fonksiyonlar
  const formatName = (value) => {
    return value
      .replace(/[^a-zA-ZğüşöçıİĞÜŞÖÇ\s]/gi, '') // Sadece harf ve boşluk
      .replace(/\s+/g, ' ') // Çift boşlukları teke indir
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Soyad alanı için fonksiyon
  const formatSurname = (value) => {
    return value
      .replace(/[^a-zA-ZğüşöçıİĞÜŞÖÇ]/gi, '') // Sadece harf
      .toUpperCase();
  };

  // Telefon alanı için fonksiyon
  const formatPhone = (value) => {
    return value.replace(/[^0-9]/g, '').slice(0, 11);
  };

  // Satışa Ekle Modal'ın hemen başına ekle:
      const onsaleAnimals = animals.filter(animal => animal.status === 'Satışta' || animal.status === 'onsale');

  // Satışta Olan Hayvanlar Modalı:
  const uniqueOnsaleBreeds = Array.from(new Set(onsaleAnimals.map(a => a.breed).filter(Boolean)));
  const uniqueOnsaleTypes = Array.from(new Set(onsaleAnimals.map(a => a.type).filter(Boolean)));
  const uniqueOnsaleGenders = Array.from(new Set(onsaleAnimals.map(a => a.gender).filter(Boolean)));
  
  // Basitleştirilmiş filtreleme - sadece arama terimi kontrolü
  const filteredOnsaleAnimals = onsaleAnimals.filter(animal => {
    // Arama terimi kontrolü
    if (shareSaleSearch && shareSaleSearch.trim() !== '') {
      const searchTerm = shareSaleSearch.toLowerCase();
      const searchFields = [
        animal.id?.toString() || '',
        animal.breed || '',
        animal.ear_tag || '',
        animal.type || '',
        animal.gender || ''
      ].join(' ').toLowerCase();
      
      if (!searchFields.includes(searchTerm)) {
        return false;
      }
    }
    
    // Tür filtresi
    if (shareSaleType !== 'all' && animal.type !== shareSaleType) {
      return false;
    }
    
    // Irk filtresi
    if (shareSaleBreed !== 'all' && animal.breed !== shareSaleBreed) {
      return false;
    }
    
    // Cins filtresi
    if (shareSaleGender !== 'all' && animal.gender !== shareSaleGender) {
      return false;
    }
    
    return true;
  });

  // Debug log (geliştirme için)

  return (
    <AdminContainer>
      <TopButtonGroup
        ref={topButtonGroupRef}
        $open={isMobileMenuOpen}
        onClick={(e) => {
          if (typeof window !== 'undefined' && window.innerWidth > 768) return;
          if (!isMobileMenuOpen) return;
          if (e.target.closest('.mobile-menu-toggle')) return;
          /* Ayarlar / Raporlar ilk dokunuşta alt menü açılsın; menü hemen kapanmasın */
          if (e.target.closest('[data-mobile-submenu-trigger="true"]')) return;
          closeMobileToolbarMenu();
        }}
      >
        <button
          type="button"
          className="mobile-menu-toggle"
          onClick={() => {
            setIsMobileMenuOpen((prev) => {
              if (prev) {
                setShowSettingsDropdown(false);
                setShowReportsDropdown(false);
                return false;
              }
              return true;
            });
          }}
          aria-label="Panel menüsünü aç/kapat"
        >
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          Menü
        </button>
        <LogoutButton style={{ position: 'static' }} onClick={() => setShowShareSaleListModal(true)}>
          <FaChartPie />
          Hisse Satışı
        </LogoutButton>
        
        <LogoutButton style={{ position: 'static' }} onClick={() => {
          setShowHayvanlarModal(true);
          fetchHayvanlarList();
        }}>
          <FaPaw />
          Hayvanlar
        </LogoutButton>
        
        <SettingsButton 
          data-mobile-submenu-trigger="true"
          onClick={handleToggleSettings}
          ref={settingsRef}
        >
          <FaCog />
          Ayarlar
        </SettingsButton>
        
        {/* Ayarlar dropdown'ı */}
        {showSettingsDropdown && (
          <SettingsDropdown>
            <button 
              type="button"
              style={{
                width: '100%',
                padding: '12px 20px',
                background: 'white',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'var(--secondary-color)',
                borderBottom: '1px solid var(--light-gray)',
                fontSize: '16px',
                fontWeight: 'normal'
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Yetki kontrolü - Sadece Yönetici erişebilir
                if (currentUser.role !== 'Yönetici' && currentUser.role !== 'admin') {
                  alert('⚠️ Bu özelliği kullanmak için yetkiniz yok.\n\nSadece Sistem Yöneticisi erişebilir.');
                  closeMobileToolbarMenu();
                  return;
                }
                
                console.log('🚨🚨🚨 Kullanıcı Ayarları onMouseDown tetiklendi!');
                setShowUserListModal(true);
                closeMobileToolbarMenu();
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Yetki kontrolü - Sadece Yönetici erişebilir
                if (currentUser.role !== 'Yönetici' && currentUser.role !== 'admin') {
                  alert('⚠️ Bu özelliği kullanmak için yetkiniz yok.\n\nSadece Sistem Yöneticisi erişebilir.');
                  closeMobileToolbarMenu();
                  return;
                }
                
                console.log('🚨🚨🚨 Kullanıcı Ayarları onTouchStart tetiklendi!');
                setShowUserListModal(true);
                closeMobileToolbarMenu();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Yetki kontrolü - Sadece Yönetici erişebilir
                if (currentUser.role !== 'Yönetici' && currentUser.role !== 'admin') {
                  alert('⚠️ Bu özelliği kullanmak için yetkiniz yok.\n\nSadece Sistem Yöneticisi erişebilir.');
                  closeMobileToolbarMenu();
                  return;
                }
                
                setShowUserListModal(true);
                closeMobileToolbarMenu();
              }}
            >
              <FaUsers />
              Kullanıcı Ayarları
            </button>
            <button 
              type="button"
              style={{
                width: '100%',
                padding: '12px 20px',
                background: 'white',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'var(--secondary-color)',
                fontSize: '16px',
                fontWeight: 'normal'
              }}
              onMouseDown={() => {
                console.log('🚨🚨🚨 Profil Ayarları onMouseDown tetiklendi!');
                setShowProfileForm(true);
                closeMobileToolbarMenu();
              }}
              onTouchStart={() => {
                console.log('🚨🚨🚨 Profil Ayarları onTouchStart tetiklendi!');
                setShowProfileForm(true);
                closeMobileToolbarMenu();
              }}
            >
              <FaUserCog />
              Profil Ayarları
            </button>
            <button 
              type="button"
              style={{
                width: '100%',
                padding: '12px 20px',
                background: 'white',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'var(--secondary-color)',
                borderBottom: '1px solid var(--light-gray)',
                fontSize: '16px',
                fontWeight: 'normal'
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (currentUser.role !== 'Yönetici' && currentUser.role !== 'admin') {
                  alert('⚠️ Bu özelliği kullanmak için yetkiniz yok.\n\nSadece Sistem Yöneticisi erişebilir.');
                  closeMobileToolbarMenu();
                  return;
                }
                setSystemSettingsMessage('');
                setShowSystemSettingsModal(true);
                closeMobileToolbarMenu();
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (currentUser.role !== 'Yönetici' && currentUser.role !== 'admin') {
                  alert('⚠️ Bu özelliği kullanmak için yetkiniz yok.\n\nSadece Sistem Yöneticisi erişebilir.');
                  closeMobileToolbarMenu();
                  return;
                }
                setSystemSettingsMessage('');
                setShowSystemSettingsModal(true);
                closeMobileToolbarMenu();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (currentUser.role !== 'Yönetici' && currentUser.role !== 'admin') {
                  alert('⚠️ Bu özelliği kullanmak için yetkiniz yok.\n\nSadece Sistem Yöneticisi erişebilir.');
                  closeMobileToolbarMenu();
                  return;
                }
                setSystemSettingsMessage('');
                setShowSystemSettingsModal(true);
                closeMobileToolbarMenu();
              }}
            >
              <FaDatabase />
              Sistem ayarları
            </button>
            <button 
              type="button"
              style={{
                width: '100%',
                padding: '12px 20px',
                background: 'white',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'var(--secondary-color)',
                fontSize: '16px',
                fontWeight: 'normal'
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Yetki kontrolü - Sadece Yönetici erişebilir
                if (currentUser.role !== 'Yönetici' && currentUser.role !== 'admin') {
                  alert('⚠️ Bu özelliği kullanmak için yetkiniz yok.\n\nSadece Sistem Yöneticisi erişebilir.');
                  closeMobileToolbarMenu();
                  return;
                }
                
                console.log('🚨🚨🚨 Kurban Modu onMouseDown tetiklendi!');
                setShowSacrificeModal(true);
                closeMobileToolbarMenu();
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Yetki kontrolü - Sadece Yönetici erişebilir
                if (currentUser.role !== 'Yönetici' && currentUser.role !== 'admin') {
                  alert('⚠️ Bu özelliği kullanmak için yetkiniz yok.\n\nSadece Sistem Yöneticisi erişebilir.');
                  closeMobileToolbarMenu();
                  return;
                }
                
                console.log('🚨🚨🚨 Kurban Modu onTouchStart tetiklendi!');
                setShowSacrificeModal(true);
                closeMobileToolbarMenu();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Yetki kontrolü - Sadece Yönetici erişebilir
                if (currentUser.role !== 'Yönetici' && currentUser.role !== 'admin') {
                  alert('⚠️ Bu özelliği kullanmak için yetkiniz yok.\n\nSadece Sistem Yöneticisi erişebilir.');
                  closeMobileToolbarMenu();
                  return;
                }
                
                setShowSacrificeModal(true);
                closeMobileToolbarMenu();
              }}
            >
              <FaCoins />
              Kurban Modu
            </button>
          </SettingsDropdown>
        )}
        
        <div style={{ position: 'relative' }}>
          <LogoutButton 
            data-mobile-submenu-trigger="true"
            style={{ position: 'static' }} 
            onClick={() => {
              setShowReportsDropdown(!showReportsDropdown);
              setShowSettingsDropdown(false); // Diğer dropdown'ı kapat
            }}
          >
            <FaFileAlt />
            Raporlar
          </LogoutButton>
          
          {/* Raporlar dropdown'ı */}
          {showReportsDropdown && (
            <SettingsDropdown>
            <button 
              type="button"
              style={{
                width: '100%',
                padding: '12px 20px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'var(--secondary-color)',
                borderBottom: '1px solid var(--light-gray)'
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleKasaRaporlariOpen();
                closeMobileToolbarMenu();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleKasaRaporlariOpen();
                closeMobileToolbarMenu();
              }}
            >
              <FaChartBar />
              Kasa Raporları
            </button>
            <button 
              type="button"
              style={{
                width: '100%',
                padding: '12px 20px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'var(--secondary-color)',
                borderBottom: '1px solid var(--light-gray)'
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Kurum Raporları butonu onClick tetiklendi');
                handleKurumRaporlariOpen();
                closeMobileToolbarMenu();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Kurum Raporları butonu onMouseDown tetiklendi');
                handleKurumRaporlariOpen();
                closeMobileToolbarMenu();
              }}
            >
              <FaUsers />
              Kurum Raporları
            </button>
            <button 
              type="button"
              style={{
                width: '100%',
                padding: '12px 20px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'var(--secondary-color)'
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Personel Raporları butonu onClick tetiklendi');
                handlePersonelRaporlariOpen();
                closeMobileToolbarMenu();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Personel Raporları butonu onMouseDown tetiklendi');
                handlePersonelRaporlariOpen();
                closeMobileToolbarMenu();
              }}
            >
              <FaUserCircle />
              Personel Raporları
            </button>
            </SettingsDropdown>
          )}
        </div>
        
        <LogoutButton 
          style={{ 
            position: 'static'
          }} 
          onClick={() => {
            console.log('Tahsilatlar butonu tıklandı!');
            setShowTahsilatlarModal(true);
            fetchTahsilatlarData();
          }}
        >
          <FaMoneyBillWave />
          Tahsilatlar
        </LogoutButton>

        <LogoutButton
          style={{
            position: 'static'
          }}
          onClick={() => setShowContentPlannerModal(true)}
        >
          <FaImage />
          İçerik Planlayıcı
        </LogoutButton>
        
        <LogoutButton 
          style={{ 
            position: 'static'
          }} 
          onClick={() => {
            handleGiderEkleOpen();
          }}
        >
          <FaReceipt />
          Gider Ekle
        </LogoutButton>
        
        <LogoutButton style={{ position: 'static' }} onClick={handleLogout}>
          <FaSignOutAlt />
          Çıkış Yap
        </LogoutButton>
      </TopButtonGroup>
      
      <AdminHeader>
        <Container>
          <AdminTitle>🔐 Gizli Admin Paneli</AdminTitle>
          <AdminSubtitle>SULTANLAR BESİCİLİK - Personel Yönetim Sistemi</AdminSubtitle>
          {userData && (
            <div style={{ 
              textAlign: 'center', 
              marginTop: '10px', 
              color: 'var(--white-color)', 
              opacity: 0.9 
            }}>
              Hoş geldiniz, <strong>{userData.username}</strong>! 
              {userData.rememberMe && ' (Oturum hatırlanıyor)'}
            </div>
          )}
        </Container>
      </AdminHeader>

      <Container>
        {/* İstatistikler - Kurban Modu Aktif Değilse Göster */}
        {!isSacrificeModeActive ? (
          <DashboardGrid>
            <StatCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <StatIcon>
                <FaChartBar />
              </StatIcon>
              <StatNumber>{stats.topBranch}</StatNumber>
              <StatLabel>En Çok Satış Yapan Kurum</StatLabel>
            </StatCard>

            <StatCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <StatIcon>
                <FaPaw />
              </StatIcon>
              <StatNumber>{stats.smallAnimals}</StatNumber>
              <StatLabel>Küçükbaş Hayvan</StatLabel>
            </StatCard>

            <StatCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <StatIcon>
                <FaDrumstickBite />
              </StatIcon>
              <StatNumber>{stats.largeAnimals}</StatNumber>
              <StatLabel>Büyükbaş Hayvan</StatLabel>
            </StatCard>

            <StatCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <StatIcon>
                <FaUserCog />
              </StatIcon>
              <StatNumber>{stats.topSalesperson}</StatNumber>
              <StatLabel>En Çok Satış Yapan Personel</StatLabel>
            </StatCard>
          </DashboardGrid>
        ) : (
          /* Kurban Modu Aktif - Düzenlenebilir Sıra Numaraları */
          <DashboardGrid>
            {/* Kesim Sırası */}
            <StatCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <StatIcon>
                🔪
              </StatIcon>
              <StatNumber>
                <input
                  type="number"
                  value={sacrificeData.kesimSira || ''}
                  onChange={(e) => setSacrificeData(prev => ({ ...prev, kesimSira: e.target.value }))}
                  placeholder="0"
                  style={{
                    width: '80px',
                    padding: '8px',
                    border: '2px solid var(--primary-color)',
                    borderRadius: '5px',
                    fontSize: '2rem',
                    fontWeight: '700',
                    textAlign: 'center',
                    color: 'var(--primary-color)',
                    background: 'transparent'
                  }}
                />
              </StatNumber>
              <StatLabel>Kesim Sırası</StatLabel>
              <button
                onClick={() => {
                  const updatedData = { ...sacrificeData };
                  localStorage.setItem('sacrificeMode', JSON.stringify({
                    isActive: true,
                    data: updatedData
                  }));
                  alert('✅ Kesim Sırası güncellendi!');
                }}
                style={{
                  marginTop: '15px',
                  padding: '8px 20px',
                  background: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'var(--transition)'
                }}
                onMouseOver={(e) => e.target.style.background = '#e67e00'}
                onMouseOut={(e) => e.target.style.background = 'var(--primary-color)'}
              >
                Güncelle
              </button>
            </StatCard>

            {/* Teslim Sırası 1. Masa */}
            <StatCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <StatIcon>
                🍽️
              </StatIcon>
              <StatNumber>
                <input
                  type="number"
                  value={sacrificeData.teslimSira1 || ''}
                  onChange={(e) => setSacrificeData(prev => ({ ...prev, teslimSira1: e.target.value }))}
                  placeholder="0"
                  style={{
                    width: '80px',
                    padding: '8px',
                    border: '2px solid var(--primary-color)',
                    borderRadius: '5px',
                    fontSize: '2rem',
                    fontWeight: '700',
                    textAlign: 'center',
                    color: 'var(--primary-color)',
                    background: 'transparent'
                  }}
                />
              </StatNumber>
              <StatLabel>Teslim Sırası 1. Masa</StatLabel>
              <button
                onClick={() => {
                  const updatedData = { ...sacrificeData };
                  localStorage.setItem('sacrificeMode', JSON.stringify({
                    isActive: true,
                    data: updatedData
                  }));
                  alert('✅ Teslim Sırası 1. Masa güncellendi!');
                }}
                style={{
                  marginTop: '15px',
                  padding: '8px 20px',
                  background: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'var(--transition)'
                }}
                onMouseOver={(e) => e.target.style.background = '#e67e00'}
                onMouseOut={(e) => e.target.style.background = 'var(--primary-color)'}
              >
                Güncelle
              </button>
            </StatCard>

            {/* Teslim Sırası 2. Masa */}
            <StatCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <StatIcon>
                🍽️
              </StatIcon>
              <StatNumber>
                <input
                  type="number"
                  value={sacrificeData.teslimSira2 || ''}
                  onChange={(e) => setSacrificeData(prev => ({ ...prev, teslimSira2: e.target.value }))}
                  placeholder="0"
                  style={{
                    width: '80px',
                    padding: '8px',
                    border: '2px solid var(--primary-color)',
                    borderRadius: '5px',
                    fontSize: '2rem',
                    fontWeight: '700',
                    textAlign: 'center',
                    color: 'var(--primary-color)',
                    background: 'transparent'
                  }}
                />
              </StatNumber>
              <StatLabel>Teslim Sırası 2. Masa</StatLabel>
              <button
                onClick={() => {
                  const updatedData = { ...sacrificeData };
                  localStorage.setItem('sacrificeMode', JSON.stringify({
                    isActive: true,
                    data: updatedData
                  }));
                  alert('✅ Teslim Sırası 2. Masa güncellendi!');
                }}
                style={{
                  marginTop: '15px',
                  padding: '8px 20px',
                  background: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'var(--transition)'
                }}
                onMouseOver={(e) => e.target.style.background = '#e67e00'}
                onMouseOut={(e) => e.target.style.background = 'var(--primary-color)'}
              >
                Güncelle
              </button>
            </StatCard>

            {/* Teslim Sırası 3. Masa */}
            <StatCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <StatIcon>
                🍽️
              </StatIcon>
              <StatNumber>
                <input
                  type="number"
                  value={sacrificeData.teslimSira3 || ''}
                  onChange={(e) => setSacrificeData(prev => ({ ...prev, teslimSira3: e.target.value }))}
                  placeholder="0"
                  style={{
                    width: '80px',
                    padding: '8px',
                    border: '2px solid var(--primary-color)',
                    borderRadius: '5px',
                    fontSize: '2rem',
                    fontWeight: '700',
                    textAlign: 'center',
                    color: 'var(--primary-color)',
                    background: 'transparent'
                  }}
                />
              </StatNumber>
              <StatLabel>Teslim Sırası 3. Masa</StatLabel>
              <button
                onClick={() => {
                  const updatedData = { ...sacrificeData };
                  localStorage.setItem('sacrificeMode', JSON.stringify({
                    isActive: true,
                    data: updatedData
                  }));
                  alert('✅ Teslim Sırası 3. Masa güncellendi!');
                }}
                style={{
                  marginTop: '15px',
                  padding: '8px 20px',
                  background: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'var(--transition)'
                }}
                onMouseOver={(e) => e.target.style.background = '#e67e00'}
                onMouseOut={(e) => e.target.style.background = 'var(--primary-color)'}
              >
                Güncelle
              </button>
            </StatCard>

            {/* Teslim Sırası 4. Masa */}
            <StatCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <StatIcon>
                🍽️
              </StatIcon>
              <StatNumber>
                <input
                  type="number"
                  value={sacrificeData.teslimSira4 || ''}
                  onChange={(e) => setSacrificeData(prev => ({ ...prev, teslimSira4: e.target.value }))}
                  placeholder="0"
                  style={{
                    width: '80px',
                    padding: '8px',
                    border: '2px solid var(--primary-color)',
                    borderRadius: '5px',
                    fontSize: '2rem',
                    fontWeight: '700',
                    textAlign: 'center',
                    color: 'var(--primary-color)',
                    background: 'transparent'
                  }}
                />
              </StatNumber>
              <StatLabel>Teslim Sırası 4. Masa</StatLabel>
              <button
                onClick={() => {
                  const updatedData = { ...sacrificeData };
                  localStorage.setItem('sacrificeMode', JSON.stringify({
                    isActive: true,
                    data: updatedData
                  }));
                  alert('✅ Teslim Sırası 4. Masa güncellendi!');
                }}
                style={{
                  marginTop: '15px',
                  padding: '8px 20px',
                  background: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'var(--transition)'
                }}
                onMouseOver={(e) => e.target.style.background = '#e67e00'}
                onMouseOut={(e) => e.target.style.background = 'var(--primary-color)'}
              >
                Güncelle
              </button>
            </StatCard>
          </DashboardGrid>
        )}

        {/* Hisse Satış Yönetimi */}
        <ContentSection>
          <SectionHeader>
            <SectionTitle>💰 Hisse Satış Yönetimi</SectionTitle>
            <DropdownContainer className="share-sale-dropdown">
              <DropdownButton
                onClick={(e) => {
                  e.stopPropagation();
                  setShowShareSaleDropdown(!showShareSaleDropdown);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaCoins />
                Hisse Satışı
                <FaChevronDown />
              </DropdownButton>
              <AnimatePresence>
                {showShareSaleDropdown && (
                  <DropdownMenu
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownItem onClick={(e) => {
                      e.stopPropagation();
                      setShowShareSaleDropdown(false);
                      setShowShareSaleForm(true);
                      // Eğer hayvan seçili değilse, ilk satışta olan hayvanı seç
                      if (!selectedShareSaleAnimal && animals.length > 0) {
                        const firstSaleAnimal = animals.find(animal => animal.status === 'Satışta');
                        if (firstSaleAnimal) {
                          setSelectedShareSaleAnimal(firstSaleAnimal);
                        }
                      }
                    }}>
                      Yeni Satış Ekle
                    </DropdownItem>
                    <DropdownItem onClick={(e) => {
                      e.stopPropagation();
                      setShowShareSaleDropdown(false);
                      setShowShareSaleListModal(true);
                    }}>
                      Hisse Listesi
                    </DropdownItem>
                    <DropdownItem onClick={(e) => {
                      e.stopPropagation();
                      setShowShareSaleDropdown(false);
                      // Satış raporu modalını aç
                      setShowSalesReportModal(true);
                    }}>
                      Satış Raporu
                    </DropdownItem>
                    <DropdownItem onClick={(e) => {
                      e.stopPropagation();
                      setShowShareSaleDropdown(false);
                      // Geçmiş fonksiyonu buraya eklenebilir
                      console.log('Satış geçmişi özelliği geliştiriliyor...');
                    }}>
                      Satış Geçmişi
                    </DropdownItem>
                  </DropdownMenu>
                )}
              </AnimatePresence>
            </DropdownContainer>
          </SectionHeader>
          
          <div style={{ padding: '20px' }}>
            <SearchBar>
              <SearchInput
                type="text"
                placeholder="Hisse satışı ara..."
                value={shareSaleSearch}
                onChange={(e) => setShareSaleSearch(e.target.value)}
              />
              <select
                value={shareSaleType}
                onChange={(e) => setShareSaleType(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--light-gray)',
                  borderRadius: '5px',
                  fontSize: '14px'
                }}
              >
                <option value="all">Tüm Türler</option>
                <option value="Büyükbaş">Büyükbaş</option>
                <option value="Küçükbaş">Küçükbaş</option>
              </select>
              <select
                value={shareSaleGender}
                onChange={(e) => setShareSaleGender(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--light-gray)',
                  borderRadius: '5px',
                  fontSize: '14px'
                }}
              >
                <option value="all">Tüm Cinsler</option>
                <option value="Dana">Dana</option>
                <option value="Düve">Düve</option>
                <option value="Keçi">Keçi</option>
                <option value="Kuzu">Kuzu</option>
              </select>
              <ActionButton onClick={handleDownloadShareSaleReport}>
                <FaDownload />
                Excel İndir
              </ActionButton>
            </SearchBar>
            
            <TableContainer>
              <AdminTable>
                <thead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Hisse Türü</TableHeader>
                    <TableHeader>Hisse Cinsi</TableHeader>
                    <TableHeader>Hisse Sahibi</TableHeader>
                    <TableHeader>Hisse Adedi</TableHeader>
                    <TableHeader>Aracı Olan</TableHeader>
                    <TableHeader>Kurum</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {shareSalesLoading ? (
                    <tr>
                      <td colSpan="7" style={{ padding: '20px', textAlign: 'center' }}>Yükleniyor...</td>
                    </tr>
                  ) : shareSalesError ? (
                    <tr>
                      <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: 'red' }}>{shareSalesError}</td>
                    </tr>
                  ) : shareSales.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: 'gray' }}>Henüz hisse satışı bulunmuyor</td>
                    </tr>
                  ) : (
                    shareSales
                      .filter(sale => {
                                const matchesSearch = (sale.customer_name || '').toLowerCase().includes(shareSaleSearch.toLowerCase()) ||
        (sale.reference_user_name || '').toLowerCase().includes(shareSaleSearch.toLowerCase());
                        const matchesType = shareSaleType === 'all' || sale.animal_type === shareSaleType;
                        const matchesGender = shareSaleGender === 'all' || sale.animal_gender === shareSaleGender;
                        return matchesSearch && matchesType && matchesGender;
                      })
                      .map((sale) => (
                        <TableRow key={sale.id}>
                          <TableCell>#{sale.id}</TableCell>
                                                  <TableCell>{sale.animal_type || ''}</TableCell>
                        <TableCell>{sale.animal_gender || ''}</TableCell>
                        <TableCell>{sale.customer_name || ''}</TableCell>
                        <TableCell>{sale.share_count || 0}</TableCell>
                        <TableCell>{sale.reference_user_name || ''}</TableCell>
                        <TableCell>{sale.reference_user_branch || ''}</TableCell>
                        </TableRow>
                      ))
                  )}
                </tbody>
              </AdminTable>
            </TableContainer>
          </div>
        </ContentSection>

        {/* Hayvan Yönetimi */}
        <ContentSection>
          <SectionHeader>
            <SectionTitle>🐄 Hayvan Yönetimi</SectionTitle>
            {canManageAnimals() && (
              <ActionButton onClick={() => setShowAnimalForm(true)}>
                <FaPlus />
                Yeni Hayvan Ekle
              </ActionButton>
            )}
          </SectionHeader>
          
          <div style={{ padding: '20px' }}>
            <SearchBar>
              <SearchInput
                type="text"
                                  placeholder="ID, Irk, Küpe No, Tür veya Cins ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FilterSelect
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">Tüm Türler</option>
                <option value="Büyükbaş">Büyükbaş</option>
                <option value="Küçükbaş">Küçükbaş</option>
              </FilterSelect>
              <FilterSelect
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="active">Sadece Aktif</option>
                <option value="all">Tüm Durumlar</option>
              </FilterSelect>
              <FilterSelect
                value={filterBreed}
                onChange={(e) => setFilterBreed(e.target.value)}
              >
                <option value="all">Tüm Irklar</option>
                {uniqueBreeds.map(breed => (
                  <option key={breed} value={breed}>{breed}</option>
                ))}
              </FilterSelect>
              <FilterSelect
                value={filterGender}
                onChange={(e) => setFilterGender(e.target.value)}
              >
                <option value="all">Tüm Cinsler</option>
                {uniqueGenders.map(gender => (
                  <option key={gender} value={gender}>{gender}</option>
                ))}
              </FilterSelect>
              <ActionButton onClick={resetAllFilters} style={{ background: 'var(--secondary-color)' }}>
                <FaTimes />
                Filtreleri Temizle
              </ActionButton>
              <ActionButton onClick={handleDownloadReport}>
                <FaDownload />
                Rapor İndir
              </ActionButton>
            </SearchBar>
            
            <TableContainer>
              {animalsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--secondary-color)' }}>
                  <div style={{ fontSize: '1.2rem', marginBottom: '10px' }}>Hayvanlar yükleniyor...</div>
                  <div style={{ fontSize: '0.9rem' }}>Lütfen bekleyin</div>
                </div>
              ) : animalsError ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#e74c3c' }}>
                  <div style={{ fontSize: '1.2rem', marginBottom: '10px' }}>Hata!</div>
                  <div style={{ fontSize: '0.9rem' }}>{animalsError}</div>
                  <button 
                    onClick={() => window.location.reload()} 
                    style={{ 
                      marginTop: '15px', 
                      padding: '8px 16px', 
                      background: 'var(--primary-color)', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer' 
                    }}
                  >
                    Tekrar Dene
                  </button>
                </div>
              ) : filteredAnimals.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--secondary-color)' }}>
                  <div style={{ fontSize: '1.2rem', marginBottom: '10px' }}>
                    {searchTerm || filterType !== 'all' || filterStatus !== 'active' || filterBreed !== 'all' || filterGender !== 'all' ? 'Arama sonucu bulunamadı' : 'Henüz aktif hayvan kaydı yok'}
                  </div>
                  <div style={{ fontSize: '0.9rem' }}>
                    {searchTerm || filterType !== 'all' || filterStatus !== 'active' || filterBreed !== 'all' || filterGender !== 'all' ? 'ID, Irk, Küpe No, Tür veya Cins ile arama yapabilirsiniz' : 'İlk aktif hayvanı eklemek için "Yeni Hayvan Ekle" butonunu kullanın'}
                  </div>
                </div>
              ) : (
                <AdminTable>
                  <thead>
                    <tr>
                      <TableHeader>ID</TableHeader>
                      <TableHeader>Tür</TableHeader>
                      <TableHeader>Irk</TableHeader>
                      <TableHeader>Cins</TableHeader>
                      <TableHeader>Küpe/Hayvan No</TableHeader>
                      <TableHeader>Hayvanın Kilosu</TableHeader>
                      <TableHeader>Giriş Tarihi</TableHeader>
                      <TableHeader>Durum</TableHeader>
                      <TableHeader>Alındığı Kişi/Firma</TableHeader>
                      <TableHeader>İşlemler</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAnimals.map((animal) => (
                      <React.Fragment key={animal.id}>
                        <TableRow
                          key={animal.id}
                          style={selectedRowId === animal.id ? { background: '#fffbe6' } : {}}
                          onClick={() => setSelectedRowId(animal.id)}
                        >
                          <TableCell>#{animal.id}</TableCell>
                          <TableCell>{animal.type}</TableCell>
                          <TableCell>{animal.breed}</TableCell>
                          <TableCell>{animal.gender}</TableCell>
                          <TableCell>
                            {animal.ear_tag ? (
                              <span style={{ fontWeight: '500', color: animal.ear_tag.startsWith('BB') || animal.ear_tag.startsWith('KB') ? '#FF8C00' : '#333' }}>{animal.ear_tag}</span>
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                            )}
                          </TableCell>
                          <TableCell>{animal.last_weight != null ? `${animal.last_weight} kg` : (animal.start_weight ? `${animal.start_weight} kg` : '-')}</TableCell>
                          <TableCell>{animal.entry_date ? new Date(animal.entry_date).toLocaleDateString('tr-TR') : '-'}</TableCell>
                          <TableCell>
                            <span style={{ padding: '4px 8px', borderRadius: '12px', fontSize: '0.8rem', background: animal.status === 'active' ? '#d4edda' : (animal.status === 'Satışta' ? '#fff3cd' : '#f8d7da'), color: animal.status === 'active' ? '#155724' : (animal.status === 'Satışta' ? '#856404' : '#721c24') }}>
                              {animal.status === 'active' ? 'Aktif' : (animal.status === 'Satışta' ? 'Satışta' : 'Pasif')} ({animal.status})
                            </span>
                          </TableCell>
                          <TableCell>{animal.supplier || '-'}</TableCell>
                          <TableCell>
                            <IconButton className="view" title="Görüntüle" onClick={() => handleViewAnimal(animal)}><FaEye /></IconButton>
                            <IconButton className="edit" title="Düzenle" onClick={() => handleEditAnimal(animal)}><FaEdit /></IconButton>
                            <IconButton className="delete" title="Sil" onClick={() => handleDeleteAnimal(animal)}><FaTrash /></IconButton>
                          </TableCell>
                        </TableRow>
                        {/* Alt satır: Butonlar */}
                        {selectedRowId === animal.id && canManageAnimals() && (
                          <tr>
                            <td colSpan={11} style={{ padding: 0, background: '#f8f9fa' }}>
                              <div style={{ display: 'flex', gap: '16px', padding: '8px 0 12px 24px' }}>
                                <Button type="button" className="primary" style={{ minWidth: 160 }} onClick={(e) => { e.stopPropagation(); setSelectedWeightAnimal(animal); setShowWeightModal(true); }}>
                                  Yeni Kilo Ekle
                                </Button>
                                <Button type="button" className="primary" style={{ minWidth: 160 }} onClick={(e) => { e.stopPropagation(); setSelectedSaleAnimal(animal); setShowSaleModal(true); }}>
                                  Satışa Ekle
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </AdminTable>
              )}
            </TableContainer>
          </div>
        </ContentSection>
      </Container>

      {/* Hayvan Ekleme Modal */}
      <AnimatePresence>
        {showAnimalForm && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ModalContent
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              <ModalHeader>
                <ModalTitle>Yeni Hayvan Ekle</ModalTitle>
                <CloseModalButton onClick={() => setShowAnimalForm(false)}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>

              <Form onSubmit={handleAnimalFormSubmit}>
                {animalSubmitError && <ErrorMessage>{typeof animalSubmitError === 'string' ? animalSubmitError : JSON.stringify(animalSubmitError)}</ErrorMessage>}
                {animalSubmitSuccess && <SuccessBanner>{animalSubmitSuccess}</SuccessBanner>}

                <FormRow>
                  <FormGroup>
                    <FormLabel>Hayvan Türü <span className="required">*</span></FormLabel>
                    <FormSelect
                      value={animalFormData.type}
                      onChange={e => setAnimalFormData({ ...animalFormData, type: e.target.value, gender: '' })}
                      required
                    >
                      <option value="">Tür seçin</option>
                      <option value="Büyükbaş">Büyükbaş</option>
                      <option value="Küçükbaş">Küçükbaş</option>
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Irk <span className="required">*</span></FormLabel>
                    <FormInput
                      type="text"
                      value={animalFormData.breed}
                      onChange={e => setAnimalFormData({ ...animalFormData, breed: e.target.value })}
                      placeholder="Örn: Holstein, Simmental, Kıvırcık"
                      required
                    />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <FormLabel>Cins <span className="required">*</span></FormLabel>
                    <FormSelect
                      value={animalFormData.gender}
                      onChange={e => setAnimalFormData({ ...animalFormData, gender: e.target.value })}
                      required
                    >
                      <option value="">Cins seçin</option>
                      {animalFormData.type === 'Büyükbaş' && <>
                        <option value="Dana">Dana</option>
                        <option value="Düve">Düve</option>
                      </>}
                      {animalFormData.type === 'Küçükbaş' && <>
                        <option value="Keçi">Keçi</option>
                        <option value="Kuzu">Kuzu</option>
                      </>}
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Küpe/Hayvan No <span className="required">*</span></FormLabel>
                    <FormInput
                      type="text"
                      value={animalFormData.earTag}
                      onChange={e => setAnimalFormData({ ...animalFormData, earTag: e.target.value })}
                      placeholder="Örn: TR123456789"
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <FormLabel>
                      Başlangıç Kilosu (kg) <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="number"
                      name="startWeight"
                      value={animalFormData.startWeight}
                      onChange={handleAnimalInputChange}
                      min="1"
                      max="2000"
                      step="0.1"
                      placeholder="Örn: 250.5"
                      className={animalFormErrors.startWeight ? 'error' : ''}
                    />
                    {animalFormErrors.startWeight && <ErrorMessage>{animalFormErrors.startWeight}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <FormLabel>Doğum Tarihi</FormLabel>
                    <FormInput
                      type="date"
                      name="birthDate"
                      value={animalFormData.birthDate}
                      onChange={handleAnimalInputChange}
                      className={animalFormErrors.birthDate ? 'error' : ''}
                    />
                    {animalFormErrors.birthDate && <ErrorMessage>{animalFormErrors.birthDate}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      Çiftliğe Giriş Tarihi <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="date"
                      name="entryDate"
                      value={animalFormData.entryDate}
                      onChange={handleAnimalInputChange}
                      className={animalFormErrors.entryDate ? 'error' : ''}
                    />
                    {animalFormErrors.entryDate && <ErrorMessage>{animalFormErrors.entryDate}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <FormLabel>Notlar / Açıklama</FormLabel>
                  <FormTextarea
                    name="notes"
                    value={animalFormData.notes}
                    onChange={handleAnimalInputChange}
                    maxLength={500}
                    placeholder="Hayvan hakkında ek bilgiler..."
                  />
                  <div style={{ textAlign: 'right', fontSize: '0.8rem', color: 'var(--secondary-color)', marginTop: '5px' }}>
                    {animalFormData.notes.length}/500
                  </div>
                </FormGroup>

                <FormGroup>
                  <FormLabel>
                    Alındığı Kişi/Firma <span className="required">*</span>
                  </FormLabel>
                  <FormInput
                    type="text"
                    name="supplier"
                    value={animalFormData.supplier}
                                          onChange={handleAnimalInputChange}
                      onBlur={handleAnimalInputBlur}
                      maxLength={100}
                      placeholder="Örn: Mehmet Yılmaz, ABC Hayvancılık"
                      className={animalFormErrors.supplier ? 'error' : ''}
                  />
                  {animalFormErrors.supplier && <ErrorMessage>{animalFormErrors.supplier}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <FormLabel>Hayvan Fotoğrafı</FormLabel>
                  <ImageUploadContainer 
                    className={imagePreview ? 'has-image' : ''}
                    onClick={() => document.getElementById('animal-image-input').click()}
                  >
                    {imagePreview ? (
                      <ImagePreview>
                        <img src={imagePreview} alt="Önizleme" />
                      </ImagePreview>
                    ) : (
                      <UploadIcon>
                        <FaImage />
                      </UploadIcon>
                    )}
                    <UploadText>
                      {imagePreview ? 'Resmi değiştirmek için tıklayın' : 'Resim yüklemek için tıklayın'}
                    </UploadText>
                    <UploadSubtext>
                      JPG, PNG, WEBP (Max 5MB)
                    </UploadSubtext>
                  </ImageUploadContainer>
                  <HiddenFileInput
                    id="animal-image-input"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageSelect}
                  />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <FormLabel>
                      Hayvanın Alış Fiyatı (₺)
                    </FormLabel>
                    <FormInput
                      type="text"
                      name="purchasePrice"
                      value={animalFormData.purchasePrice}
                      onChange={handleAnimalInputChange}
                      placeholder="Örn: 15000,00"
                      inputMode="decimal"
                      maxLength={14}
                    />
                    {animalFormErrors.purchasePrice && <ErrorMessage>{animalFormErrors.purchasePrice}</ErrorMessage>}
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      Kilo Fiyatı (₺/kg)
                    </FormLabel>
                    <FormInput
                      type="text"
                      name="purchasePricePerKg"
                      value={animalFormData.purchasePricePerKg}
                      onChange={handleAnimalInputChange}
                      placeholder="Örn: 120,50"
                      inputMode="decimal"
                      maxLength={12}
                    />
                    {animalFormErrors.purchasePricePerKg && <ErrorMessage>{animalFormErrors.purchasePricePerKg}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormButtons>
                  <Button type="button" className="secondary" onClick={handleAnimalFormReset}>
                    Temizle
                  </Button>
                  <Button type="button" className="danger" onClick={() => setShowAnimalForm(false)}>
                    İptal
                  </Button>
                  <Button type="submit" className="primary" disabled={animalSubmitLoading}>
                    {animalSubmitLoading ? 'Kaydediliyor...' : 'Kaydet'}
                  </Button>
                </FormButtons>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* Hayvan Görüntüleme Modal */}
      <AnimatePresence>
        {showAnimalViewModal && selectedAnimal && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ModalContent
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              <ModalHeader>
                <ModalTitle>🐄 Hayvan Detayları</ModalTitle>
                <CloseModalButton onClick={() => setShowAnimalViewModal(false)}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Hayvan Fotoğrafı */}
                <div style={{ textAlign: 'center', position: 'relative' }}>
                  {(() => {
                    const imageUrl = selectedAnimal.image_url || selectedAnimal.imageUrl;
                    console.log('🔍 Modal içinde - selectedAnimal:', selectedAnimal);
                    console.log('🔍 Modal içinde - imageUrl:', imageUrl);
                    console.log('🔍 Modal içinde - imageLoadError:', imageLoadError);
                    
                    const placeholderDiv = (
                      <div style={{ 
                        color: '#999', 
                        fontStyle: 'italic', 
                        fontSize: '1.1rem', 
                        padding: '32px 0',
                        background: '#f5f5f5',
                        borderRadius: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '200px'
                      }}>
                        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📷</div>
                        <div>Hayvan Fotoğrafı</div>
                        <div style={{ fontSize: '0.9rem', marginTop: '8px', color: '#bbb' }}>Fotoğraf yüklenmemiş</div>
                      </div>
                    );
                    
                    if (imageUrl && !imageLoadError) {
                      // URL'yi düzgün formatla
                      let finalUrl;
                      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
                        // Zaten tam URL
                        finalUrl = imageUrl;
                      } else if (imageUrl.startsWith('/uploads/')) {
                        // Backend'den gelen /uploads/animals/... formatı
                        finalUrl = `${API_BASE_URL}${imageUrl}`;
                      } else if (imageUrl.startsWith('/')) {
                        // Diğer / ile başlayan yollar
                        finalUrl = `${API_BASE_URL}${imageUrl}`;
                      } else {
                        // Sadece dosya adı gelirse
                        finalUrl = `${API_BASE_URL}/uploads/animals/${imageUrl}`;
                      }
                      
                      console.log('📷 Hayvan fotoğrafı URL oluşturuldu:', { imageUrl, finalUrl });
                      
                      return (
                        <img
                          src={finalUrl}
                          alt="Hayvan Fotoğrafı"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '320px',
                            borderRadius: '12px',
                            objectFit: 'cover',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
                          }}
                          onError={() => {
                            console.error('Görsel yükleme hatası:', finalUrl);
                            setImageLoadError(true);
                          }}
                        />
                      );
                    } else {
                      return placeholderDiv;
                    }
                  })()}
                </div>

                {/* Bilgiler Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '18px',
                  background: '#f8f9fa',
                  borderRadius: '10px',
                  padding: '24px',
                  fontSize: '1rem',
                  color: 'var(--secondary-color)'
                }}>
                  <div><strong>ID:</strong> #{selectedAnimal.id}</div>
                  <div><strong>Tür:</strong> {selectedAnimal.type || 'Belirtilmemiş'}</div>
                  <div><strong>Irk:</strong> {selectedAnimal.breed || 'Belirtilmemiş'}</div>
                  <div><strong>Cins:</strong> {selectedAnimal.gender || 'Belirtilmemiş'}</div>
                  <div><strong>Küpe/Hayvan No:</strong> {selectedAnimal.ear_tag || 'Belirtilmemiş'}</div>
                  <div><strong>Hayvanın Kilosu:</strong> {selectedAnimal.last_weight != null ? `${selectedAnimal.last_weight} kg` : (selectedAnimal.start_weight ? `${selectedAnimal.start_weight} kg` : '-')}</div>
                  <div><strong>Doğum Tarihi:</strong> {selectedAnimal.birth_date ? new Date(selectedAnimal.birth_date).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}</div>
                  <div><strong>Giriş Tarihi:</strong> {selectedAnimal.entry_date ? new Date(selectedAnimal.entry_date).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}</div>
                  <div><strong>Durum:</strong> <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '0.9rem',
                    background: selectedAnimal.status === 'active' ? '#d4edda' : '#f8d7da',
                    color: selectedAnimal.status === 'active' ? '#155724' : '#721c24'
                  }}>{selectedAnimal.status === 'active' ? 'Aktif' : 'Pasif'}</span></div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <strong>Notlar:</strong><br />
                    <span style={{
                      display: 'inline-block',
                      marginTop: '6px',
                      background: '#fff',
                      borderRadius: '6px',
                      padding: '10px',
                      minHeight: '32px',
                      color: '#555',
                      fontStyle: selectedAnimal.notes ? 'normal' : 'italic'
                    }}>{selectedAnimal.notes || 'Belirtilmemiş'}</span>
                  </div>
                  <div><strong>Alındığı Kişi/Firma:</strong> {selectedAnimal.supplier || 'Belirtilmemiş'}</div>
                  <div><strong>Kilo Farkı:</strong> {(() => {
                    let sonTartimKilosu = selectedAnimal.start_weight;
                    if (animalWeights && animalWeights.length > 0) {
                      sonTartimKilosu = animalWeights[animalWeights.length - 1].weight;
                    }
                    const kiloFarki = sonTartimKilosu - selectedAnimal.start_weight;
                    if (animalWeights && animalWeights.length > 0) {
                      return (kiloFarki > 0 ? `+${kiloFarki} kg` : `${kiloFarki} kg`);
                    } else {
                      return '-';
                    }
                  })()}</div>
                </div>

                {/* Tartım Geçmişi */}
                <div style={{ marginTop: 24 }}>
                  <h3 style={{ color: 'var(--primary-color)', fontSize: '1.1rem', marginBottom: 10 }}>Tartım Geçmişi</h3>
                  {animalWeightsLoading ? (
                    <div style={{ color: '#888', fontStyle: 'italic', fontSize: '1rem', padding: '12px 0' }}>Yükleniyor...</div>
                  ) : animalWeightsError ? (
                    <div style={{ color: '#e74c3c', fontStyle: 'italic', fontSize: '1rem', padding: '12px 0' }}>{animalWeightsError}</div>
                  ) : animalWeights.length === 0 ? (
                    <div style={{ color: '#888', fontStyle: 'italic', fontSize: '1rem', padding: '12px 0' }}>Tartım kaydı yok</div>
                  ) : (
                    <table style={{ width: '100%', background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', fontSize: '0.98rem', marginTop: 4 }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa' }}>
                          <th style={{ padding: 8, textAlign: 'left', color: '#555', fontWeight: 600 }}>#</th>
                          <th style={{ padding: 8, textAlign: 'left', color: '#555', fontWeight: 600 }}>Tarih</th>
                          <th style={{ padding: 8, textAlign: 'left', color: '#555', fontWeight: 600 }}>Kilo (kg)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: 8 }}>Giriş Kilosu</td>
                          <td style={{ padding: 8 }}>{selectedAnimal.entry_date ? new Date(selectedAnimal.entry_date).toLocaleDateString('tr-TR') : '-'}</td>
                          <td style={{ padding: 8 }}>{selectedAnimal.start_weight}</td>
                        </tr>
                        {animalWeights.map((w, i) => (
                          <tr key={w.id}>
                            <td style={{ padding: 8 }}>{i + 1}. tartım</td>
                            <td style={{ padding: 8 }}>{w.date ? new Date(w.date).toLocaleDateString('tr-TR') : '-'}</td>
                            <td style={{ padding: 8 }}>{w.weight}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <Button
                    type="button"
                    className="primary"
                    onClick={() => {
                      setShowAnimalViewModal(false);
                      handleEditAnimal(selectedAnimal);
                    }}
                  >
                    Düzenle
                  </Button>
                </div>
              </div>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* Hayvan Düzenleme Modal */}
      <AnimatePresence>
        {showAnimalEditModal && selectedAnimal && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ModalContent
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              <ModalHeader>
                <ModalTitle>✏️ Hayvan Düzenle - #{selectedAnimal.id}</ModalTitle>
                <CloseModalButton onClick={() => setShowAnimalEditModal(false)}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>

              <Form onSubmit={handleEditAnimalSubmit}>
                {editSubmitError && <ErrorMessage>{typeof editSubmitError === 'string' ? editSubmitError : JSON.stringify(editSubmitError)}</ErrorMessage>}
                {editSubmitSuccess && <SuccessBanner>{editSubmitSuccess}</SuccessBanner>}

                <FormRow>
                  <FormGroup>
                    <FormLabel>
                      Hayvan Türü <span className="required">*</span>
                    </FormLabel>
                    <FormSelect
                      name="type"
                      value={editAnimalData.type}
                      onChange={handleEditInputChange}
                      className={editAnimalErrors.type ? 'error' : ''}
                    >
                      <option value="">Tür seçin</option>
                      <option value="Büyükbaş">Büyükbaş</option>
                      <option value="Küçükbaş">Küçükbaş</option>
                    </FormSelect>
                    {editAnimalErrors.type && <ErrorMessage>{editAnimalErrors.type}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      Irk / Cins <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="text"
                      name="breed"
                      value={editAnimalData.breed}
                      onChange={handleEditInputChange}
                      maxLength={50}
                      placeholder="Örn: Holstein, Simmental, Kıvırcık"
                      className={editAnimalErrors.breed ? 'error' : ''}
                    />
                    {editAnimalErrors.breed && <ErrorMessage>{editAnimalErrors.breed}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <FormLabel>
                      Cins <span className="required">*</span>
                    </FormLabel>
                    <RadioGroup>
                      <RadioLabel>
                        <input
                          type="radio"
                          name="gender"
                          value="Erkek"
                          checked={editAnimalData.gender === 'Erkek'}
                          onChange={handleEditInputChange}
                        />
                        Erkek
                      </RadioLabel>
                      <RadioLabel>
                        <input
                          type="radio"
                          name="gender"
                          value="Dişi"
                          checked={editAnimalData.gender === 'Dişi'}
                          onChange={handleEditInputChange}
                        />
                        Dişi
                      </RadioLabel>
                    </RadioGroup>
                    {editAnimalErrors.gender && <ErrorMessage>{editAnimalErrors.gender}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      Küpe/Hayvan No <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="text"
                      name="earTag"
                      value={editAnimalData.earTag}
                      onChange={handleEditInputChange}
                      maxLength={50}
                      placeholder="Örn: TR123456789"
                      className={editAnimalErrors.earTag ? 'error' : ''}
                    />
                    {editAnimalErrors.earTag && <ErrorMessage>{editAnimalErrors.earTag}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <FormLabel>
                      Başlangıç Kilosu (kg) <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="number"
                      name="startWeight"
                      value={editAnimalData.startWeight}
                      onChange={handleEditInputChange}
                      min="1"
                      max="2000"
                      step="0.1"
                      placeholder="Örn: 250.5"
                      className={editAnimalErrors.startWeight ? 'error' : ''}
                    />
                    {editAnimalErrors.startWeight && <ErrorMessage>{editAnimalErrors.startWeight}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <FormLabel>Doğum Tarihi</FormLabel>
                    <FormInput
                      type="date"
                      name="birthDate"
                      value={editAnimalData.birthDate}
                      onChange={handleEditInputChange}
                      className={editAnimalErrors.birthDate ? 'error' : ''}
                    />
                    {editAnimalErrors.birthDate && <ErrorMessage>{editAnimalErrors.birthDate}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      Çiftliğe Giriş Tarihi <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="date"
                      name="entryDate"
                      value={editAnimalData.entryDate}
                      onChange={handleEditInputChange}
                      className={editAnimalErrors.entryDate ? 'error' : ''}
                    />
                    {editAnimalErrors.entryDate && <ErrorMessage>{editAnimalErrors.entryDate}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <FormLabel>Notlar / Açıklama</FormLabel>
                  <FormTextarea
                    name="notes"
                    value={editAnimalData.notes}
                    onChange={handleEditInputChange}
                    maxLength={500}
                    placeholder="Hayvan hakkında ek bilgiler..."
                  />
                  <div style={{ textAlign: 'right', fontSize: '0.8rem', color: 'var(--secondary-color)', marginTop: '5px' }}>
                    {editAnimalData.notes.length}/500
                  </div>
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <FormLabel>
                      Alındığı Kişi/Firma <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="text"
                      name="supplier"
                      value={editAnimalData.supplier}
                                              onChange={handleEditInputChange}
                        onBlur={handleEditInputBlur}
                        maxLength={100}
                        placeholder="Örn: Mehmet Yılmaz, ABC Hayvancılık"
                        className={editAnimalErrors.supplier ? 'error' : ''}
                    />
                    {editAnimalErrors.supplier && <ErrorMessage>{editAnimalErrors.supplier}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormButtons>
                  <Button type="button" className="secondary" onClick={() => setShowAnimalEditModal(false)}>
                    İptal
                  </Button>
                  <Button type="submit" className="primary" disabled={editSubmitLoading}>
                    {editSubmitLoading ? 'Güncelleniyor...' : 'Güncelle'}
                  </Button>
                </FormButtons>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* Hayvan Silme Onay Modal */}
      <AnimatePresence>
        {deleteConfirmModal && animalToDelete && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ModalContent
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              transition={{ duration: 0.3 }}
              style={{ maxWidth: '400px' }}
            >
              <ModalHeader>
                <ModalTitle>⚠️ Hayvan Silme Onayı</ModalTitle>
                <CloseModalButton onClick={() => setDeleteConfirmModal(false)}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>

              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '1.1rem', marginBottom: '15px', color: 'var(--secondary-color)' }}>
                  Bu hayvanı silmek istediğinizden emin misiniz?
                </div>
                
                <div style={{ 
                  background: '#f8f9fa', 
                  padding: '15px', 
                  borderRadius: '8px', 
                  marginBottom: '20px',
                  textAlign: 'left'
                }}>
                  <div><strong>ID:</strong> #{animalToDelete.id}</div>
                  <div><strong>Tür:</strong> {animalToDelete.type}</div>
                  <div><strong>Irk:</strong> {animalToDelete.breed}</div>
                  <div><strong>Cins:</strong> {animalToDelete.gender}</div>
                </div>

                <div style={{ color: '#e74c3c', fontSize: '0.9rem', marginBottom: '20px' }}>
                  ⚠️ Bu işlem geri alınamaz!
                </div>

                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                  <Button 
                    type="button" 
                    className="secondary"
                    onClick={() => setDeleteConfirmModal(false)}
                    disabled={deleteLoading}
                  >
                    İptal
                  </Button>
                  <Button 
                    type="button" 
                    className="danger"
                    onClick={confirmDeleteAnimal}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? 'Siliniyor...' : 'Evet, Sil'}
                  </Button>
                </div>
              </div>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* Kullanıcı Ekleme Modal */}
      <AnimatePresence>
        {showUserForm && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ModalContent
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              <ModalHeader>
                <ModalTitle>Yeni Kullanıcı Ekle</ModalTitle>
                <CloseModalButton onClick={() => setShowUserForm(false)}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>

              <Form onSubmit={handleUserFormSubmit}>
                {submitError && <ErrorMessage>{typeof submitError === 'string' ? submitError : JSON.stringify(submitError)}</ErrorMessage>}
                {submitSuccess && <SuccessBanner>{submitSuccess}</SuccessBanner>}
                <FormRow>
                  <FormGroup>
                    <FormLabel>
                      Ad <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={e => setFormData({ ...formData, firstName: formatName(e.target.value) })}
                      maxLength={50}
                      className={formErrors.firstName ? 'error' : ''}
                    />
                    {formErrors.firstName && <ErrorMessage>{formErrors.firstName}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      Soyad <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={e => setFormData({ ...formData, lastName: formatSurname(e.target.value) })}
                      maxLength={50}
                      className={formErrors.lastName ? 'error' : ''}
                    />
                    {formErrors.lastName && <ErrorMessage>{formErrors.lastName}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <FormLabel>
                      E-posta <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      className={formErrors.email ? 'error' : ''}
                    />
                    {formErrors.email && <ErrorMessage>{formErrors.email}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>Telefon</FormLabel>
                    <FormInput
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                      maxLength={11}
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <FormLabel>
                      Kullanıcı Adı <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={e => setFormData({ ...formData, username: formatName(e.target.value) })}
                      maxLength={30}
                      className={formErrors.username ? 'error' : ''}
                    />
                    {formErrors.username && <ErrorMessage>{formErrors.username}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      Rol / Yetki <span className="required">*</span>
                    </FormLabel>
                    <FormSelect
                      name="role"
                      value={formData.role}
                      onChange={e => setFormData({ ...formData, role: e.target.value })}
                      className={formErrors.role ? 'error' : ''}
                    >
                      <option value="">Rol seçin</option>
                      <option value="admin">Yönetici</option>
                      <option value="manager">Kurum Mesulü</option>
                      <option value="staff">Personel</option>
                    </FormSelect>
                    {formErrors.role && <ErrorMessage>{formErrors.role}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup style={{ position: 'relative' }}>
                    <FormLabel>
                      Şifre <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={e => setFormData({ ...formData, password: e.target.value })}
                      minLength={8}
                      required
                    />
                    <span onClick={() => setShowPassword(v => !v)} style={{ position: 'absolute', right: 12, top: 32, cursor: 'pointer' }}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                    {formErrors.password && <ErrorMessage>{formErrors.password}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup style={{ position: 'relative' }}>
                    <FormLabel>
                      Şifre Tekrar <span className="required">*</span>
                    </FormLabel>
                    <FormInput
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                      minLength={8}
                      required
                    />
                    <span onClick={() => setShowConfirmPassword(v => !v)} style={{ position: 'absolute', right: 12, top: 32, cursor: 'pointer' }}>
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                    {formErrors.confirmPassword && <ErrorMessage>{formErrors.confirmPassword}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <FormLabel>
                    Bağlı Olduğu Kurum <span className="required">*</span>
                  </FormLabel>
                  <FormSelect
                    name="branch"
                    value={formData.branch}
                    onChange={e => setFormData({ ...formData, branch: formatName(e.target.value) })}
                    className={formErrors.branch ? 'error' : ''}
                  >
                    <option value="">Kurum seçin</option>
                    <option value="Süleymaniye">Süleymaniye</option>
                    <option value="Paşaköprüsü">Paşaköprüsü</option>
                    <option value="Bağlarbaşı">Bağlarbaşı</option>
                    <option value="Ferhatlar">Ferhatlar</option>
                    <option value="Fatih Kız">Fatih Kız</option>
                    <option value="Çamlıca">Çamlıca</option>
                    <option value="Şirinevler Kız">Şirinevler Kız</option>
                    <option value="Bahçelievler Kız">Bahçelievler Kız</option>
                    <option value="Çiftlik">Çiftlik</option>
                  </FormSelect>
                  {formErrors.branch && <ErrorMessage>{formErrors.branch}</ErrorMessage>}
                </FormGroup>

                <FormButtons>
                  <Button type="button" className="secondary" onClick={handleFormReset}>
                    Temizle
                  </Button>
                  <Button type="button" className="danger" onClick={() => setShowUserForm(false)}>
                    İptal
                  </Button>
                  <Button type="submit" className="primary" disabled={submitLoading}>
                    {submitLoading ? 'Kaydediliyor...' : 'Kaydet'}
                  </Button>
                </FormButtons>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* Yeni Kilo Ekle Modal */}
      <AnimatePresence>
        {showWeightModal && selectedWeightAnimal && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ModalContent
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              <ModalHeader>
                <ModalTitle>Yeni Kilo Ekle - #{selectedWeightAnimal.id}</ModalTitle>
                <CloseModalButton onClick={() => { setShowWeightModal(false); setWeightError(''); setWeightSuccess(''); }}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>
              <Form onSubmit={handleWeightSubmit}>
                {weightError && <ErrorMessage>{weightError}</ErrorMessage>}
                {weightSuccess && <SuccessBanner>{weightSuccess}</SuccessBanner>}
                <FormRow>
                  <FormGroup>
                    <FormLabel>Tartım Tarihi <span className="required">*</span></FormLabel>
                    <FormInput
                      type="date"
                      value={weightDate}
                      onChange={e => setWeightDate(e.target.value)}
                      max={new Date().toISOString().slice(0, 10)}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Yeni Kilo (kg) <span className="required">*</span></FormLabel>
                    <FormInput
                      type="number"
                      value={weightValue}
                      onChange={e => setWeightValue(e.target.value.replace(/[^0-9.]/g, ''))}
                      min="1"
                      max="2000"
                      step="0.1"
                      placeholder="Örn: 350.5"
                      required
                    />
                  </FormGroup>
                </FormRow>
                <FormButtons>
                  <Button type="button" className="secondary" onClick={() => { setShowWeightModal(false); setWeightError(''); setWeightSuccess(''); }}>İptal</Button>
                  <Button type="submit" className="primary">Kaydet</Button>
                </FormButtons>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* Satışa Ekle Modal */}
      <AnimatePresence>
        {showSaleModal && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ModalContent
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              <ModalHeader>
                <ModalTitle>Satışa Ekle {selectedSaleAnimal ? `- #${selectedSaleAnimal.id}` : ''}</ModalTitle>
                <CloseModalButton onClick={() => setShowSaleModal(false)}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>
              {selectedSaleAnimal ? (
                <Form onSubmit={handleSaleSubmit}>
                  {saleError && <ErrorMessage>{saleError}</ErrorMessage>}
                  {saleSuccess && <SuccessBanner>{saleSuccess}</SuccessBanner>}
                  <FormRow>
                    <FormGroup style={{ gridColumn: '1 / -1' }}>
                      <FormLabel>Mevcut kilo (hesap bazı)</FormLabel>
                      <FormInput
                        type="text"
                        readOnly
                        value={(() => {
                          const w = getSaleWeightKg(selectedSaleAnimal);
                          return Number.isFinite(w) ? `${String(w).replace('.', ',')} kg` : 'Tanımlı değil';
                        })()}
                        style={{ background: '#f8f9fa', fontWeight: 600 }}
                      />
                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: 6 }}>
                        Kilo fiyatı ve toplam tutar bu değere göre hesaplanır: önce son tartım kilosu, yoksa başlangıç kilosu.
                      </div>
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <FormLabel>Hisse Adedi <span className="required">*</span></FormLabel>
                      <FormSelect
                        value={saleShareCount}
                        onChange={e => setSaleShareCount(Number(e.target.value))}
                        disabled={selectedSaleAnimal.type === 'Küçükbaş'}
                      >
                        {selectedSaleAnimal.type === 'Büyükbaş'
                          ? [1,2,3,4,5,6,7].map(n => <option key={n} value={n}>{n}</option>)
                          : <option value={1}>1</option>
                        }
                      </FormSelect>
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>Toplam Satış Fiyatı <span className="required">*</span></FormLabel>
                      <FormInput
                        type="text"
                        value={saleTotalPrice}
                        onChange={handleSaleTotalChange}
                        placeholder="Örn: 15000,00"
                        inputMode="decimal"
                        maxLength={14}
                        required
                      />
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <FormLabel>Kilo Fiyatı (₺/kg)</FormLabel>
                      <FormInput
                        type="text"
                        value={salePricePerKg}
                        onChange={handleSalePricePerKgChange}
                        placeholder="Örn: 120,50"
                        inputMode="decimal"
                        maxLength={12}
                      />
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <FormLabel>Hisse Başı Fiyat</FormLabel>
                      <FormInput
                        type="text"
                        value={salePricePerShare}
                        readOnly
                        style={{ background: '#f8f9fa', fontWeight: 600 }}
                      />
                    </FormGroup>
                  </FormRow>
                  <FormButtons>
                    <Button type="button" className="secondary" onClick={() => setShowSaleModal(false)}>İptal</Button>
                    <Button type="submit" className="primary">Kaydet</Button>
                  </FormButtons>
                </Form>
              ) : (
                <div style={{ padding: 32, textAlign: 'center', color: '#e67e00', fontWeight: 600 }}>
                  Satışta hayvan bulunamadı.
                </div>
              )}
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* Profil Ayarları Modal */}
      {showProfileForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--white-color)',
            borderRadius: 'var(--border-radius)',
            padding: '30px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative'
          }}>
              <ModalHeader>
                <ModalTitle>Profil Ayarları</ModalTitle>
                <CloseModalButton onClick={() => setShowProfileForm(false)}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>
              <Form onSubmit={handleProfileFormSubmit}>
                {profileSubmitError && <ErrorMessage>{profileSubmitError}</ErrorMessage>}
                {profileSubmitSuccess && <SuccessBanner>{profileSubmitSuccess}</SuccessBanner>}
                <FormRow>
                  <FormGroup>
                    <FormLabel>Ad <span className="required">*</span></FormLabel>
                    <FormInput name="firstName" value={profileFormData.firstName} onChange={handleProfileInputChange} />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Soyad <span className="required">*</span></FormLabel>
                    <FormInput name="lastName" value={profileFormData.lastName} onChange={handleProfileInputChange} />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <FormLabel>E-posta <span className="required">*</span></FormLabel>
                    <FormInput name="email" value={profileFormData.email} onChange={handleProfileInputChange} />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Telefon</FormLabel>
                    <FormInput name="phone" value={profileFormData.phone} onChange={handleProfileInputChange} />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <FormLabel>Kullanıcı Adı <span className="required">*</span></FormLabel>
                    <FormInput name="username" value={profileFormData.username} readOnly disabled />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Rol / Yetki</FormLabel>
                    <FormInput name="role" value={profileFormData.role} readOnly disabled />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <FormLabel>Bağlı Olduğu Kurum</FormLabel>
                    <FormInput name="branch" value={profileFormData.branch} readOnly disabled />
                  </FormGroup>
                </FormRow>
                <FormButtons>
                  <Button type="button" className="secondary" onClick={handleProfileFormReset}>Temizle</Button>
                  <Button type="button" className="danger" onClick={() => setShowProfileForm(false)}>İptal</Button>
                  <Button type="submit" className="primary" disabled={profileSubmitLoading}>{profileSubmitLoading ? 'Kaydediliyor...' : 'Kaydet'}</Button>
                  <Button type="button" className="primary" onClick={handleOpenChangePassword}>Şifre Değiştir</Button>
                </FormButtons>
              </Form>
            </div>
          </div>
        )}

      {/* Sistem ayarları — veritabanı yedek / sıfırla / yükle (yalnızca admin şifresi) */}
      {showSystemSettingsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '20px'
        }} onClick={() => { setShowSystemSettingsModal(false); setSystemAdminPassword(''); }}>
          <div style={{
            background: 'var(--white-color)',
            borderRadius: 'var(--border-radius)',
            padding: '28px',
            maxWidth: '520px',
            width: '100%',
            position: 'relative',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '1.35rem', display: 'flex', alignItems: 'center', gap: 10 }}>
                <FaDatabase /> Sistem ayarları
              </h2>
              <button
                type="button"
                onClick={() => { setShowSystemSettingsModal(false); setSystemAdminPassword(''); setSystemSettingsMessage(''); }}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#999', lineHeight: 1 }}
                aria-label="Kapat"
              >
                <FaTimes />
              </button>
            </div>
            <p style={{ color: '#555', fontSize: '14px', marginBottom: '16px', lineHeight: 1.5 }}>
              Aşağıdaki işlemler için <strong>yönetici (admin)</strong> hesabının şifresi gereklidir. Her işlem sunucuda doğrulanır.
            </p>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: 'var(--secondary-color)' }}>Yönetici şifresi</label>
              <input
                type="password"
                autoComplete="current-password"
                value={systemAdminPassword}
                onChange={(e) => setSystemAdminPassword(e.target.value)}
                placeholder="Admin şifrenizi girin"
                style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '2px solid #ddd', fontSize: '15px', boxSizing: 'border-box' }}
              />
            </div>
            {systemSettingsMessage && (
              <div style={{ marginBottom: '14px', padding: '10px 12px', background: '#e8f5e9', color: '#2e7d32', borderRadius: '8px', fontSize: '14px' }}>
                {systemSettingsMessage}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                type="button"
                disabled={systemSettingsLoading}
                onClick={handleSystemSettingsBackup}
                style={{
                  padding: '14px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'var(--primary-color)',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: systemSettingsLoading ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10
                }}
              >
                <FaDownload /> Veritabanını yedekle
              </button>
              <button
                type="button"
                disabled={systemSettingsLoading}
                onClick={handleSystemSettingsReset}
                style={{
                  padding: '14px 16px',
                  borderRadius: '8px',
                  border: '1px solid #c62828',
                  background: '#fff',
                  color: '#c62828',
                  fontWeight: 600,
                  cursor: systemSettingsLoading ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10
                }}
              >
                <FaTrash /> Veritabanını sıfırla
              </button>
              <input
                ref={systemDbRestoreInputRef}
                type="file"
                accept=".db,application/x-sqlite3,application/octet-stream"
                style={{ display: 'none' }}
                onChange={handleSystemSettingsRestoreFile}
              />
              <button
                type="button"
                disabled={systemSettingsLoading}
                onClick={() => systemDbRestoreInputRef.current?.click()}
                style={{
                  padding: '14px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#1565c0',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: systemSettingsLoading ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10
                }}
              >
                <FaUpload /> Veritabanı yükle
              </button>
            </div>
            {systemSettingsLoading && (
              <p style={{ marginTop: '14px', textAlign: 'center', color: '#666', fontSize: '14px' }}>İşlem yapılıyor…</p>
            )}
          </div>
        </div>
      )}

      {/* Kurban Modu Modal */}
      {showSacrificeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }} onClick={() => setShowSacrificeModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: 'var(--secondary-color)',
                margin: 0
              }}>
                Kurban Modu {isSacrificeModeActive ? '(Aktif)' : '(Pasif)'}
              </h2>
              <button 
                onClick={() => setShowSacrificeModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: 'var(--secondary-color)',
                  cursor: 'pointer',
                  transition: 'var(--transition)'
                }}
                onMouseOver={(e) => e.target.style.color = 'var(--primary-color)'}
                onMouseOut={(e) => e.target.style.color = 'var(--secondary-color)'}
              >
                <FaTimes />
              </button>
            </div>
            
            <div style={{
              textAlign: 'center',
              padding: '20px 0'
            }}>
              <div style={{
                fontSize: '4rem',
                color: 'var(--primary-color)',
                marginBottom: '20px'
              }}>
                🐑
              </div>
              <h3 style={{
                fontSize: '1.2rem',
                color: 'var(--secondary-color)',
                marginBottom: '15px'
              }}>
                {isSacrificeModeActive ? 'Kurban Modu Aktif' : 'Kurban Modu Pasif'}
              </h3>
              <p style={{
                color: 'var(--secondary-color)',
                lineHeight: '1.6',
                marginBottom: '30px'
              }}>
                {isSacrificeModeActive 
                  ? 'Kurban modu şu anda aktif. Aşağıdaki sıra numaralarını güncelleyebilir veya modu deaktif edebilirsiniz.'
                  : 'Kurban modunu aktif etmek için aşağıdaki sıra numaralarını girin. Bu bilgiler anasayfada gösterilecektir.'
                }
              </p>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                <div style={{
                  padding: '20px',
                  border: '2px solid var(--light-gray)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🔪</div>
                  <h4 style={{ margin: '0 0 15px 0', color: 'var(--secondary-color)' }}>Kesim Sırası</h4>
                  <input
                    type="number"
                    value={sacrificeData.kesimSira}
                    onChange={(e) => setSacrificeData(prev => ({ ...prev, kesimSira: e.target.value }))}
                    placeholder="Sıra numarası"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid var(--light-gray)',
                      borderRadius: '5px',
                      fontSize: '16px',
                      textAlign: 'center'
                    }}
                  />
                </div>
                
                <div style={{
                  padding: '20px',
                  border: '2px solid var(--light-gray)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🍽️</div>
                  <h4 style={{ margin: '0 0 15px 0', color: 'var(--secondary-color)' }}>Teslim Sırası 1. Masa</h4>
                  <input
                    type="number"
                    value={sacrificeData.teslimSira1}
                    onChange={(e) => setSacrificeData(prev => ({ ...prev, teslimSira1: e.target.value }))}
                    placeholder="Sıra numarası"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid var(--light-gray)',
                      borderRadius: '5px',
                      fontSize: '16px',
                      textAlign: 'center'
                    }}
                  />
                </div>
                
                <div style={{
                  padding: '20px',
                  border: '2px solid var(--light-gray)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🍽️</div>
                  <h4 style={{ margin: '0 0 15px 0', color: 'var(--secondary-color)' }}>Teslim Sırası 2. Masa</h4>
                  <input
                    type="number"
                    value={sacrificeData.teslimSira2}
                    onChange={(e) => setSacrificeData(prev => ({ ...prev, teslimSira2: e.target.value }))}
                    placeholder="Sıra numarası"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid var(--light-gray)',
                      borderRadius: '5px',
                      fontSize: '16px',
                      textAlign: 'center'
                    }}
                  />
                </div>
                
                <div style={{
                  padding: '20px',
                  border: '2px solid var(--light-gray)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🍽️</div>
                  <h4 style={{ margin: '0 0 15px 0', color: 'var(--secondary-color)' }}>Teslim Sırası 3. Masa</h4>
                  <input
                    type="number"
                    value={sacrificeData.teslimSira3}
                    onChange={(e) => setSacrificeData(prev => ({ ...prev, teslimSira3: e.target.value }))}
                    placeholder="Sıra numarası"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid var(--light-gray)',
                      borderRadius: '5px',
                      fontSize: '16px',
                      textAlign: 'center'
                    }}
                  />
                </div>
                
                <div style={{
                  padding: '20px',
                  border: '2px solid var(--light-gray)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🍽️</div>
                  <h4 style={{ margin: '0 0 15px 0', color: 'var(--secondary-color)' }}>Teslim Sırası 4. Masa</h4>
                  <input
                    type="number"
                    value={sacrificeData.teslimSira4}
                    onChange={(e) => setSacrificeData(prev => ({ ...prev, teslimSira4: e.target.value }))}
                    placeholder="Sıra numarası"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid var(--light-gray)',
                      borderRadius: '5px',
                      fontSize: '16px',
                      textAlign: 'center'
                    }}
                  />
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                gap: '15px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <button 
                  onClick={() => setShowSacrificeModal(false)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: 'var(--light-gray)',
                    color: 'var(--secondary-color)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'var(--transition)'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#ddd'}
                  onMouseOut={(e) => e.target.style.background = 'var(--light-gray)'}
                >
                  Kapat
                </button>
                
                {isSacrificeModeActive && (
                  <button 
                    onClick={() => {
                      setIsSacrificeModeActive(false);
                      // LocalStorage'ı temizle
                      localStorage.removeItem('sacrificeMode');
                      alert('Kurban Modu deaktif edildi! Anasayfa eski haline döndü.');
                      setShowSacrificeModal(false);
                    }}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'var(--transition)'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#c82333'}
                    onMouseOut={(e) => e.target.style.background = '#dc3545'}
                  >
                    Modu Deaktif Et
                  </button>
                )}
                
                <button 
                  onClick={() => {
                    setIsSacrificeModeActive(true);
                    // LocalStorage'a kaydet
                    localStorage.setItem('sacrificeMode', JSON.stringify({
                      isActive: true,
                      data: sacrificeData
                    }));
                    alert('Kurban Modu aktif edildi! Anasayfada sıra bilgileri gösterilecek.');
                    setShowSacrificeModal(false);
                  }}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'var(--transition)'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#e67e00'}
                  onMouseOut={(e) => e.target.style.background = 'var(--primary-color)'}
                >
                  Modu Aktif Et
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Şifre Değiştir Modal */}
      <AnimatePresence>
        {showChangePasswordModal && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ModalContent
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              <ModalHeader>
                <ModalTitle>Şifre Değiştir</ModalTitle>
                <CloseModalButton onClick={() => setShowChangePasswordModal(false)}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>
              <Form onSubmit={handleChangePassword}>
                {changePasswordError && <ErrorMessage>{changePasswordError}</ErrorMessage>}
                {changePasswordSuccess && <SuccessBanner>{changePasswordSuccess}</SuccessBanner>}
                {changePasswordStep === 1 ? (
                  <FormGroup>
                    <FormLabel>Mevcut Şifre</FormLabel>
                    <FormInput
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={e => setCurrentPassword(e.target.value)}
                      required
                    />
                    <span onClick={() => setShowCurrentPassword(v => !v)} style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}>
                      {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </FormGroup>
                ) : (
                  <>
                    <FormGroup style={{ position: 'relative' }}>
                      <FormLabel>Yeni Şifre</FormLabel>
                      <FormInput
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        minLength={8}
                        required
                      />
                      <span onClick={() => setShowNewPassword(v => !v)} style={{ position: 'absolute', right: 12, top: 32, cursor: 'pointer' }}>
                        {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </FormGroup>
                    <FormGroup style={{ position: 'relative' }}>
                      <FormLabel>Yeni Şifre Tekrar</FormLabel>
                      <FormInput
                        type={showNewPasswordRepeat ? 'text' : 'password'}
                        value={newPasswordRepeat}
                        onChange={e => setNewPasswordRepeat(e.target.value)}
                        minLength={8}
                        required
                      />
                      <span onClick={() => setShowNewPasswordRepeat(v => !v)} style={{ position: 'absolute', right: 12, top: 32, cursor: 'pointer' }}>
                        {showNewPasswordRepeat ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </FormGroup>
                  </>
                )}
                <FormButtons>
                  <Button type="button" className="secondary" onClick={() => setShowChangePasswordModal(false)}>İptal</Button>
                  <Button type="submit" className="primary">{changePasswordStep === 1 ? 'Devam Et' : 'Şifreyi Değiştir'}</Button>
                </FormButtons>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* Kullanıcı Listesi Modalı (şık listview ile) */}
      {showUserListModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--white-color)',
            borderRadius: 'var(--border-radius)',
            padding: '30px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
              <ModalHeader>
                <ModalTitle>Kullanıcılar</ModalTitle>
                <CloseModalButton onClick={() => setShowUserListModal(false)}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>
              <div style={{ marginBottom: 20, textAlign: 'right' }}>
                <OrangeButton onClick={handleShowUserForm}>
                  <FaUserPlus style={{ marginRight: 6 }} /> Yeni Kullanıcı Ekle
                </OrangeButton>
              </div>
              {usersLoading ? (
                <div>Yükleniyor...</div>
              ) : usersError ? (
                <div style={{ color: 'red' }}>{usersError}</div>
              ) : (
                <UserList>
                  {users.map(u => (
                    <React.Fragment key={u.id}>
                      <UserListItem
                        onClick={() => handleUserRowClick(u.id)}
                        style={{ border: u.id === selectedUserId ? '2px solid #ff8c00' : undefined }}
                      >
                        <UserInfo>
                          <span style={{color:'#ff8c00', fontWeight:700}}>{u.id}</span>
                          <span>{u.first_name} {u.last_name}</span>
                          <span style={{color:'#888'}}>@{u.username}</span>
                        </UserInfo>
                        <UserMeta>
                          <span>{u.role}</span>
                          <span>{u.branch}</span>
                        </UserMeta>
                      </UserListItem>
                      {selectedUserId === u.id && (
                        <div style={{ display: 'flex', gap: 12, margin: '8px 0 16px 32px' }}>
                          <OrangeButton onClick={() => handleEditUser(u)}>
                            <FaEdit /> Düzenle
                          </OrangeButton>
                          <RedButton onClick={() => handleDeleteUser(u)}>
                            <FaTrash /> Sil
                          </RedButton>
                          <Button 
                            type="button" 
                            className="secondary" 
                            onClick={() => setSelectedUserId(null)}
                            style={{
                              background: 'var(--light-gray)',
                              color: 'var(--secondary-color)',
                              border: '1px solid var(--light-gray)',
                              padding: '8px 16px',
                              borderRadius: '5px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <FaTimes /> İptal
                          </Button>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </UserList>
              )}
            </div>
          </div>
        )}

      {/* Düzenle Modalı */}
      <AnimatePresence>
        {showEditUserModal && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ModalContent
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              <ModalHeader>
                <ModalTitle>Kullanıcıyı Düzenle</ModalTitle>
                <CloseModalButton onClick={() => setShowEditUserModal(false)}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>
              <Form onSubmit={handleEditUserSubmit}>
                <FormRow>
                  <FormGroup>
                    <FormLabel>Ad</FormLabel>
                    <FormInput value={editUserData?.first_name || ''} onChange={e => setEditUserData({...editUserData, first_name: e.target.value})} required />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Soyad</FormLabel>
                    <FormInput value={editUserData?.last_name || ''} onChange={e => setEditUserData({...editUserData, last_name: e.target.value})} required />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <FormLabel>Kullanıcı Adı</FormLabel>
                    <FormInput value={editUserData?.username || ''} onChange={e => setEditUserData({...editUserData, username: e.target.value})} required />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>E-posta</FormLabel>
                    <FormInput type="email" value={editUserData?.email || ''} onChange={e => setEditUserData({...editUserData, email: e.target.value})} required />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <FormLabel>Telefon</FormLabel>
                    <FormInput value={editUserData?.phone || ''} onChange={e => setEditUserData({...editUserData, phone: e.target.value})} />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Rol</FormLabel>
                    <select
                      value={editUserData?.role || ''}
                      onChange={e => setEditUserData({...editUserData, role: e.target.value})}
                      required
                      style={{ padding: '12px 15px', borderRadius: 6, border: '2px solid var(--light-gray)' }}
                    >
                      <option value="">Rol seçin</option>
                      <option value="admin">admin</option>
                      <option value="manager">manager</option>
                      <option value="staff">staff</option>
                    </select>
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <FormLabel>Kurum</FormLabel>
                    <select
                      value={editUserData?.branch || ''}
                      onChange={e => setEditUserData({...editUserData, branch: e.target.value})}
                      required
                      style={{ padding: '12px 15px', borderRadius: 6, border: '2px solid var(--light-gray)' }}
                    >
                      <option value="">Kurum seçin</option>
                      <option value="Süleymaniye">Süleymaniye</option>
                      <option value="Paşaköprüsü">Paşaköprüsü</option>
                      <option value="Bağlarbaşı">Bağlarbaşı</option>
                      <option value="Ferhatlar">Ferhatlar</option>
                      <option value="Fatih Kız">Fatih Kız</option>
                      <option value="Çamlıca">Çamlıca</option>
                      <option value="Şirinevler Kız">Şirinevler Kız</option>
                      <option value="Bahçelievler Kız">Bahçelievler Kız</option>
                      <option value="Çiftlik">Çiftlik</option>
                      <option value="merkez">merkez</option>
                    </select>
                  </FormGroup>
                </FormRow>
                {editUserError && <ErrorMessage>{editUserError}</ErrorMessage>}
                {editUserSuccess && <SuccessBanner>{editUserSuccess}</SuccessBanner>}
                <div style={{ textAlign: 'right', marginTop: 16 }}>
                  <OrangeButton type="submit" disabled={editUserLoading}>Kaydet</OrangeButton>
                </div>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* Hisse Satışı Hayvan Listesi Modalı */}
      <AnimatePresence>
        {showShareSaleListModal && (
          <Modal initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
            <LargeModalContent initial={{ scale: 0.8, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, y: 50 }} transition={{ duration: 0.3 }}>
              <ModalHeader>
                <ModalTitle>Satışta Olan Hayvanlar</ModalTitle>
                <CloseModalButton onClick={() => setShowShareSaleListModal(false)}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>
              {/* Filtreler */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="ID, Irk, Küpe No, Tür veya Cins ara..."
                  value={shareSaleSearch}
                  onChange={e => setShareSaleSearch(e.target.value)}
                  style={{ flex: 2, minWidth: 180, padding: 10, borderRadius: 6, border: '2px solid #eee', fontSize: 15 }}
                />
                <select value={shareSaleType} onChange={e => setShareSaleType(e.target.value)} style={{ padding: 10, borderRadius: 6, border: '2px solid #eee', fontSize: 15 }}>
                  <option value="all">Tüm Türler</option>
                  {uniqueOnsaleTypes.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
                <select value={shareSaleBreed} onChange={e => setShareSaleBreed(e.target.value)} style={{ padding: 10, borderRadius: 6, border: '2px solid #eee', fontSize: 15 }}>
                  <option value="all">Tüm Irklar</option>
                  {uniqueOnsaleBreeds.map(breed => <option key={breed} value={breed}>{breed}</option>)}
                </select>
                <select value={shareSaleGender} onChange={e => setShareSaleGender(e.target.value)} style={{ padding: 10, borderRadius: 6, border: '2px solid #eee', fontSize: 15 }}>
                  <option value="all">Tüm Cinsler</option>
                  {uniqueOnsaleGenders.map(gender => <option key={gender} value={gender}>{gender}</option>)}
                </select>
              </div>
              {/* Tablo */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px #eee', tableLayout: 'auto' }}>
                  <thead>
                    <tr style={{ background: '#ff8c00', color: '#fff', fontWeight: 700 }}>
                      <th style={{ padding: 12, border: '1px solid #f3f3f3', minWidth: 50, maxWidth: 60 }}>ID</th>
                      <th style={{ padding: 12, border: '1px solid #f3f3f3', minWidth: 80, maxWidth: 100 }}>Tür</th>
                      <th style={{ padding: 12, border: '1px solid #f3f3f3', minWidth: 80, maxWidth: 120 }}>Irk</th>
                      <th style={{ padding: 12, border: '1px solid #f3f3f3', minWidth: 80, maxWidth: 100 }}>Cins</th>
                      <th style={{ padding: 12, border: '1px solid #f3f3f3', minWidth: 100, maxWidth: 130 }}>
                        Küpe/<br />Hayvan No
                      </th>
                      <th style={{ padding: 12, border: '1px solid #f3f3f3', minWidth: 90, maxWidth: 120 }}>
                        Kilo
                      </th>
                      <th style={{ padding: 12, border: '1px solid #f3f3f3', minWidth: 90, maxWidth: 120 }}>
                        Giriş<br />Tarihi
                      </th>
                      <th style={{ padding: 12, border: '1px solid #f3f3f3', minWidth: 70, maxWidth: 90 }}>Durum</th>
                      <th style={{ padding: 12, border: '1px solid #f3f3f3', minWidth: 120, maxWidth: 180 }}>Alındığı Kişi/Firma</th>
                      <th style={{ padding: 12, border: '1px solid #f3f3f3', minWidth: 80, maxWidth: 120 }}>Notlar</th>
                      <th style={{ padding: 12, border: '1px solid #f3f3f3', minWidth: 120, maxWidth: 150 }}>
                        Satılabilir<br />Hisse
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOnsaleAnimals.length === 0 ? (
                      <tr><td colSpan={11} style={{ textAlign: 'center', padding: 32, color: '#e67e00' }}>Kriterlere uygun satışta hayvan bulunamadı.</td></tr>
                    ) : (
                      filteredOnsaleAnimals.map((animal, idx) => (
                        <React.Fragment key={animal.id}>
                          <tr 
                            style={{ 
                              background: selectedShareSaleAnimal?.id === animal.id ? '#fffbe6' : (idx % 2 === 0 ? '#fff7ed' : '#fff'), 
                              transition: 'background 0.2s',
                              cursor: 'pointer'
                            }}
                            onClick={() => setSelectedShareSaleAnimal(selectedShareSaleAnimal?.id === animal.id ? null : animal)}
                          >
                            <td style={{ padding: 10, border: '1px solid #f3f3f3', minWidth: 50, maxWidth: 60 }}>#{animal.id}</td>
                            <td style={{ padding: 10, border: '1px solid #f3f3f3', minWidth: 80, maxWidth: 100 }}>{animal.type}</td>
                            <td style={{ padding: 10, border: '1px solid #f3f3f3', minWidth: 80, maxWidth: 120 }}>{animal.breed}</td>
                            <td style={{ padding: 10, border: '1px solid #f3f3f3', minWidth: 80, maxWidth: 100 }}>{animal.gender}</td>
                            <td style={{ padding: 10, border: '1px solid #f3f3f3', minWidth: 100, maxWidth: 130 }}>{animal.ear_tag}</td>
                            <td style={{ padding: 10, border: '1px solid #f3f3f3', minWidth: 90, maxWidth: 120 }}>{animal.last_weight || animal.start_weight} kg</td>
                            <td style={{ padding: 10, border: '1px solid #f3f3f3', minWidth: 90, maxWidth: 120 }}>{animal.entry_date}</td>
                            <td style={{ padding: 10, border: '1px solid #f3f3f3', minWidth: 70, maxWidth: 90, color: '#e67e00', fontWeight: 600 }}>Satışta</td>
                            <td style={{ padding: 10, border: '1px solid #f3f3f3', minWidth: 120, maxWidth: 180 }}>{animal.supplier}</td>
                            <td style={{ padding: 10, border: '1px solid #f3f3f3', minWidth: 80, maxWidth: 120 }}>{animal.notes}</td>
                            <td style={{ padding: 10, border: '1px solid #f3f3f3', minWidth: 120, maxWidth: 150, textAlign: 'center' }}>
                              <div style={{ fontWeight: '600', color: '#e67e00' }}>
                                {animal.remaining_shares || animal.total_shares || (animal.type === 'Büyükbaş' ? 7 : 1)}
                              </div>
                              <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                / {animal.total_shares || (animal.type === 'Büyükbaş' ? 7 : 1)}
                              </div>
                            </td>
                          </tr>
                          {/* Seçili satırın altına butonlar */}
                          {selectedShareSaleAnimal?.id === animal.id && (
                            <tr>
                              <td colSpan={11} style={{ padding: '16px', background: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
                                  <button
                                    onClick={() => handleOpenShareSaleForm(animal)}
                                    style={{
                                      background: 'var(--primary-color)',
                                      color: 'white',
                                      border: 'none',
                                      padding: '10px 20px',
                                      borderRadius: '6px',
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#e67e00'}
                                    onMouseLeave={(e) => e.target.style.background = 'var(--primary-color)'}
                                  >
                                    <FaShoppingCart />
                                    Hisse Sat
                                  </button>
                                  <button
                                    onClick={() => setSelectedShareSaleAnimal(null)}
                                    style={{
                                      background: '#6c757d',
                                      color: 'white',
                                      border: 'none',
                                      padding: '8px 16px',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#5a6268'}
                                    onMouseLeave={(e) => e.target.style.background = '#6c757d'}
                                  >
                                    Gizle
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </LargeModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* Hisse Satış Formu Modalı */}
      <AnimatePresence>
        {showShareSaleForm && (
          <Modal initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
            <LargeModalContent initial={{ scale: 0.8, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, y: 50 }} transition={{ duration: 0.3 }}>
              <ModalHeader>
                <ModalTitle>Hisse Satış Formu</ModalTitle>
                <CloseModalButton onClick={() => setShowShareSaleForm(false)}>
                  <FaTimes />
                </CloseModalButton>
              </ModalHeader>
              
              <form onSubmit={handleShareSaleSubmit} style={{ padding: '20px 0' }}>
                {/* Hayvan Seçimi */}
                {!selectedShareSaleAnimal && (
                  <div style={{ background: '#fff3cd', padding: '20px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #ffeaa7' }}>
                    <h3 style={{ color: '#856404', marginBottom: '16px', fontSize: '18px' }}>
                      ⚠️ Hayvan Seçimi Gerekli
                    </h3>
                    <p style={{ color: '#856404', marginBottom: '16px' }}>
                      Hisse satışı yapmak için önce bir hayvan seçmeniz gerekiyor. Aşağıdaki listeden bir hayvan seçin:
                    </p>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '6px' }}>
                      {animals.filter(animal => animal.status === 'Satışta').map(animal => (
                        <div
                          key={animal.id}
                          onClick={() => setSelectedShareSaleAnimal(animal)}
                          style={{
                            padding: '12px',
                            borderBottom: '1px solid #eee',
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                        >
                          <strong>#{animal.id}</strong> - {animal.type} {animal.breed} ({animal.ear_tag})
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Seçilen Hayvanın Bilgileri */}
                {selectedShareSaleAnimal && (
                  <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '24px' }}>
                    <h3 style={{ color: 'var(--primary-color)', marginBottom: '16px', fontSize: '18px' }}>
                      🐄 Seçilen Hayvanın Bilgileri
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div><strong>ID:</strong> #{selectedShareSaleAnimal.id}</div>
                      <div><strong>Tür:</strong> {selectedShareSaleAnimal.type}</div>
                      <div><strong>Irk:</strong> {selectedShareSaleAnimal.breed}</div>
                      <div><strong>Cins:</strong> {selectedShareSaleAnimal.gender}</div>
                      <div><strong>Küpe/Hayvan No:</strong> {selectedShareSaleAnimal.ear_tag}</div>
                      <div><strong>Kilo:</strong> {selectedShareSaleAnimal.last_weight || selectedShareSaleAnimal.start_weight} kg</div>
                      <div><strong>Giriş Tarihi:</strong> {selectedShareSaleAnimal.entry_date}</div>
                      <div><strong>Durum:</strong> <span style={{ padding: '4px 8px', borderRadius: '12px', fontSize: '0.9rem', background: '#ffeeba', color: '#856404' }}>Satışta</span></div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedShareSaleAnimal(null);
                        setShowShareSaleListModal(true);
                      }}
                      style={{
                        marginTop: '16px',
                        padding: '8px 16px',
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Hayvan Seçimini Değiştir
                    </button>
                  </div>
                )}

                {/* Hisse Sahibi Bilgileri */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: 'var(--primary-color)', marginBottom: '16px', fontSize: '18px' }}>
                    👤 Hisse Sahibi Bilgileri
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Ad Soyad <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="customerName"
                        value={shareSaleFormData.customerName}
                        onChange={handleShareSaleInputChange}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${shareSaleFormErrors.customerName ? '#e74c3c' : '#ddd'}`,
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                        placeholder="Müşteri adı ve soyadı"
                      />
                      {shareSaleFormErrors.customerName && (
                        <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                          {shareSaleFormErrors.customerName}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Telefon <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="tel"
                        name="customerPhone"
                        value={shareSaleFormData.customerPhone}
                        onChange={handleShareSaleInputChange}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${shareSaleFormErrors.customerPhone ? '#e74c3c' : '#ddd'}`,
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                        placeholder="05xx xxx xx xx"
                        maxLength="11"
                      />
                      {shareSaleFormErrors.customerPhone && (
                        <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                          {shareSaleFormErrors.customerPhone}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Takip Eden ve Kurum Bilgileri */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: 'var(--primary-color)', marginBottom: '16px', fontSize: '18px' }}>
                    🏢 Takip Eden ve Kurum Bilgileri
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Takip Eden (Referans)
                      </label>
                      <input
                        type="text"
                        value={currentUserInfo ? `${currentUserInfo.first_name || ''} ${currentUserInfo.last_name || ''}` : 'Yükleniyor...'}
                        disabled
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          background: '#f8f9fa',
                          color: '#666'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Kurumu
                      </label>
                      <input
                        type="text"
                        value={currentUserInfo?.branch || 'Yükleniyor...'}
                        disabled
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          background: '#f8f9fa',
                          color: '#666'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Hisse Bilgileri */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: 'var(--primary-color)', marginBottom: '16px', fontSize: '18px' }}>
                    📊 Hisse Bilgileri
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Satılan Hisse Sayısı <span style={{ color: 'red' }}>*</span>
                      </label>
                      <select
                        name="shareCount"
                        value={shareSaleFormData.shareCount}
                        onChange={handleShareSaleInputChange}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${shareSaleFormErrors.shareCount ? '#e74c3c' : '#ddd'}`,
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        {(() => {
                          const maxShares = selectedShareSaleAnimal?.type === 'Büyükbaş' ? 7 : 1;
                          const currentSoldShares = selectedShareSaleAnimal?.sold_shares || 0;
                          const availableShares = maxShares - currentSoldShares;
                          
                          return Array.from({ length: Math.max(1, availableShares) }, (_, i) => i + 1)
                            .map(num => (
                              <option key={num} value={num}>{num}</option>
                            ));
                        })()}
                      </select>
                      {shareSaleFormErrors.shareCount && (
                        <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                          {shareSaleFormErrors.shareCount}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Hisse Başı Fiyat <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="pricePerShare"
                        value={shareSaleFormData.pricePerShare ? `₺${Number(shareSaleFormData.pricePerShare).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '₺0,00'}
                        onChange={(e) => {
                          // Sadece rakamları al ve decimal point'e izin ver
                          const value = e.target.value.replace(/[^\d,]/g, '').replace(',', '.');
                          if (value === '' || !isNaN(parseFloat(value))) {
                            handleShareSaleInputChange({
                              target: {
                                name: 'pricePerShare',
                                value: value
                              }
                            });
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${shareSaleFormErrors.pricePerShare ? '#e74c3c' : '#ddd'}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#333'
                        }}
                        placeholder="₺0,00"
                      />
                      {shareSaleFormErrors.pricePerShare && (
                        <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                          {shareSaleFormErrors.pricePerShare}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Toplam Tutar
                      </label>
                      <input
                        type="text"
                        value={`₺${Number(shareSaleFormData.totalAmount || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
                        disabled
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          background: '#f8f9fa',
                          color: '#666',
                          fontWeight: '600'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Ödeme Bilgileri */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: 'var(--primary-color)', marginBottom: '16px', fontSize: '18px' }}>
                    💰 Ödeme Bilgileri
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Ödeme Yöntemi <span style={{ color: 'red' }}>*</span>
                      </label>
                      <select
                        name="paymentMethod"
                        value={shareSaleFormData.paymentMethod}
                        onChange={handleShareSaleInputChange}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${shareSaleFormErrors.paymentMethod ? '#e74c3c' : '#ddd'}`,
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Seçiniz</option>
                        <option value="Nakit">Nakit</option>
                        <option value="Havale/EFT">Havale/EFT</option>
                      </select>
                      {shareSaleFormErrors.paymentMethod && (
                        <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                          {shareSaleFormErrors.paymentMethod}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Ödenen Tutar
                      </label>
                      <input
                        type="number"
                        name="paidAmount"
                        value={shareSaleFormData.paidAmount}
                        onChange={handleShareSaleInputChange}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Kalan Borç
                      </label>
                      <input
                        type="text"
                        value={`₺${Number(shareSaleFormData.remainingDebt || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
                        disabled
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          background: '#f8f9fa',
                          color: '#666',
                          fontWeight: '600'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Ödeme Tarihi <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="date"
                        name="paymentDate"
                        value={shareSaleFormData.paymentDate}
                        onChange={handleShareSaleInputChange}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${shareSaleFormErrors.paymentDate ? '#e74c3c' : '#ddd'}`,
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                      {shareSaleFormErrors.paymentDate && (
                        <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                          {shareSaleFormErrors.paymentDate}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Hata ve Başarı Mesajları */}
                {shareSaleSubmitError && (
                  <div style={{ background: '#f8d7da', color: '#721c24', padding: '12px', borderRadius: '6px', marginBottom: '16px', border: '1px solid #f5c6cb' }}>
                    {shareSaleSubmitError}
                  </div>
                )}
                {shareSaleSubmitSuccess && (
                  <div style={{ background: '#d4edda', color: '#155724', padding: '12px', borderRadius: '6px', marginBottom: '16px', border: '1px solid #c3e6cb' }}>
                    {shareSaleSubmitSuccess}
                  </div>
                )}

                {/* Butonlar */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
                  <button
                    type="submit"
                    disabled={shareSaleSubmitLoading}
                    style={{
                      background: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#e67e00'}
                    onMouseLeave={(e) => e.target.style.background = 'var(--primary-color)'}
                  >
                    {shareSaleSubmitLoading ? 'Kaydediliyor...' : 'Kaydet / Satışı Tamamla'}
                  </button>
                  <button
                    type="button"
                    onClick={handleShareSaleReset}
                    style={{
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#5a6268'}
                    onMouseLeave={(e) => e.target.style.background = '#6c757d'}
                  >
                    Temizle / Sıfırla
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowShareSaleForm(false)}
                    style={{
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#c82333'}
                    onMouseLeave={(e) => e.target.style.background = '#dc3545'}
                  >
                    İptal / Geri Dön
                  </button>
                </div>
              </form>
            </LargeModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* İçerik Planlayıcı Modal */}
      {showContentPlannerModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999
          }}
          onClick={() => setShowContentPlannerModal(false)}
        >
          <div
            style={{
              background: 'white',
              padding: '24px',
              borderRadius: '10px',
              maxWidth: '1280px',
              width: '96%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ margin: 0, fontSize: '1.6rem', color: '#333' }}>Icerik Planlayici</h2>
              <button
                onClick={() => setShowContentPlannerModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#333' }}
              >
                ✕
              </button>
            </div>
            <ContentPlannerSection />
          </div>
        </div>
      )}
        
        {/* Satış Raporu Modal */}
        {showSalesReportModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999
          }} onClick={() => setShowSalesReportModal(false)}>
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '1000px',
              width: '95%',
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'relative'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #eee' }}>
                <h2 style={{ fontSize: '1.8rem', fontWeight: '600', color: '#333', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  📊 Satış Raporu
                </h2>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                  {currentUser.role === 'Yönetici' && (
                    <button
                      onClick={exportToExcel}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      📄 Excel İndir
                    </button>
                  )}
                  <button 
                    onClick={() => setShowSalesReportModal(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      color: '#333',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                <strong>Kullanıcı:</strong> {currentUser.name} ({currentUser.role})<br/>
                <strong>Kurum:</strong> {currentUser.institution}<br/>
                <strong>Gösterilen Veri:</strong> {
                  currentUser.role === 'Personel' ? 'Sadece kendi satışlarınız' :
                  currentUser.role === 'Kurum Mesulü' ? 'Kurumunuza ait tüm satışlar' :
                  'Tüm satışlar'
                }
              </div>

              {salesReportLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#333', fontSize: '1.1rem', background: '#f8f9fa', borderRadius: '8px', marginTop: '20px' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '12px' }}>⏳</div>
                  <div>Satış listesi yükleniyor…</div>
                </div>
              ) : getFilteredSalesData().length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>ID</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Hisse Sahibi</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Hisse Adedi</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Aracı</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Kurum</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Fiyat (TL)</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Tarih</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Personel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredSalesData().map((sale) => (
                      <tr key={sale.id} style={{ transition: 'all 0.3s ease' }}>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.id}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.hisseSahibi}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.hisseAdedi}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.aracı}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.kurum}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.fiyat.toLocaleString('tr-TR')}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.tarih}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.personelAdi}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#333', fontSize: '1.1rem', background: '#f8f9fa', borderRadius: '8px', marginTop: '20px' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '15px', opacity: 0.5 }}>📊</div>
                  <div>Gösterilecek satış verisi bulunamadı.</div>
                  <div style={{ fontSize: '0.9rem', marginTop: '10px', opacity: 0.7 }}>
                    {currentUser.role === 'Personel' ? 'Henüz hiç satış yapmamışsınız.' :
                     currentUser.role === 'Kurum Mesulü' ? 'Kurumunuza ait satış bulunmuyor.' :
                     'Sistemde hiç satış kaydı bulunmuyor.'}
                  </div>
                </div>
              )}

              {!salesReportLoading && (
                <div style={{ marginTop: '20px', padding: '15px', background: '#e8f5e8', borderRadius: '8px', fontSize: '0.9rem' }}>
                  <strong>Toplam Satış:</strong> {getFilteredSalesData().length} adet<br/>
                  <strong>Toplam Tutar:</strong> {getFilteredSalesData().reduce((sum, sale) => sum + sale.fiyat, 0).toLocaleString('tr-TR')} TL
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tahsilatlar Modal */}
        {showTahsilatlarModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999
          }} onClick={() => {
            setShowTahsilatlarModal(false);
            // Tahsilatlar modalı kapandığında Tahsilat Ekle modalını da kapat
            setShowTahsilatEkleModal(false);
            setSelectedTahsilatId(null);
            setSelectedTahsilatData(null);
          }}>
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '1200px',
              width: '95%',
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'relative'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #eee' }}>
                <h2 style={{ fontSize: '1.8rem', fontWeight: '600', color: '#333', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  💰 Tahsilatlar
                </h2>
                <button 
                  onClick={() => {
                    setShowTahsilatlarModal(false);
                    // Tahsilatlar modalı kapandığında Tahsilat Ekle modalını da kapat
                    setShowTahsilatEkleModal(false);
                    setSelectedTahsilatId(null);
                    setSelectedTahsilatData(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    color: '#333',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                <strong>Kullanıcı:</strong> {currentUser.name} ({currentUser.role})<br/>
                <strong>Kurum:</strong> {currentUser.institution}<br/>
                <strong>Gösterilen Veri:</strong> {
                  currentUser.role === 'Personel' ? 'Sadece kendi aracı olduğunuz hisse sahipleri' :
                  currentUser.role === 'Kurum Mesulü' ? 'Kurumunuza ait tüm hisse sahipleri' :
                  'Tüm hisse sahipleri'
                }
              </div>

              {/* Arama Textbox */}
              <div style={{ marginBottom: '20px', padding: '15px', background: '#e8f5e8', borderRadius: '8px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                  🔍 Hisse Sahibi Ara:
                </label>
                <input
                  type="text"
                  value={tahsilatlarSearchTerm}
                  onChange={(e) => setTahsilatlarSearchTerm(e.target.value)}
                  placeholder="Hisse sahibi adını yazın..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    transition: 'border-color 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
                {tahsilatlarSearchTerm && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                    "{tahsilatlarSearchTerm}" için {getFilteredTahsilatlarData().length} sonuç bulundu
                  </div>
                )}
              </div>

              {tahsilatlarLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#333', fontSize: '1.1rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '15px' }}>⏳</div>
                  <div>Tahsilat verileri yükleniyor...</div>
                </div>
              ) : getFilteredTahsilatlarData().length > 0 ? (
                <>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>ID</th>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Hisse Sahibi</th>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Aracı Olan</th>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Toplam Bakiye (TL)</th>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Ödeme Miktarı (TL)</th>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Kalan Bakiye (TL)</th>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredTahsilatlarData().map((tahsilat) => (
                        <React.Fragment key={tahsilat.id}>
                          <tr 
                            onClick={() => handleTahsilatSelect(tahsilat.id)}
                            style={{ 
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                              backgroundColor: selectedTahsilatId === tahsilat.id ? '#e3f2fd' : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedTahsilatId !== tahsilat.id) {
                                e.target.style.backgroundColor = '#f5f5f5';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedTahsilatId !== tahsilat.id) {
                                e.target.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{tahsilat.id}</td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{tahsilat.hisse_sahibi}</td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{tahsilat.araci_adi || 'Belirtilmemiş'}</td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{tahsilat.toplam_bakiye.toLocaleString('tr-TR')}</td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{tahsilat.odeme_miktari.toLocaleString('tr-TR')}</td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{tahsilat.kalan_bakiye.toLocaleString('tr-TR')}</td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                background: tahsilat.kalan_bakiye === 0 ? '#d4edda' : tahsilat.kalan_bakiye < tahsilat.toplam_bakiye * 0.5 ? '#fff3cd' : '#f8d7da',
                                color: tahsilat.kalan_bakiye === 0 ? '#155724' : tahsilat.kalan_bakiye < tahsilat.toplam_bakiye * 0.5 ? '#856404' : '#721c24'
                              }}>
                                {tahsilat.kalan_bakiye === 0 ? 'Tamamlandı' : tahsilat.kalan_bakiye < tahsilat.toplam_bakiye * 0.5 ? 'Kısmi Ödeme' : 'Beklemede'}
                              </span>
                            </td>
                          </tr>
                          {selectedTahsilatId === tahsilat.id && (
                            <tr>
                              <td colSpan="7" style={{ padding: '15px', border: '1px solid #ddd', backgroundColor: '#f8f9fa' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTahsilatEkle();
                                    }}
                                    style={{
                                      padding: '8px 16px',
                                      backgroundColor: '#28a745',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      transition: 'all 0.3s ease',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '5px'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
                                  >
                                    💰 Tahsilat Ekle
                                  </button>
                                  <span style={{ fontSize: '12px', color: '#666' }}>
                                    Seçili: {tahsilat.hisse_sahibi} - Kalan Bakiye: {tahsilat.kalan_bakiye.toLocaleString('tr-TR')} TL
                                  </span>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ marginTop: '20px', padding: '15px', background: '#e8f5e8', borderRadius: '8px', fontSize: '0.9rem' }}>
                    <strong>Toplam Hisse Sahibi:</strong> {getFilteredTahsilatlarData().length} kişi<br/>
                    <strong>Toplam Bakiye:</strong> {getFilteredTahsilatlarData().reduce((sum, t) => sum + t.toplam_bakiye, 0).toLocaleString('tr-TR')} TL<br/>
                    <strong>Toplam Ödeme:</strong> {getFilteredTahsilatlarData().reduce((sum, t) => sum + t.odeme_miktari, 0).toLocaleString('tr-TR')} TL<br/>
                    <strong>Kalan Bakiye:</strong> {getFilteredTahsilatlarData().reduce((sum, t) => sum + t.kalan_bakiye, 0).toLocaleString('tr-TR')} TL
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#333', fontSize: '1.1rem', background: '#f8f9fa', borderRadius: '8px', marginTop: '20px' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '15px', opacity: 0.5 }}>💰</div>
                  <div>
                    {tahsilatlarSearchTerm ? 
                      `"${tahsilatlarSearchTerm}" için sonuç bulunamadı.` : 
                      'Gösterilecek tahsilat verisi bulunamadı.'
                    }
                  </div>
                  <div style={{ fontSize: '0.9rem', marginTop: '10px', opacity: 0.7 }}>
                    {tahsilatlarSearchTerm ? 
                      'Farklı bir arama terimi deneyin.' :
                      currentUser.role === 'Personel' ? 'Henüz hiç hisse sahibi bulunmuyor.' :
                      currentUser.role === 'Kurum Mesulü' ? 'Kurumunuza ait hisse sahibi bulunmuyor.' :
                      'Sistemde hiç hisse sahibi kaydı bulunmuyor.'
                    }
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Personel Raporları Modal */}
        {showPersonelRaporlariModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              console.log('Modal dışı tıklandı, modal kapatılıyor');
              setShowPersonelRaporlariModal(false);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100000,
              padding: '20px'
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                console.log('Modal içeriği tıklandı');
              }}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '1400px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                position: 'relative',
                zIndex: 100001
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                <h2 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '700', color: '#333', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FaChartBar />
                  Personel Raporları
                </h2>
                <button
                  onClick={() => setShowPersonelRaporlariModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '5px',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f5f5f5';
                    e.target.style.color = '#333';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#666';
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Filtreler */}
              <div style={{ 
                marginBottom: '25px', 
                padding: '20px', 
                background: '#f8f9fa', 
                borderRadius: '8px',
                display: 'grid',
                gridTemplateColumns: currentUser.role === 'Yönetici' || currentUser.role === 'admin' ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr',
                gap: '15px'
              }}>
                {/* Kullanıcı Filtresi */}
                {(currentUser.role === 'Kurum Mesulü' || currentUser.role === 'Yönetici' || currentUser.role === 'admin') && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                      👤 Personel:
                    </label>
                    <select
                      value={personelRaporlariFilters.userId}
                      onChange={(e) => setPersonelRaporlariFilters({...personelRaporlariFilters, userId: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'border-color 0.3s ease',
                        outline: 'none'
                      }}
                    >
                      <option value="">Tümü</option>
                      {availableUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.kurum})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Kurum Filtresi (Sadece Admin/Yönetici) */}
                {(currentUser.role === 'Yönetici' || currentUser.role === 'admin') && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                      🏢 Kurum:
                    </label>
                    <select
                      value={personelRaporlariFilters.branch}
                      onChange={(e) => setPersonelRaporlariFilters({...personelRaporlariFilters, branch: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'border-color 0.3s ease',
                        outline: 'none'
                      }}
                    >
                      <option value="">Tümü</option>
                      {availableBranches.map(branch => (
                        <option key={branch} value={branch}>
                          {branch}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Başlangıç Tarihi */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                    📅 Başlangıç Tarihi:
                  </label>
                  <input
                    type="date"
                    value={personelRaporlariFilters.startDate}
                    onChange={(e) => setPersonelRaporlariFilters({...personelRaporlariFilters, startDate: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      transition: 'border-color 0.3s ease',
                      outline: 'none'
                    }}
                  />
                </div>
                
                {/* Bitiş Tarihi */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                    📅 Bitiş Tarihi:
                  </label>
                  <input
                    type="date"
                    value={personelRaporlariFilters.endDate}
                    onChange={(e) => setPersonelRaporlariFilters({...personelRaporlariFilters, endDate: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      transition: 'border-color 0.3s ease',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Rapor Tablosu */}
              {personelRaporlariLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#333', fontSize: '1.1rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '15px' }}>⏳</div>
                  <div>Rapor verileri yükleniyor...</div>
                </div>
              ) : personelRaporlariData.length > 0 ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      Toplam {personelRaporlariData.length} işlem bulundu
                    </div>
                    <button
                      onClick={() => {
                        // Excel export fonksiyonu
                        const exportToExcel = () => {
                          try {
                            // Excel için veri hazırla
                            const excelData = personelRaporlariData.map((report, index) => ({
                              'Sıra No': index + 1,
                              'Tarih': new Date(report.tarih).toLocaleDateString('tr-TR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              }),
                              'İşlem Tipi': report.islem_tipi === 'hisse_satisi' ? 'Hisse Satışı' : 'Tahsilat',
                              'Personel': report.personel_adi || 'Belirtilmemiş',
                              'Kurum': report.kurum || 'Belirtilmemiş',
                              'Hisse Sahibi': report.musteri_adi || 'Belirtilmemiş',
                              'Tutar (₺)': report.tutar ? report.tutar.toFixed(2) : '0.00',
                              'Hisse Adedi': report.hisse_adedi || '-',
                              'Hayvan Bilgisi': report.hayvan_bilgisi || '-',
                              'Açıklama': report.aciklama || '-'
                            }));

                            // Excel dosyası oluştur
                            const worksheet = XLSX.utils.json_to_sheet(excelData);
                            const workbook = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(workbook, worksheet, 'Personel Raporları');

                            // Dosya adını oluştur (tarih ve filtre bilgisi ile)
                            const now = new Date();
                            const dateStr = now.toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\./g, '-');
                            let fileName = `Personel_Raporlari_${dateStr}`;
                            
                            // Filtre bilgilerini dosya adına ekle
                            if (personelRaporlariFilters.userId) {
                              const selectedUser = availableUsers.find(u => u.id === parseInt(personelRaporlariFilters.userId));
                              if (selectedUser) {
                                fileName += `_${selectedUser.name.replace(/\s+/g, '_')}`;
                              }
                            }
                            if (personelRaporlariFilters.branch) {
                              fileName += `_${personelRaporlariFilters.branch.replace(/\s+/g, '_')}`;
                            }
                            if (personelRaporlariFilters.startDate && personelRaporlariFilters.endDate) {
                              fileName += `_${personelRaporlariFilters.startDate}_${personelRaporlariFilters.endDate}`;
                            }
                            
                            fileName += '.xlsx';

                            // Dosyayı indir
                            XLSX.writeFile(workbook, fileName);
                            alert(`✅ Excel dosyası başarıyla indirildi!\n\nDosya Adı: ${fileName}\nToplam ${personelRaporlariData.length} kayıt`);
                          } catch (error) {
                            console.error('Excel export hatası:', error);
                            alert('❌ Excel dosyası oluşturulurken bir hata oluştu!');
                          }
                        };
                        
                        exportToExcel();
                      }}
                      style={{
                        padding: '10px 20px',
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#218838';
                        e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#28a745';
                        e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                    >
                      <FaDownload />
                      Excel Olarak İndir
                    </button>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', minWidth: '1000px' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Tarih</th>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>İşlem Tipi</th>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Personel</th>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Kurum</th>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Hisse Sahibi</th>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Tutar (₺)</th>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Açıklama</th>
                        </tr>
                      </thead>
                      <tbody>
                        {personelRaporlariData.map((report, index) => (
                          <tr 
                            key={`${report.islem_tipi}-${report.id}-${index}`}
                            style={{ 
                              backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#e8f4f8';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9f9f9';
                            }}
                          >
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px' }}>
                              {new Date(report.tarih).toLocaleDateString('tr-TR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px' }}>
                              <span style={{
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                background: report.islem_tipi === 'hisse_satisi' ? '#d4edda' : '#cce5ff',
                                color: report.islem_tipi === 'hisse_satisi' ? '#155724' : '#004085'
                              }}>
                                {report.islem_tipi === 'hisse_satisi' ? '📊 Hisse Satışı' : '💰 Tahsilat'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px' }}>
                              {report.personel_adi || 'Belirtilmemiş'}
                            </td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px' }}>
                              {report.kurum || 'Belirtilmemiş'}
                            </td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px' }}>
                              {report.musteri_adi || 'Belirtilmemiş'}
                            </td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px', fontWeight: '600', color: '#28a745' }}>
                              {report.tutar ? report.tutar.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'} ₺
                            </td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px' }}>
                              {report.aciklama || '-'}
                              {report.hayvan_bilgisi && (
                                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                                  🐄 {report.hayvan_bilgisi}
                                </div>
                              )}
                              {report.hisse_adedi && (
                                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                                  📦 {report.hisse_adedi} adet hisse
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999', fontSize: '1.1rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '15px' }}>📊</div>
                  <div>Seçilen filtreler için işlem bulunamadı.</div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}

        {/* Kurum Raporları Modal */}
        {showKurumRaporlariModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              console.log('Modal dışı tıklandı, modal kapatılıyor');
              setShowKurumRaporlariModal(false);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100000,
              padding: '20px'
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                console.log('Modal içeriği tıklandı');
              }}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '1400px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                position: 'relative',
                zIndex: 100001
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                <h2 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '700', color: '#333', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FaUsers />
                  Kurum Raporları
                </h2>
                <button
                  onClick={() => setShowKurumRaporlariModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '5px',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f5f5f5';
                    e.target.style.color = '#333';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#666';
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Filtreler */}
              <div style={{ 
                marginBottom: '25px', 
                padding: '20px', 
                background: '#f8f9fa', 
                borderRadius: '8px',
                display: 'grid',
                gridTemplateColumns: currentUser.role === 'Yönetici' || currentUser.role === 'admin' ? '1fr 1fr 1fr' : '1fr 1fr',
                gap: '15px'
              }}>
                {/* Kurum Filtresi (Sadece Admin/Yönetici) */}
                {(currentUser.role === 'Yönetici' || currentUser.role === 'admin') && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                      🏢 Kurum:
                    </label>
                    <select
                      value={kurumRaporlariFilters.branch}
                      onChange={(e) => setKurumRaporlariFilters({...kurumRaporlariFilters, branch: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'border-color 0.3s ease',
                        outline: 'none'
                      }}
                    >
                      <option value="">Tümü</option>
                      {availableBranchesForKurum.map(branch => (
                        <option key={branch} value={branch}>
                          {branch}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Başlangıç Tarihi */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                    📅 Başlangıç Tarihi:
                  </label>
                  <input
                    type="date"
                    value={kurumRaporlariFilters.startDate}
                    onChange={(e) => setKurumRaporlariFilters({...kurumRaporlariFilters, startDate: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      transition: 'border-color 0.3s ease',
                      outline: 'none'
                    }}
                  />
                </div>
                
                {/* Bitiş Tarihi */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                    📅 Bitiş Tarihi:
                  </label>
                  <input
                    type="date"
                    value={kurumRaporlariFilters.endDate}
                    onChange={(e) => setKurumRaporlariFilters({...kurumRaporlariFilters, endDate: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      transition: 'border-color 0.3s ease',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Rapor Tablosu */}
              {kurumRaporlariLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#333', fontSize: '1.1rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '15px' }}>⏳</div>
                  <div>Rapor verileri yükleniyor...</div>
                </div>
              ) : kurumRaporlariData.length > 0 ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      Toplam {kurumRaporlariData.length} işlem bulundu
                    </div>
                    <button
                      onClick={() => {
                        // Excel export fonksiyonu
                        const exportToExcel = () => {
                          try {
                            // Excel için veri hazırla
                            const excelData = kurumRaporlariData.map((report, index) => ({
                              'Sıra No': index + 1,
                              'Tarih': new Date(report.tarih).toLocaleDateString('tr-TR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              }),
                              'İşlem Tipi': report.islem_tipi === 'hisse_satisi' ? 'Hisse Satışı' : 'Tahsilat',
                              'Personel': report.personel_adi || 'Belirtilmemiş',
                              'Kurum': report.kurum || 'Belirtilmemiş',
                              'Hisse Sahibi': report.musteri_adi || 'Belirtilmemiş',
                              'Tutar (₺)': report.tutar ? report.tutar.toFixed(2) : '0.00',
                              'Hisse Adedi': report.hisse_adedi || '-',
                              'Hayvan Bilgisi': report.hayvan_bilgisi || '-',
                              'Açıklama': report.aciklama || '-'
                            }));

                            // Excel dosyası oluştur
                            const worksheet = XLSX.utils.json_to_sheet(excelData);
                            const workbook = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(workbook, worksheet, 'Kurum Raporları');

                            // Dosya adını oluştur (tarih ve filtre bilgisi ile)
                            const now = new Date();
                            const dateStr = now.toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\./g, '-');
                            let fileName = `Kurum_Raporlari_${dateStr}`;
                            
                            // Filtre bilgilerini dosya adına ekle
                            if (kurumRaporlariFilters.branch) {
                              fileName += `_${kurumRaporlariFilters.branch.replace(/\s+/g, '_')}`;
                            }
                            if (kurumRaporlariFilters.startDate && kurumRaporlariFilters.endDate) {
                              fileName += `_${kurumRaporlariFilters.startDate}_${kurumRaporlariFilters.endDate}`;
                            }
                            
                            fileName += '.xlsx';

                            // Dosyayı indir
                            XLSX.writeFile(workbook, fileName);
                            alert(`✅ Excel dosyası başarıyla indirildi!\n\nDosya Adı: ${fileName}\nToplam ${kurumRaporlariData.length} kayıt`);
                          } catch (error) {
                            console.error('Excel export hatası:', error);
                            alert('❌ Excel dosyası oluşturulurken bir hata oluştu!');
                          }
                        };
                        
                        exportToExcel();
                      }}
                      style={{
                        padding: '10px 20px',
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#218838';
                        e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#28a745';
                        e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                    >
                      <FaDownload />
                      Excel Olarak İndir
                    </button>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', minWidth: '1000px' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Tarih</th>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>İşlem Tipi</th>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Personel</th>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Kurum</th>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Hisse Sahibi</th>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Tutar (₺)</th>
                          <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Açıklama</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kurumRaporlariData.map((report, index) => (
                          <tr 
                            key={`${report.islem_tipi}-${report.id}-${index}`}
                            style={{ 
                              backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#e8f4f8';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9f9f9';
                            }}
                          >
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px' }}>
                              {new Date(report.tarih).toLocaleDateString('tr-TR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px' }}>
                              <span style={{
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                background: report.islem_tipi === 'hisse_satisi' ? '#d4edda' : '#cce5ff',
                                color: report.islem_tipi === 'hisse_satisi' ? '#155724' : '#004085'
                              }}>
                                {report.islem_tipi === 'hisse_satisi' ? '📊 Hisse Satışı' : '💰 Tahsilat'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px' }}>
                              {report.personel_adi || 'Belirtilmemiş'}
                            </td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px' }}>
                              {report.kurum || 'Belirtilmemiş'}
                            </td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px' }}>
                              {report.musteri_adi || 'Belirtilmemiş'}
                            </td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px', fontWeight: '600', color: '#28a745' }}>
                              {report.tutar ? report.tutar.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'} ₺
                            </td>
                            <td style={{ padding: '12px 10px', border: '1px solid #ddd', fontSize: '14px' }}>
                              {report.aciklama || '-'}
                              {report.hayvan_bilgisi && (
                                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                                  🐄 {report.hayvan_bilgisi}
                                </div>
                              )}
                              {report.hisse_adedi && (
                                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                                  📦 {report.hisse_adedi} adet hisse
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999', fontSize: '1.1rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '15px' }}>📊</div>
                  <div>Seçilen filtreler için işlem bulunamadı.</div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}

        {/* Tahsilat Ekle Modal */}
        {showTahsilatEkleModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100000,
              padding: '20px'
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '800px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                <h2 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '700', color: '#333', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  💰 Tahsilat Ekle
                </h2>
                <button
                  onClick={() => setShowTahsilatEkleModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '5px',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f5f5f5';
                    e.target.style.color = '#333';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#666';
                  }}
                >
                  ✕
                </button>
              </div>

              {selectedTahsilatData && (
                <>
                  {/* Hisse Sahibi Bilgileri */}
                  <div style={{ marginBottom: '25px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ margin: '0 0 15px 0', fontSize: '1.2rem', color: '#333' }}>📋 Hisse Sahibi Bilgileri</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div>
                        <strong>Ad Soyad:</strong> {selectedTahsilatData.hisse_sahibi}
                      </div>
                      <div>
                        <strong>Aracı:</strong> {selectedTahsilatData.araci_adi || 'Belirtilmemiş'}
                      </div>
                      <div>
                        <strong>Toplam Bakiye:</strong> {selectedTahsilatData.toplam_bakiye.toLocaleString('tr-TR')} TL
                      </div>
                      <div>
                        <strong>Ödenen Miktar:</strong> {selectedTahsilatData.odeme_miktari.toLocaleString('tr-TR')} TL
                      </div>
                      <div>
                        <strong>Kalan Bakiye:</strong> {selectedTahsilatData.kalan_bakiye.toLocaleString('tr-TR')} TL
                      </div>
                      <div>
                        <strong>Durum:</strong> 
                        <span style={{
                          marginLeft: '8px',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          background: selectedTahsilatData.kalan_bakiye === 0 ? '#d4edda' : selectedTahsilatData.kalan_bakiye < selectedTahsilatData.toplam_bakiye * 0.5 ? '#fff3cd' : '#f8d7da',
                          color: selectedTahsilatData.kalan_bakiye === 0 ? '#155724' : selectedTahsilatData.kalan_bakiye < selectedTahsilatData.toplam_bakiye * 0.5 ? '#856404' : '#721c24'
                        }}>
                          {selectedTahsilatData.kalan_bakiye === 0 ? 'Tamamlandı' : selectedTahsilatData.kalan_bakiye < selectedTahsilatData.toplam_bakiye * 0.5 ? 'Kısmi Ödeme' : 'Beklemede'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Tahsilat Formu */}
                  <div style={{ marginBottom: '25px', padding: '20px', background: '#e8f5e8', borderRadius: '8px' }}>
                    <h3 style={{ margin: '0 0 15px 0', fontSize: '1.2rem', color: '#333' }}>💰 Yeni Tahsilat</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                          📅 Tahsilat Tarihi:
                        </label>
                        <input
                          type="date"
                          value={tahsilatEkleFormData.tahsilatTarihi}
                          onChange={(e) => setTahsilatEkleFormData({...tahsilatEkleFormData, tahsilatTarihi: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '2px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            transition: 'border-color 0.3s ease',
                            outline: 'none'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                          onBlur={(e) => e.target.style.borderColor = '#ddd'}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                          💵 Tahsilat Miktarı (₺):
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max={selectedTahsilatData.kalan_bakiye}
                          value={tahsilatEkleFormData.tahsilatMiktari}
                          onChange={(e) => setTahsilatEkleFormData({...tahsilatEkleFormData, tahsilatMiktari: e.target.value})}
                          placeholder={`Maksimum: ${selectedTahsilatData.kalan_bakiye.toLocaleString('tr-TR')} TL`}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '2px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            transition: 'border-color 0.3s ease',
                            outline: 'none'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                          onBlur={(e) => e.target.style.borderColor = '#ddd'}
                        />
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                          Kalan bakiye: {selectedTahsilatData.kalan_bakiye.toLocaleString('tr-TR')} TL
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                          🏦 Tahsilat Yolu:
                        </label>
                        <select
                          value={tahsilatEkleFormData.tahsilatYolu}
                          onChange={(e) => setTahsilatEkleFormData({...tahsilatEkleFormData, tahsilatYolu: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '2px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            transition: 'border-color 0.3s ease',
                            outline: 'none',
                            backgroundColor: 'white',
                            cursor: 'pointer'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                          onBlur={(e) => e.target.style.borderColor = '#ddd'}
                        >
                          <option value="Nakit">💵 Nakit</option>
                          <option value="EFT/Havale">🏦 EFT/Havale</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Butonlar */}
                  <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowTahsilatEkleModal(false)}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleTahsilatKaydet}
                      disabled={!tahsilatEkleFormData.tahsilatMiktari || parseFloat(tahsilatEkleFormData.tahsilatMiktari) <= 0}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: !tahsilatEkleFormData.tahsilatMiktari || parseFloat(tahsilatEkleFormData.tahsilatMiktari) <= 0 ? '#ccc' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: !tahsilatEkleFormData.tahsilatMiktari || parseFloat(tahsilatEkleFormData.tahsilatMiktari) <= 0 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (tahsilatEkleFormData.tahsilatMiktari && parseFloat(tahsilatEkleFormData.tahsilatMiktari) > 0) {
                          e.target.style.backgroundColor = '#218838';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (tahsilatEkleFormData.tahsilatMiktari && parseFloat(tahsilatEkleFormData.tahsilatMiktari) > 0) {
                          e.target.style.backgroundColor = '#28a745';
                        }
                      }}
                    >
                      💾 Tahsilatı Kaydet
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
        {/* Gider Ekle Modal */}
        <AnimatePresence>
          {showGiderEkleModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowGiderEkleModal(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100000,
                padding: '20px'
              }}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '30px',
                  maxWidth: '800px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'auto',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                  <h2 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '700', color: '#333', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FaReceipt />
                    Gider Ekle
                  </h2>
                  <button
                    onClick={() => setShowGiderEkleModal(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      color: '#666',
                      padding: '5px',
                      borderRadius: '50%',
                      width: '35px',
                      height: '35px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f5f5f5';
                      e.target.style.color = '#333';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#666';
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Gider Başlığı */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                      📝 Gider Başlığı <span style={{ color: 'red' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={giderFormData.title}
                      onChange={(e) => setGiderFormData({...giderFormData, title: e.target.value})}
                      placeholder="Örn: Yem alımı, Veteriner masrafı..."
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `2px solid ${giderFormErrors.title ? '#dc3545' : '#ddd'}`,
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'border-color 0.3s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                      onBlur={(e) => e.target.style.borderColor = giderFormErrors.title ? '#dc3545' : '#ddd'}
                    />
                    {giderFormErrors.title && (
                      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{giderFormErrors.title}</div>
                    )}
                  </div>

                  {/* Kategori */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                      🏷️ Kategori <span style={{ color: 'red' }}>*</span>
                    </label>
                    <select
                      value={giderFormData.category}
                      onChange={(e) => setGiderFormData({...giderFormData, category: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `2px solid ${giderFormErrors.category ? '#dc3545' : '#ddd'}`,
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'border-color 0.3s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                      onBlur={(e) => e.target.style.borderColor = giderFormErrors.category ? '#dc3545' : '#ddd'}
                    >
                      <option value="Yem">Yem</option>
                      <option value="Hayvan Alımı">Hayvan Alımı</option>
                      <option value="Veteriner">Veteriner</option>
                      <option value="Nakliye">Nakliye</option>
                      <option value="Elektrik">Elektrik</option>
                      <option value="Personel">Personel</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                    {giderFormErrors.category && (
                      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{giderFormErrors.category}</div>
                    )}
                  </div>

                  {/* Tutar ve Tarih */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                        💰 Tutar (₺) <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={giderFormData.amount}
                        onChange={(e) => setGiderFormData({...giderFormData, amount: e.target.value})}
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${giderFormErrors.amount ? '#dc3545' : '#ddd'}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          transition: 'border-color 0.3s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                        onBlur={(e) => e.target.style.borderColor = giderFormErrors.amount ? '#dc3545' : '#ddd'}
                      />
                      {giderFormErrors.amount && (
                        <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{giderFormErrors.amount}</div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                        📅 Tarih <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="date"
                        value={giderFormData.expense_date}
                        onChange={(e) => setGiderFormData({...giderFormData, expense_date: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${giderFormErrors.expense_date ? '#dc3545' : '#ddd'}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          transition: 'border-color 0.3s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                        onBlur={(e) => e.target.style.borderColor = giderFormErrors.expense_date ? '#dc3545' : '#ddd'}
                      />
                      {giderFormErrors.expense_date && (
                        <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{giderFormErrors.expense_date}</div>
                      )}
                    </div>
                  </div>

                  {/* Ödeme Türü ve Kurum */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                        💳 Ödeme Türü <span style={{ color: 'red' }}>*</span>
                      </label>
                      <select
                        value={giderFormData.payment_method}
                        onChange={(e) => setGiderFormData({...giderFormData, payment_method: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${giderFormErrors.payment_method ? '#dc3545' : '#ddd'}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          transition: 'border-color 0.3s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                        onBlur={(e) => e.target.style.borderColor = giderFormErrors.payment_method ? '#dc3545' : '#ddd'}
                      >
                        <option value="Nakit">Nakit</option>
                        <option value="Havale/EFT">Havale/EFT</option>
                        <option value="Kredi Kartı">Kredi Kartı</option>
                      </select>
                      {giderFormErrors.payment_method && (
                        <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{giderFormErrors.payment_method}</div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                        🏢 Kurum <span style={{ color: 'red' }}>*</span>
                      </label>
                      <select
                        value={giderFormData.branch}
                        onChange={(e) => setGiderFormData({...giderFormData, branch: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${giderFormErrors.branch ? '#dc3545' : '#ddd'}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          transition: 'border-color 0.3s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                        onBlur={(e) => e.target.style.borderColor = giderFormErrors.branch ? '#dc3545' : '#ddd'}
                      >
                        <option value="">Kurum Seçiniz</option>
                        {availableBranchesForGider.map(branch => (
                          <option key={branch} value={branch}>
                            {branch}
                          </option>
                        ))}
                      </select>
                      {giderFormErrors.branch && (
                        <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{giderFormErrors.branch}</div>
                      )}
                    </div>
                  </div>

                  {/* Açıklama */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                      📄 Açıklama
                    </label>
                    <textarea
                      value={giderFormData.description}
                      onChange={(e) => setGiderFormData({...giderFormData, description: e.target.value})}
                      placeholder="Gider hakkında ek bilgiler..."
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'border-color 0.3s ease',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                      onBlur={(e) => e.target.style.borderColor = '#ddd'}
                    />
                  </div>

                  {/* Belge / Fatura */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                      📎 Belge / Fatura
                    </label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        type="file"
                        id="gider-document-input"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 10 * 1024 * 1024) {
                              alert('Dosya boyutu 10MB\'dan büyük olamaz!');
                              return;
                            }
                            setGiderFormData({...giderFormData, document: file});
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                      <label
                        htmlFor="gider-document-input"
                        style={{
                          padding: '10px 20px',
                          background: '#f8f9fa',
                          border: '2px dashed #ddd',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.3s ease',
                          flex: 1
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#ff6b35';
                          e.currentTarget.style.background = '#fff5f2';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#ddd';
                          e.currentTarget.style.background = '#f8f9fa';
                        }}
                      >
                        <FaFileInvoice />
                        {giderFormData.document ? giderFormData.document.name : 'Dosya Seç'}
                      </label>
                      {giderFormData.document && (
                        <button
                          type="button"
                          onClick={() => setGiderFormData({...giderFormData, document: null})}
                          style={{
                            padding: '10px 15px',
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      PDF, resim veya ofis dosyaları (Max: 10MB)
                    </div>
                  </div>

                  {/* İşlemi Yapan Personel */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                      👤 İşlemi Yapan Personel
                    </label>
                    <input
                      type="text"
                      value={currentUser.name || 'Sistem'}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: '#f8f9fa',
                        cursor: 'not-allowed'
                      }}
                    />
                  </div>

                  {/* Butonlar */}
                  <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
                    <button
                      type="button"
                      onClick={handleAddExpense}
                      style={{
                        flex: 1,
                        padding: '14px 20px',
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#218838';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#28a745';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      ✅ Kaydet
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowGiderEkleModal(false);
                        setGiderFormErrors({});
                      }}
                      style={{
                        flex: 1,
                        padding: '14px 20px',
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#5a6268';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#6c757d';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      ❌ İptal
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Kasa Raporları Modal */}
        <AnimatePresence>
          {showKasaRaporlariModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100001,
                padding: '20px'
              }}
              onClick={() => setShowKasaRaporlariModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '30px',
                  maxWidth: '1200px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                }}
              >
                {/* Modal Header */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '25px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #eee'
                }}>
                  <h2 style={{ 
                    margin: 0, 
                    color: '#333',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    💰 Kasa Raporları
                  </h2>
                  <button
                    onClick={() => setShowKasaRaporlariModal(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: '#999',
                      padding: '5px 10px',
                      borderRadius: '50%',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f0f0f0';
                      e.target.style.color = '#333';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#999';
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Excel İndir Butonu */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginBottom: '25px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px'
                }}>
                  <button
                    onClick={() => {
                      if (!kasaRaporlariData || !kasaRaporlariData.hayvan_kar_zarar) {
                        alert('❌ İndirilecek veri bulunamadı!');
                        return;
                      }
                      
                      try {
                        // Excel için veri hazırla
                        const excelData = [
                          ['Hayvan Bilgileri'],
                          ['Hayvan Adı / Küpe No', 'Giriş Kilosu', 'Satış Kilosu', 'Kilo Farkı', 'Alış Fiyatı (₺)', 'Satış Fiyatı (₺)', 'Kâr / Zarar (₺)'],
                          ...kasaRaporlariData.hayvan_kar_zarar.map(hayvan => [
                            `${hayvan.hayvan_adi || 'N/A'} ${hayvan.kupe_no ? `(${hayvan.kupe_no})` : ''}`,
                            `${parseFloat(hayvan.giris_kilosu || 0).toFixed(2)} kg`,
                            `${parseFloat(hayvan.satis_kilosu || 0).toFixed(2)} kg`,
                            `${parseFloat(hayvan.kilo_farki || 0) >= 0 ? '+' : ''}${parseFloat(hayvan.kilo_farki || 0).toFixed(2)} kg`,
                            parseFloat(hayvan.alis_fiyati || 0),
                            parseFloat(hayvan.satis_fiyati || 0),
                            parseFloat(hayvan.kar_zarar || 0)
                          ]),
                          [],
                          ['Finansal Özet'],
                          ['Toplam Hisse Satışı (₺)', parseFloat(kasaRaporlariData.finansal_ozet?.toplam_hisse_satis || 0)],
                          ['Toplam Tahsilatlar (₺)', parseFloat(kasaRaporlariData.finansal_ozet?.toplam_tahsilatlar || 0)],
                          ['Toplam Gider (₺)', parseFloat(kasaRaporlariData.finansal_ozet?.toplam_gider || 0)],
                          ['Toplam Kâr / Zarar (₺)', parseFloat(kasaRaporlariData.finansal_ozet?.toplam_kar_zarar || 0)]
                        ];

                        // Excel dosyası oluştur
                        const worksheet = XLSX.utils.aoa_to_sheet(excelData);
                        const workbook = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(workbook, worksheet, 'Kasa Raporları');

                        // Dosya adını oluştur (tarih ve filtre bilgisi ile)
                        const now = new Date();
                        const dateStr = now.toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\./g, '-');
                        let fileName = `Kasa_Raporlari_${dateStr}`;
                        
                        fileName += '.xlsx';

                        // Dosyayı indir
                        XLSX.writeFile(workbook, fileName);
                        alert(`✅ Excel dosyası başarıyla indirildi!\n\nDosya Adı: ${fileName}\nToplam ${kasaRaporlariData.hayvan_kar_zarar.length} hayvan kaydı`);
                      } catch (error) {
                        console.error('Excel export hatası:', error);
                        alert('❌ Excel dosyası oluşturulurken bir hata oluştu!');
                      }
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#218838';
                      e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#28a745';
                      e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    }}
                  >
                    📥 Excel Olarak İndir
                  </button>
                </div>

                {kasaRaporlariLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div style={{ fontSize: '18px', color: '#666' }}>📊 Rapor yükleniyor...</div>
                  </div>
                ) : kasaRaporlariData ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                    {/* Hayvan Bilgileri Bölümü */}
                    <div>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#ff6b35',
                        marginBottom: '15px',
                        paddingBottom: '10px',
                        borderBottom: '2px solid #ff6b35'
                      }}>
                        🐄 Hayvan Bilgileri
                      </h3>
                      
                      {kasaRaporlariData.hayvan_kar_zarar && kasaRaporlariData.hayvan_kar_zarar.length > 0 ? (
                        <div style={{ 
                          overflowX: 'auto',
                          border: '1px solid #ddd',
                          borderRadius: '8px'
                        }}>
                          <table style={{ 
                            width: '100%',
                            borderCollapse: 'collapse',
                            minWidth: '800px'
                          }}>
                            <thead>
                              <tr style={{ 
                                backgroundColor: '#f8f9fa',
                                borderBottom: '2px solid #ddd'
                              }}>
                                <th style={{ 
                                  padding: '12px',
                                  textAlign: 'left',
                                  fontWeight: '600',
                                  color: '#333',
                                  borderRight: '1px solid #ddd'
                                }}>Hayvan Adı / Küpe No</th>
                                <th style={{ 
                                  padding: '12px',
                                  textAlign: 'center',
                                  fontWeight: '600',
                                  color: '#333',
                                  borderRight: '1px solid #ddd'
                                }}>Giriş Kilosu</th>
                                <th style={{ 
                                  padding: '12px',
                                  textAlign: 'center',
                                  fontWeight: '600',
                                  color: '#333',
                                  borderRight: '1px solid #ddd'
                                }}>Satış Kilosu</th>
                                <th style={{ 
                                  padding: '12px',
                                  textAlign: 'center',
                                  fontWeight: '600',
                                  color: '#333',
                                  borderRight: '1px solid #ddd'
                                }}>Kilo Farkı</th>
                                <th style={{ 
                                  padding: '12px',
                                  textAlign: 'right',
                                  fontWeight: '600',
                                  color: '#333',
                                  borderRight: '1px solid #ddd'
                                }}>Alış Fiyatı (₺)</th>
                                <th style={{ 
                                  padding: '12px',
                                  textAlign: 'right',
                                  fontWeight: '600',
                                  color: '#333',
                                  borderRight: '1px solid #ddd'
                                }}>Satış Fiyatı (₺)</th>
                                <th style={{ 
                                  padding: '12px',
                                  textAlign: 'right',
                                  fontWeight: '600',
                                  color: '#333'
                                }}>Kâr / Zarar (₺)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {kasaRaporlariData.hayvan_kar_zarar.map((hayvan, index) => {
                                const karZarar = parseFloat(hayvan.kar_zarar || 0);
                                const karZararColor = karZarar >= 0 ? '#28a745' : '#dc3545';
                                
                                return (
                                  <tr 
                                    key={hayvan.id || index}
                                    style={{
                                      borderBottom: '1px solid #eee',
                                      transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#f8f9fa'}
                                    onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'transparent'}
                                  >
                                    <td style={{ 
                                      padding: '12px',
                                      borderRight: '1px solid #ddd',
                                      fontWeight: '500'
                                    }}>
                                      {hayvan.hayvan_adi || 'N/A'} {hayvan.kupe_no ? `(${hayvan.kupe_no})` : ''}
                                    </td>
                                    <td style={{ 
                                      padding: '12px',
                                      textAlign: 'center',
                                      borderRight: '1px solid #ddd'
                                    }}>
                                      {parseFloat(hayvan.giris_kilosu || 0).toFixed(2)} kg
                                    </td>
                                    <td style={{ 
                                      padding: '12px',
                                      textAlign: 'center',
                                      borderRight: '1px solid #ddd'
                                    }}>
                                      {parseFloat(hayvan.satis_kilosu || 0).toFixed(2)} kg
                                    </td>
                                    <td style={{ 
                                      padding: '12px',
                                      textAlign: 'center',
                                      borderRight: '1px solid #ddd',
                                      color: parseFloat(hayvan.kilo_farki || 0) >= 0 ? '#28a745' : '#dc3545',
                                      fontWeight: '500'
                                    }}>
                                      {parseFloat(hayvan.kilo_farki || 0) >= 0 ? '+' : ''}{parseFloat(hayvan.kilo_farki || 0).toFixed(2)} kg
                                    </td>
                                    <td style={{ 
                                      padding: '12px',
                                      textAlign: 'right',
                                      borderRight: '1px solid #ddd'
                                    }}>
                                      {parseFloat(hayvan.alis_fiyati || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                                    </td>
                                    <td style={{ 
                                      padding: '12px',
                                      textAlign: 'right',
                                      borderRight: '1px solid #ddd'
                                    }}>
                                      {parseFloat(hayvan.satis_fiyati || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                                    </td>
                                    <td style={{ 
                                      padding: '12px',
                                      textAlign: 'right',
                                      color: karZararColor,
                                      fontWeight: 'bold'
                                    }}>
                                      {karZarar >= 0 ? '+' : ''}{karZarar.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={{ 
                          padding: '20px',
                          textAlign: 'center',
                          color: '#999',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px'
                        }}>
                          Hayvan bilgisi bulunamadı.
                        </div>
                      )}
                    </div>

                    {/* Finansal Özet Bölümü */}
                    <div>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#ff6b35',
                        marginBottom: '15px',
                        paddingBottom: '10px',
                        borderBottom: '2px solid #ff6b35'
                      }}>
                        💵 Finansal Özet
                      </h3>
                      
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '15px'
                      }}>
                        {/* Toplam Hisse Satışı */}
                        <div style={{
                          padding: '20px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '2px solid #ddd',
                          textAlign: 'center'
                        }}>
                          <div style={{ 
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '8px',
                            fontWeight: '500'
                          }}>
                            Toplam Hisse Satışı
                          </div>
                          <div style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: '#333'
                          }}>
                            {parseFloat(kasaRaporlariData.finansal_ozet?.toplam_hisse_satis || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                          </div>
                        </div>

                        {/* Toplam Tahsilatlar */}
                        <div style={{
                          padding: '20px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '2px solid #ddd',
                          textAlign: 'center'
                        }}>
                          <div style={{ 
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '8px',
                            fontWeight: '500'
                          }}>
                            Toplam Tahsilatlar
                          </div>
                          <div style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: '#28a745'
                          }}>
                            {parseFloat(kasaRaporlariData.finansal_ozet?.toplam_tahsilatlar || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                          </div>
                        </div>

                        {/* Toplam Gider */}
                        <div style={{
                          padding: '20px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '2px solid #ddd',
                          textAlign: 'center'
                        }}>
                          <div style={{ 
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '8px',
                            fontWeight: '500'
                          }}>
                            Toplam Gider
                          </div>
                          <div style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: '#dc3545'
                          }}>
                            {parseFloat(kasaRaporlariData.finansal_ozet?.toplam_gider || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                          </div>
                        </div>

                        {/* Toplam Kâr / Zarar */}
                        <div style={{
                          padding: '20px',
                          backgroundColor: parseFloat(kasaRaporlariData.finansal_ozet?.toplam_kar_zarar || 0) >= 0 ? '#d4edda' : '#f8d7da',
                          borderRadius: '8px',
                          border: `2px solid ${parseFloat(kasaRaporlariData.finansal_ozet?.toplam_kar_zarar || 0) >= 0 ? '#28a745' : '#dc3545'}`,
                          textAlign: 'center'
                        }}>
                          <div style={{ 
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '8px',
                            fontWeight: '500'
                          }}>
                            Toplam Kâr / Zarar
                          </div>
                          <div style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: parseFloat(kasaRaporlariData.finansal_ozet?.toplam_kar_zarar || 0) >= 0 ? '#28a745' : '#dc3545'
                          }}>
                            {parseFloat(kasaRaporlariData.finansal_ozet?.toplam_kar_zarar || 0) >= 0 ? '+' : ''}{parseFloat(kasaRaporlariData.finansal_ozet?.toplam_kar_zarar || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div style={{ fontSize: '18px', color: '#dc3545' }}>❌ Rapor yüklenemedi.</div>
                  </div>
                )}

                {/* Kapat Butonu */}
                <div style={{ 
                  marginTop: '30px',
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => setShowKasaRaporlariModal(false)}
                    style={{
                      padding: '12px 30px',
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#5a6268';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#6c757d';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    ❌ Kapat
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hayvanlar Modal */}
        <AnimatePresence>
          {showHayvanlarModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: '20px'
              }}
              onClick={() => setShowHayvanlarModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '30px',
                  maxWidth: '1200px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'auto',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h2 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '24px', fontWeight: '600' }}>
                    <FaPaw style={{ marginRight: '10px', display: 'inline' }} />
                    Hayvanlar Yönetimi
                  </h2>
                  <button
                    onClick={() => setShowHayvanlarModal(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '28px',
                      cursor: 'pointer',
                      color: '#999',
                      padding: '0',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Arama ve Filtreler */}
                <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Küpe numarasına göre ara..."
                    value={hayvanlarSearchTerm}
                    onChange={(e) => setHayvanlarSearchTerm(e.target.value)}
                    style={{
                      flex: 1,
                      minWidth: '200px',
                      padding: '12px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <select
                    value={hayvanlarFilterType}
                    onChange={(e) => setHayvanlarFilterType(e.target.value)}
                    style={{
                      padding: '12px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      minWidth: '150px'
                    }}
                  >
                    <option value="all">Tüm Türler</option>
                    <option value="Büyükbaş">Büyükbaş</option>
                    <option value="Küçükbaş">Küçükbaş</option>
                  </select>
                  <select
                    value={hayvanlarFilterStatus}
                    onChange={(e) => setHayvanlarFilterStatus(e.target.value)}
                    style={{
                      padding: '12px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      minWidth: '150px'
                    }}
                  >
                    <option value="all">Tüm Durumlar</option>
                    <option value="Aktif">Aktif</option>
                    <option value="Satıldı">Satıldı</option>
                    <option value="Kesim Bekliyor">Kesim Bekliyor</option>
                    <option value="Satışta">Satışta</option>
                  </select>
                </div>

                {/* Tablo */}
                {hayvanlarLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p>Yükleniyor...</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333' }}>ID</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Küpe No</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Tür</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Irk</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Cins</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Ağırlık</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Kesim No</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Durum</th>
                          <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#333' }}>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hayvanlarList
                          .filter(animal => {
                            // Arama filtresi
                            if (hayvanlarSearchTerm && !animal.ear_tag?.toLowerCase().includes(hayvanlarSearchTerm.toLowerCase())) {
                              return false;
                            }
                            // Tür filtresi
                            if (hayvanlarFilterType !== 'all' && animal.type !== hayvanlarFilterType) {
                              return false;
                            }
                            // Durum filtresi
                            if (hayvanlarFilterStatus !== 'all') {
                              if (hayvanlarFilterStatus === 'Aktif' && animal.status !== 'active') {
                                return false;
                              }
                              if (hayvanlarFilterStatus === 'Satıldı' && animal.status !== 'sold') {
                                return false;
                              }
                              if (hayvanlarFilterStatus === 'Kesim Bekliyor' && animal.status !== 'Kesim Bekliyor') {
                                return false;
                              }
                              if (hayvanlarFilterStatus === 'Satışta' && animal.status !== 'Satışta' && animal.status !== 'onsale') {
                                return false;
                              }
                            }
                            return true;
                          })
                          .map((animal, index) => (
                            <React.Fragment key={animal.id || index}>
                              <tr style={{ 
                                borderBottom: '1px solid #eee',
                                background: expandedHayvanId === animal.id ? '#f8f9fa' : 'white'
                              }}>
                                <td style={{ padding: '12px' }}>#{animal.id}</td>
                                <td style={{ padding: '12px', fontWeight: '500' }}>{animal.ear_tag || '-'}</td>
                                <td style={{ padding: '12px' }}>{animal.type || '-'}</td>
                                <td style={{ padding: '12px' }}>{animal.breed || '-'}</td>
                                <td style={{ padding: '12px' }}>{animal.gender || '-'}</td>
                                <td style={{ padding: '12px' }}>
                                  {animal.last_weight ? `${animal.last_weight} kg` : animal.start_weight ? `${animal.start_weight} kg` : '-'}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  {animal.kesim_no ? (
                                    <span style={{
                                      padding: '4px 12px',
                                      borderRadius: '12px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      background: '#e3f2fd',
                                      color: '#1976d2',
                                      display: 'inline-block'
                                    }}>
                                      Kesim No: {animal.kesim_no}
                                    </span>
                                  ) : '-'}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  <span style={{
                                    padding: '4px 12px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    background: 
                                      animal.status === 'Satışta' || animal.status === 'onsale' ? '#fff3cd' :
                                      animal.status === 'sold' ? '#d1ecf1' :
                                      animal.status === 'Kesim Bekliyor' ? '#f8d7da' :
                                      '#d4edda',
                                    color:
                                      animal.status === 'Satışta' || animal.status === 'onsale' ? '#856404' :
                                      animal.status === 'sold' ? '#0c5460' :
                                      animal.status === 'Kesim Bekliyor' ? '#721c24' :
                                      '#155724'
                                  }}>
                                    {animal.status === 'Satışta' || animal.status === 'onsale' ? 'Satışta' :
                                     animal.status === 'sold' ? 'Satıldı' :
                                     animal.status === 'Kesim Bekliyor' ? 'Kesim Bekliyor' :
                                     'Aktif'}
                                  </span>
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <button
                                      onClick={async () => {
                                        if (expandedHayvanId === animal.id) {
                                          setExpandedHayvanId(null);
                                        } else {
                                          setExpandedHayvanId(animal.id);
                                          if (!hayvanHissedarlar[animal.id]) {
                                            await fetchHayvanHissedarlar(animal.id);
                                          }
                                        }
                                      }}
                                      style={{
                                        padding: '6px 12px',
                                        background: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px'
                                      }}
                                    >
                                      <FaUserFriends /> {expandedHayvanId === animal.id ? 'Gizle' : 'Hisse Sahiplerini Göster'}
                                    </button>
                                    <button
                                      onClick={() => {
                                        // Yetki kontrolü - Sadece Yönetici erişebilir
                                        // userData varsa onu kullan, yoksa currentUser'ı kullan
                                        const user = userData || currentUser;
                                        const userRole = user?.role || user?.user_role || '';
                                        // admin, Yönetici veya yönetici rollerine izin ver
                                        if (userRole !== 'Yönetici' && userRole !== 'admin' && userRole !== 'yönetici') {
                                          alert('⚠️ Bu özelliği kullanmak için yetkiniz yok.\n\nSadece Sistem Yöneticisi kesim no atayabilir.');
                                          return;
                                        }
                                        setSelectedKesimHayvan(animal);
                                        setShowKesimModal(true);
                                      }}
                                      style={{
                                        padding: '6px 12px',
                                        background: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px'
                                      }}
                                    >
                                      <FaCalendarAlt /> Kesim No Ata
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {expandedHayvanId === animal.id && hayvanHissedarlar[animal.id] && (
                                <tr>
                                  <td colSpan="8" style={{ padding: '20px', background: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
                                    <h4 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px' }}>Hissedarlar:</h4>
                                    {hayvanHissedarlar[animal.id].length > 0 ? (
                                      <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
                                        <thead>
                                          <tr style={{ background: '#e9ecef' }}>
                                            <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>Ad Soyad</th>
                                            <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>Telefon</th>
                                            <th style={{ padding: '10px', textAlign: 'right', fontSize: '13px', fontWeight: '600' }}>Toplam Hisse</th>
                                            <th style={{ padding: '10px', textAlign: 'right', fontSize: '13px', fontWeight: '600' }}>Toplam Tutar</th>
                                            <th style={{ padding: '10px', textAlign: 'right', fontSize: '13px', fontWeight: '600' }}>Ödenen</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {hayvanHissedarlar[animal.id].map((hissedar, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                              <td style={{ padding: '10px' }}>{hissedar.name || '-'}</td>
                                              <td style={{ padding: '10px' }}>{hissedar.phone || '-'}</td>
                                              <td style={{ padding: '10px', textAlign: 'right', fontWeight: '500' }}>{hissedar.total_shares || 0}</td>
                                              <td style={{ padding: '10px', textAlign: 'right' }}>{parseFloat(hissedar.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
                                              <td style={{ padding: '10px', textAlign: 'right', color: '#28a745', fontWeight: '500' }}>{parseFloat(hissedar.paid_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    ) : (
                                      <p style={{ color: '#666', fontStyle: 'italic' }}>Bu hayvana ait hissedar bulunamadı.</p>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                      </tbody>
                    </table>
                    {hayvanlarList.filter(animal => {
                      if (hayvanlarSearchTerm && !animal.ear_tag?.toLowerCase().includes(hayvanlarSearchTerm.toLowerCase())) return false;
                      if (hayvanlarFilterType !== 'all' && animal.type !== hayvanlarFilterType) return false;
                      if (hayvanlarFilterStatus !== 'all') {
                        if (hayvanlarFilterStatus === 'Aktif' && animal.status !== 'active') return false;
                        if (hayvanlarFilterStatus === 'Satıldı' && animal.status !== 'sold') return false;
                        if (hayvanlarFilterStatus === 'Kesim Bekliyor' && animal.status !== 'Kesim Bekliyor') return false;
                        if (hayvanlarFilterStatus === 'Satışta' && animal.status !== 'Satışta' && animal.status !== 'onsale') return false;
                      }
                      return true;
                    }).length === 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <p>Hayvan bulunamadı.</p>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Kesim No Ata Modal */}
        <AnimatePresence>
          {showKesimModal && selectedKesimHayvan && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001,
                padding: '20px'
              }}
              onClick={() => {
                setShowKesimModal(false);
                setSelectedKesimHayvan(null);
                setKesimFormData({ kesim_no: '', kesim_tarihi: '', tahmini_kesim_saati: '', tahmini_teslim_saati: '' });
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '30px',
                  maxWidth: '500px',
                  width: '100%',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h2 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '20px', fontWeight: '600' }}>
                    Kesim No Ata
                  </h2>
                  <button
                    onClick={() => {
                      setShowKesimModal(false);
                      setSelectedKesimHayvan(null);
                      setKesimFormData({ kesim_no: '', kesim_tarihi: '', tahmini_kesim_saati: '', tahmini_teslim_saati: '' });
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '28px',
                      cursor: 'pointer',
                      color: '#999',
                      padding: '0',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ×
                  </button>
                </div>

                <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <p style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#666' }}>Hayvan:</p>
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#333' }}>
                    {selectedKesimHayvan.breed || selectedKesimHayvan.ear_tag} ({selectedKesimHayvan.ear_tag})
                  </p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    Kesim No <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={kesimFormData.kesim_no}
                    onChange={(e) => setKesimFormData(prev => ({ ...prev, kesim_no: e.target.value }))}
                    placeholder="Örn: 1, 2, 3..."
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    Kesim Tarihi
                  </label>
                  <input
                    type="date"
                    value={kesimFormData.kesim_tarihi}
                    onChange={(e) => setKesimFormData(prev => ({ ...prev, kesim_tarihi: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    Tahmini Kesim Saati
                  </label>
                  <input
                    type="time"
                    value={kesimFormData.tahmini_kesim_saati}
                    onChange={(e) => setKesimFormData(prev => ({ ...prev, tahmini_kesim_saati: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '30px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    Tahmini Teslim Saati
                  </label>
                  <input
                    type="time"
                    value={kesimFormData.tahmini_teslim_saati}
                    onChange={(e) => setKesimFormData(prev => ({ ...prev, tahmini_teslim_saati: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowKesimModal(false);
                      setSelectedKesimHayvan(null);
                      setKesimFormData({ kesim_no: '', kesim_tarihi: '', tahmini_kesim_saati: '', tahmini_teslim_saati: '' });
                    }}
                    style={{
                      padding: '12px 24px',
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleKesimKaydet}
                    style={{
                      padding: '12px 24px',
                      background: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Kaydet
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </AdminContainer>
  );
};

export default SecretAdminPanel; 